import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Layout, Tree, Spin, Button, Tooltip, Space, Tag, Drawer, Typography, Modal, Checkbox, Popover, Select, InputNumber, Table, Badge, ColorPicker, Dropdown, Collapse, Input, Segmented } from "antd";

const { Text } = Typography;
import { FullscreenOutlined, DragOutlined, ColumnWidthOutlined, QuestionCircleOutlined, DownloadOutlined, AimOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import proj4 from "proj4";
import { initViewer } from "../viewer/viewerEngine.js";
import { tessellateAlignment, formatStation, getPositionAtStation, projectToAlignment, getExactTangentAtStation } from "../viewer/viewerAlignment.js";
import DigiTwinLogo from "./DigiTwinLogo.js";
import PileScheduleModal from "./PileScheduleModal.js";
import BridgeQuantitiesModal from "./BridgeQuantitiesModal.js";
import useIsMobile from "../../../hooks/useIsMobile.js";
import { useRegisterNavGuard } from "../../../context/NavGuardContext.js";
import "./BimViewer.css";

const { Header, Content, Sider } = Layout;

// Satellite imagery pixel size per quality preset — fixed, capped options only (never a
// free-typed/derived value) so this can't accidentally balloon to something huge and slow.
// Esri's export endpoint cost scales with pixel count, not geographic area: measured directly
// against the real service for a ~2km viewport-scale bbox — 512px ≈1.1s, 1024px ≈3-9s
// (network-dependent), 2048px ≈10-15s. "Low" is the default since imagery now refetches on
// every camera settle (M27 v6) rather than once per file, so responsiveness matters more here
// than it did for the old one-shot whole-file load.
const IMAGERY_QUALITY_PX = { low: 512, medium: 1024, high: 2048 };

// Cross Section "At Station" Prev/Next stepping — civil design software convention: from a
// non-round current station (e.g. picked via "Pick on screen", landing on 52101.816 rather than
// an even multiple of the step), Next/Prev round to the nearest step-aligned station in the
// direction of travel instead of just adding/subtracting the interval to whatever the current
// value happens to be (which would carry the .816 forward forever — 52101.816 -> 52111.816 ->
// 52121.816..., never landing on a round station). A small epsilon guards the case where the
// current value is already meant to be exactly aligned but carries harmless floating-point noise
// (52110.00000000001 or 52109.99999999998) from earlier arithmetic.
const STATION_STEP_EPS = 1e-6;
function nextStepStation(current, interval) {
  const stepped = (Math.floor(current / interval + STATION_STEP_EPS) + 1) * interval;
  return Math.round(stepped * 1000) / 1000;
}
function prevStepStation(current, interval) {
  const stepped = (Math.ceil(current / interval - STATION_STEP_EPS) - 1) * interval;
  return Math.round(stepped * 1000) / 1000;
}

// Shared by Annotate and the IoT simulation — both group getVisibleElements() results by
// (itemType, Name) so a named feature made of several sub-elements (different rails/curves
// carrying the same Name) gets one callout/pulse, not one per sub-element.
//
// Same key does NOT always mean same physical instance, though — found via a real report
// ("60E1_Turnout" landing in the middle of the model, nowhere near any actual turnout"). Some
// item types encode Name as a CATEGORY/TYPE string rather than a unique per-instance id: every
// turnout's own Rail_Profile_Linear sub-elements share the exact literal Name
// "Rail_Profile_Single_Turnout" — confirmed against the real .bim: 96 such elements spread
// across 12 entirely separate physical turnouts. Blindly averaging all of them (the original
// approach, used by both callers before this was extracted) put the callout 1,133m from the
// nearest one. Turnouts' own Name property ("T1", "T12", ...) IS genuinely unique per instance,
// so this isn't universal — it depends on the specific item type/aspect, not something either
// caller can be expected to know in advance.
//
// Fix: cluster spatially within each (item type, Name) group before averaging. Elements more
// than clusterDistanceM apart are almost certainly different physical instances that happen to
// share a category-style Name, not sub-parts of one feature, and get their own separate
// callout/pulse. Genuinely co-located sub-elements of one real feature (confirmed in this
// project's own data: one turnout's Turnout/Branch1 Geometry/Branch1 LLS Extension pieces sit
// within ~30m of each other) still merge into one group exactly as before — this only changes
// anything for groups that were actually scattered across the model.
// Annotate: an item type with more elements than this in view is not labelled (see applyAnnotate)
const ANNOTATE_LIMIT = 40;

function clusterVisibleByName(visible, names, clusterDistanceM = 50) {
  const byKey = new Map();
  for (const v of visible) {
    const label = names[v.elementId];
    if (label == null) continue;
    // Bridge labels name the assembly ("Bridge_01 · Abutment_A01"), while its piles, cap and footing
    // are separate leaves — group bridge parts on the label alone so one support gets one callout.
    const groupKey = `${isBridgeKey(v.itemType) ? "bridge" : v.itemType}::${label}`;
    let entry = byKey.get(groupKey);
    if (!entry) { entry = { label: String(label), points: [] }; byKey.set(groupKey, entry); }
    entry.points.push({ ...v.position, preferred: !!v.preferred });
  }

  const result = [];
  for (const { label, points } of byKey.values()) {
    const clusters = [];
    for (const pos of points) {
      const cluster = clusters.find((c) =>
        c.points.some((p) => Math.hypot(p.x - pos.x, p.y - pos.y, p.z - pos.z) <= clusterDistanceM)
      );
      if (cluster) cluster.points.push(pos);
      else clusters.push({ points: [pos] });
    }
    for (const c of clusters) {
      // A preferred anchor (a bridge support's cap) wins over averaging every part — an abutment's
      // ~24 piles otherwise pull the dot under the footing, somewhere different for each support.
      const pts = c.points.some((p) => p.preferred) ? c.points.filter((p) => p.preferred) : c.points;
      const n  = pts.length;
      const sx = pts.reduce((s, p) => s + p.x, 0);
      const sy = pts.reduce((s, p) => s + p.y, 0);
      const sz = pts.reduce((s, p) => s + p.z, 0);
      result.push({ label, position: { x: sx / n, y: sy / n, z: sz / n } });
    }
  }
  return result;
}

export default function BimViewer({ file, onBack }) {
  // Left/right Siders are fixed-width push-panels (400px/300px) and the top bar packs ~14
  // icon buttons in one row — both fine on desktop, both unusable on a ~375-414px phone.
  // Below this breakpoint the Siders become overlay Drawers instead and the header sheds/
  // shrinks non-essential chrome (see isMobile usage throughout render()).
  const isMobile = useIsMobile();
  const containerRef       = useRef(null);
  const viewerRef          = useRef(null);
  const alignLabelCanvasRef = useRef(null); // 2D overlay for alignment mark station labels
  const sectionCanvasRef   = useRef(null);
  const gridLabelCanvasRef = useRef(null); // 2D overlay for grid tick labels
  const sectionPtsRef      = useRef(null); // stores {p1,p2} for the useEffect
  const profileCanvasRef      = useRef(null); // 2D canvas for profile view
  const profileTrackerCanvasRef = useRef(null); // transparent overlay for tracker
  const profileViewRef     = useRef({ zoom: 1, cx_stn: 0, cy_elev: 0 }); // pan/zoom view state
  const profilePathRef     = useRef(null); // current VA path (for event handlers)
  const profileVScaleRef   = useRef(1);    // mirrors profileVScale for event handler closures
  const profileInitRef     = useRef(false); // whether auto-fit has been applied for current VA
  const profileTrackActiveRef = useRef(false); // mirrors profileTrackActive for event handlers
  const profileShowAnnotRef   = useRef(true);  // mirrors profileShowAnnotations for event handlers

  // Viewer lifecycle
  const [viewerReady, setViewerReady]   = useState(false);
  const [viewerError, setViewerError]   = useState("");

  // Item type tree from API: array of 5-level hierarchy nodes
  const [itemTree, setItemTree]         = useState([]);
  const [treeFilter, setTreeFilter]     = useState(EMPTY_FILTER);
  const [filterGroups, setFilterGroups] = useState([]); // [{name, itemTypes}] from [Project].md front matter
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [modelsOpen, setModelsOpen]     = useState(false); // Models card starts collapsed, like the filter
  const preFilterExpandedRef            = useRef(null); // expansion to restore when the filter clears
  const [modelExtents, setModelExtents] = useState({}); // modelId → recentred AABB, for View → Zoom to
  const [zoomToOpen, setZoomToOpen]     = useState(false);
  const [hiddenTypes, setHiddenTypes]   = useState([]); // leaf keys hidden via Interact → Hide item type, newest last
  const [typeStates, setTypeStates]     = useState({}); // "ClassName::subjectId" → "idle"|"loading"|"loaded"|"error"

  // Selection / properties
  const [selectedEl, setSelectedEl]     = useState(null);
  const [loadingProps, setLoadingProps] = useState(false);
  // Bridge-part Properties sections: all start collapsed for each newly selected element; the user opens
  // the ones they want
  const [bridgeOpenKeys, setBridgeOpenKeys] = useState([]);
  useEffect(() => { setBridgeOpenKeys([]); }, [selectedEl]);

  // Toolbar state
  const [activeMode, setActiveMode]     = useState("rotate");
  const [wireframe, setWireframe]       = useState(false);
  const [isPerspective, setIsPerspective] = useState(true);
  const [measureResult, setMeasureResult] = useState(null);
  const [trackerData,   setTrackerData]   = useState(null);
  const [bgColor, setBgColor]           = useState("#ffffff");

  // Panel collapse
  // Starts open on desktop (400px Sider push-panel — always had room); starts closed on
  // mobile (86% Drawer overlay — would otherwise cover the viewport on first load).
  const [leftCollapsed, setLeftCollapsed] = useState(isMobile);

  // Cross-section
  const [sectionState, setSectionState] = useState("idle"); // "idle"|"picking1"|"picking2"|"active"
  const [sectionOpen,  setSectionOpen]  = useState(false);
  const [sectionControlsCollapsed, setSectionControlsCollapsed] = useState(false); // hides the 180px side panel for more canvas room — tap to toggle, never automatic
  const [sectionCoord,    setSectionCoord]    = useState(null);
  const [sectionPanActive,  setSectionPanActive]  = useState(true);
  const sectionPanRef   = useRef(true);

  // Alignment tracking
  const [alignList,       setAlignList]       = useState(null);    // null = not fetched yet
  const [alignLoading,    setAlignLoading]    = useState(false);
  const [alignPickerOpen, setAlignPickerOpen] = useState(false);
  const [alignActive,     setAlignActive]     = useState(false);
  const [activeAlignName, setActiveAlignName] = useState(null);
  const [alignHover,         setAlignHover]         = useState(null);    // { station, distance, radius }
  const [alignMark,          setAlignMark]          = useState(null);    // { station, distance }
  const [alignLockedStation, setAlignLockedStation] = useState(null);    // metres, or null = free tracking
  const alignListRef = useRef(null); // mirrors alignList for use in callbacks

  // Section at Station
  const [stnPickerOpen, setStnPickerOpen] = useState(false);
  const [stnAlignName,  setStnAlignName]  = useState(null);
  const [stnInput,      setStnInput]      = useState(null);   // station in metres
  const [stnAngle,      setStnAngle]      = useState(0);      // degrees; 0 = perpendicular to alignment
  const [stnPicking,    setStnPicking]    = useState(false);  // "Pick on screen" active
  const [sectionInterval, setSectionInterval] = useState(10); // station step for Prev/Next

  // Top-bar Tools menu (M26) — "At Station" needs input collection before it can run,
  // so its entry in the menu opens this Modal instead of acting immediately.
  const [topBarStnModalOpen, setTopBarStnModalOpen] = useState(false);
  // Right Tools panel (M26 bullet 3) — replaces the earlier Dropdown-menu version; a
  // persistent Sider (like the left item-type panel) instead of a transient flyout, since
  // a Dropdown felt "oversimplified" for tools with this much internal complexity.
  const [rightCollapsed, setRightCollapsed] = useState(true);
  // Accordion state (M26 bullet 4) — only one group's tool list is expanded at a time,
  // so the panel stays compact as more tools get added to each group over time.
  const [openToolGroup, setOpenToolGroup] = useState(null);

  // Track whether current section was created via At Station (for grid overlay)
  const [sectionFromStation, setSectionFromStation] = useState(false);
  const sectionFromStationRef  = useRef(false);
  const sectionDrawerReadyRef  = useRef(false); // true once Drawer animation has completed
  const sectionMeasureRef      = useRef(false);
  const [sectionMeasureActive, setSectionMeasureActive] = useState(false);
  // Token to force section view restart when a new At Station section replaces the previous one
  const [sectionToken, setSectionToken] = useState(0);

  // Profile view
  const [profileOpen,      setProfileOpen]      = useState(false);
  const [profileControlsCollapsed, setProfileControlsCollapsed] = useState(false); // hides the 180px side panel for more canvas room — tap to toggle, never automatic
  const [profileAlignName, setProfileAlignName] = useState(null);
  const [profileVerts,        setProfileVerts]        = useState([]);  // [{id, name, path}]
  const [profileSelectedVAs,  setProfileSelectedVAs]  = useState([]); // names of checked VAs
  const [profileVScale,       setProfileVScale]       = useState(null); // null = not yet auto-fitted
  const [profileLoading,      setProfileLoading]      = useState(false);
  const [profileTrackActive,  setProfileTrackActive]  = useState(false);
  const [profileShowAnnotations, setProfileShowAnnotations] = useState(true);

  // Satellite imagery
  const [imageryOn,      setImageryOn]      = useState(false);
  const [imageryLoading, setImageryLoading] = useState(false);
  const [imageryQuality, setImageryQuality] = useState("low"); // low/medium/high — see IMAGERY_QUALITY_PX
  const imageryGeoRef  = useRef(null); // cached per-file geo data (bbox/gcs/proj4Def) — see toggleImagery
  const imageryWatchStopRef = useRef(null); // unsubscribe fn from viewerEngine.watchCamera

  // Quantity Takeoff
  const [qtoOpen,     setQtoOpen]     = useState(false);
  const [pileScheduleOpen, setPileScheduleOpen] = useState(false); // Bridge Reports → pile schedule
  const [bridgeBoqOpen, setBridgeBoqOpen] = useState(false);       // Bridge Reports → bill of quantities
  const [qtoAlignName, setQtoAlignName] = useState(null);
  const [qtoStartStn, setQtoStartStn] = useState(null);
  const [qtoEndStn,   setQtoEndStn]   = useState(null);
  const [qtoLeftOff,  setQtoLeftOff]  = useState(50);
  const [qtoRightOff, setQtoRightOff] = useState(50);
  const [qtoLoading,  setQtoLoading]  = useState(false);
  const [qtoRows,     setQtoRows]     = useState(null);
  const [qtoModalOpen, setQtoModalOpen] = useState(false);
  const [qtoPicking, setQtoPicking]   = useState(null); // null | "start" | "end" — see startQtoStationPick
  // A boundary-wide compute can take tens of seconds on dense sections — rather than pinning the
  // form Modal open with a spinner for that whole time, Compute closes the form immediately and
  // the run continues in the background; this pair tracks it for the status badge next to the
  // Tools toggle (see qtoStatusPopoverOpen below) instead of the old auto-opening results Modal.
  const [qtoResultReady, setQtoResultReady] = useState(false);
  const [qtoStatusPopoverOpen, setQtoStatusPopoverOpen] = useState(false);

  // Annotate — "Name" property callouts over elements visible in the viewport
  const [annotateActive,  setAnnotateActive]  = useState(false);
  const [annotateLoading, setAnnotateLoading] = useState(false);
  const [annotatePanelOpen, setAnnotatePanelOpen] = useState(false);
  const [annotateSelectedTypes, setAnnotateSelectedTypes] = useState([]); // item type keys (or "bridge:<name>") chosen in the panel
  const [annotateIfcLabel, setAnnotateIfcLabel] = useState("type"); // IFC classes: label by "type" or "instance"
  const [annotateWarnings, setAnnotateWarnings] = useState([]);    // [{ name, count }] types too dense to label

  // Stations — 100 m-step tick marks + labels along a selected alignment, limited to view
  const [stationsPanelOpen, setStationsPanelOpen] = useState(false);
  const [stationsActive,    setStationsActive]    = useState(false);
  const [stationsAlignName, setStationsAlignName] = useState(null);

  // IoT sensor simulation — no real sensor feed exists yet; this fakes an open/closed
  // state per visible Turnout on a timer and pulses the "open" ones, purely to demonstrate
  // what a live digital-twin sensor overlay would look like once one is wired up.
  const [iotPanelOpen, setIotPanelOpen] = useState(false);
  const [iotSignals,   setIotSignals]   = useState([]); // [{name, signal: "OPEN"|"CLOSED", time}]
  const iotIntervalRef = useRef(null);

  // Show Alignment (M26 bullet 4) — static thick black polyline for the whole alignment,
  // separate from Tracking's thin blue live-preview line.
  const [showAlignPanelOpen, setShowAlignPanelOpen] = useState(false);
  const [showAlignActive,    setShowAlignActive]    = useState(false);
  const [showAlignName,      setShowAlignName]      = useState(null);

  // ── Fetch item type hierarchy when file changes ───────────────────────────
  useEffect(() => {
    if (!file) return;
    const base = `${file.projectId}/${encodeURIComponent(file.fileName)}`;
    setTreeFilter(EMPTY_FILTER);
    setExpandedKeys([]);
    setHiddenTypes([]);
    setModelExtents({});
    fetch(`/api/extents/${base}`).then((r) => (r.ok ? r.json() : { models: {} }))
      .then((d) => setModelExtents(d.models || {})).catch(() => {});
    preFilterExpandedRef.current = null;
    Promise.all([
      fetch(`/api/elements/${base}/itemtypes`).then((r) => r.json()),
      // OpenBridge bridges (docs/digitwin/Bridges Roadmap.md) — a failure here only loses the bridge
      // trees; the item-type tree still loads as before.
      fetch(`/api/bridges/${base}/tree`).then((r) => (r.ok ? r.json() : { bridges: [] })).catch(() => ({ bridges: [] })),
    ])
      .then(([data, bridgeData]) => {
        const tree = graftBridgeTrees(data.tree || [], bridgeData.bridges || []);
        setItemTree(tree);
        setFilterGroups(data.filterGroups || []);
        const initial = {};
        collectLeafKeys(tree).forEach((key) => { initial[key] = "idle"; });
        setTypeStates(initial);
      })
      .catch(() => setItemTree([]));
  }, [file]);

  // ── Init viewer ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    setViewerReady(false);
    setViewerError("");

    initViewer(containerRef.current, file, {
      onReady:      ()              => { if (!cancelled) setViewerReady(true); },
      onError:      (msg)           => { if (!cancelled) setViewerError(msg); },
      onFirstLoad:  ()              => { if (!cancelled) setIsPerspective(false); },
      onTrack:      (data)          => { if (!cancelled) setTrackerData(data); },
      // Fires whenever the engine's active mode changes for ANY reason, including internal
      // auto-switches that don't go through activateMode() (e.g. the wzoom double-right-click
      // escape) — keeps the toolbar's highlighted button in sync with reality either way.
      onModeChange: (mode)          => { if (!cancelled) setActiveMode(mode); },
      onSelect:     (el, isLoading) => {
        if (cancelled) return;
        setLoadingProps(isLoading === true);
        setSelectedEl(el);
      },
      onMeasure:      (result)        => { if (!cancelled) setMeasureResult(result); },
      // Interact → Hide item type: refs, since this effect only re-runs on file change
      onHideType:     (key)           => { if (!cancelled) hideTypeRef.current(key); },
      getTypeLabel:   (key)           => typeLabelsRef.current.get(key),
      onSectionClick: (which, p1, p2) => {
        if (cancelled) return;
        if (which === "first") {
          setSectionState("picking2");
        } else {
          viewerRef.current?.setSectionPlane(p1, p2);
          sectionPtsRef.current = { p1, p2 };
          setSectionState("active");
          setSectionOpen(true);
        }
      },
    }).then((instance) => {
      if (!cancelled) viewerRef.current = instance;
    });

    return () => {
      cancelled = true;
      if (viewerRef.current?.dispose) viewerRef.current.dispose();
      viewerRef.current = null;
    };
  }, [file]); // intentionally omits rightCollapsed

  // ── Reset alignment state when file changes ───────────────────────────────
  useEffect(() => {
    alignListRef.current = null;
    setAlignList(null);
    setAlignActive(false);
    setActiveAlignName(null);
    setAlignHover(null);
    setAlignMark(null);
    setAlignPickerOpen(false);
  }, [file]);

  // Stop the IoT simulation timer on file change or unmount — it's a plain setInterval,
  // separate from the viewer instance's own dispose(), so it needs its own cleanup.
  useEffect(() => {
    return () => {
      if (iotIntervalRef.current) { clearInterval(iotIntervalRef.current); iotIntervalRef.current = null; }
    };
  }, [file]);

  // Builds (or rebuilds) the live section view from whatever canvas size is current right now.
  // Pulled out to a stable callback so it can be triggered from three different places without
  // three copies of the same call: the Drawer's afterOpenChange (first open — see below), the
  // sectionToken effect (Prev/Next station navigation, Drawer already open), and the
  // ResizeObserver effect further down (Drawer already open, canvas genuinely resized).
  const runStartSectionView = useCallback(() => {
    const pts = sectionPtsRef.current;
    if (!pts || !sectionCanvasRef.current) return;
    viewerRef.current?.startSectionView(
      sectionCanvasRef.current, pts.p1, pts.p2,
      (coord) => {
        // Show badge when vertex is snapped or Shift key is held
        if (!coord || (!coord.snapped && !coord.shift)) setSectionCoord(null);
        else setSectionCoord({ z: coord.z, offset: coord.offset, snapped: coord.snapped });
      },
      sectionPanRef,
      sectionFromStationRef.current ? { spacing: 5 } : null,
      gridLabelCanvasRef.current,  // always pass — snap overlay works in all section types
      sectionMeasureRef            // measure tool reads this ref at click time
    );
  }, []);

  // ── Re-run section view for Prev/Next station navigation ──────────────────
  // The Drawer's own afterOpenChange (see the <Drawer> below) handles the *first* build, once
  // the open transition has actually finished — this effect only covers sectionToken bumping
  // while the Drawer is already open and stable (applyAtStation), guarded on
  // sectionDrawerReadyRef so it can't also fire for that same first open and race the transition.
  useEffect(() => {
    if (sectionState !== "active" || !sectionOpen || !sectionDrawerReadyRef.current) return;
    runStartSectionView();
  }, [sectionState, sectionOpen, sectionToken, runStartSectionView]);

  // ── XYZ Tracker precision refine ────────────────────────────────────────────
  // The live readout (onTrack, set via viewerControls.js's own frozen tracker) updates on every
  // mousemove off the Float32 render mesh — see docs/digitwin/Viewer Interactions and Fixes.md
  // ("Measure tool sub-mm precision", XYZ tracker follow-up). Only active while the tracker tool
  // itself is; the engine debounces internally so this never fires while the cursor is still
  // moving, only once it settles on a snapped point.
  useEffect(() => {
    if (activeMode !== "tracker" || !viewerRef.current) return;
    viewerRef.current.enableTrackerRefine((exact) => {
      setTrackerData((prev) => (prev ? { ...prev, ...exact } : prev));
    });
    return () => viewerRef.current?.disableTrackerRefine();
  }, [activeMode]);

  // ── Re-fit the section view when its canvas actually resizes ──────────────
  // startSectionView() computes the camera frustum from canvas.getBoundingClientRect() once,
  // at call time — it never re-measures on its own. The Drawer's height is now viewport-
  // relative (vh) on mobile specifically so it fits after a phone rotation, but that resize
  // has to actually reach the WebGL renderer/camera somehow — re-running the exact same
  // startSectionView() call the effect above already uses is the simplest correct way to do
  // that (it fully rebuilds from the canvas's current size, the same as a fresh pick), rather
  // than hand-adjusting the frustum in place and risking subtly wrong math.
  useEffect(() => {
    if (sectionState !== "active" || !sectionOpen || !sectionCanvasRef.current) return;
    let resizeTimer = null;
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(runStartSectionView, 200);
    });
    ro.observe(sectionCanvasRef.current);
    return () => { clearTimeout(resizeTimer); ro.disconnect(); };
  }, [sectionState, sectionOpen, runStartSectionView]);

  // ── Re-fit the profile view when its canvas actually resizes ───────────────
  // drawProfileCanvas() already reads canvas.clientWidth/clientHeight fresh on every call and
  // resyncs the backing buffer to match — it just needs to actually be called again after a
  // resize, which nothing currently triggers (the effect that draws it only reacts to data/
  // pan/zoom state, not the canvas's own size).
  useEffect(() => {
    if (!profileOpen || !profileCanvasRef.current) return;
    let resizeTimer = null;
    const ro = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const vaList = profilePathRef.current;
        if (!vaList || vaList.length === 0 || !profileCanvasRef.current) return;
        drawProfileCanvas(profileCanvasRef.current, vaList, profileVScaleRef.current, profileViewRef.current, profileShowAnnotRef.current);
      }, 200);
    });
    ro.observe(profileCanvasRef.current);
    return () => { clearTimeout(resizeTimer); ro.disconnect(); };
  }, [profileOpen]);

  // ── Item type toggle ──────────────────────────────────────────────────────
  const toggleItemType = useCallback(async (className) => {
    const state = typeStates[className];
    if (state === "loading") return;

    if (state === "loaded") {
      viewerRef.current?.unloadItemType(className);
      setTypeStates((prev) => ({ ...prev, [className]: "idle" }));
    } else {
      setTypeStates((prev) => ({ ...prev, [className]: "loading" }));
      try {
        if (isBridgeKey(className)) await viewerRef.current?.loadBridgeParts([className], bridgeElementIds(itemTree));
        else await viewerRef.current?.loadItemType(className);
        // New materials start with wireframe:false — re-apply if mode is active
        if (wireframe) viewerRef.current?.setWireframe(true);
        setTypeStates((prev) => ({ ...prev, [className]: "loaded" }));
      } catch (err) {
        console.error("loadItemType failed:", err);
        setTypeStates((prev) => ({ ...prev, [className]: "error" }));
      }
    }
  }, [typeStates, wireframe, itemTree]);

  // ── Discipline-level toggle — load/unload all item types under one discipline ──
  const toggleDiscipline = useCallback(async (itemTypes, loadAll) => {
    if (loadAll) {
      const toLoad = itemTypes.filter(t => {
        const s = typeStates[t.key];
        return s !== "loaded" && s !== "loading";
      });
      if (toLoad.length === 0) return;
      // Mark all as loading in one batch so the UI reacts immediately
      setTypeStates(prev => {
        const next = { ...prev };
        toLoad.forEach(t => { next[t.key] = "loading"; });
        return next;
      });
      for (const t of toLoad) {
        try {
          await viewerRef.current?.loadItemType(t.key);
          setTypeStates(prev => ({ ...prev, [t.key]: "loaded" }));
        } catch {
          setTypeStates(prev => ({ ...prev, [t.key]: "error" }));
        }
      }
      if (wireframe) viewerRef.current?.setWireframe(true);
    } else {
      const toUnload = itemTypes.filter(t => typeStates[t.key] === "loaded");
      toUnload.forEach(t => viewerRef.current?.unloadItemType(t.key));
      setTypeStates(prev => {
        const next = { ...prev };
        toUnload.forEach(t => { next[t.key] = "idle"; });
        return next;
      });
    }
  }, [typeStates, wireframe]);

  // ── Bridge group toggle — load/unload every leaf under a bridge node in ONE request ──
  const toggleBridgeGroup = useCallback(async (leafKeys, loadAll) => {
    if (loadAll) {
      const toLoad = leafKeys.filter((k) => typeStates[k] !== "loaded" && typeStates[k] !== "loading");
      if (toLoad.length === 0) return;
      const mark = (state) => setTypeStates((prev) => {
        const next = { ...prev };
        toLoad.forEach((k) => { next[k] = state; });
        return next;
      });
      mark("loading");
      try {
        await viewerRef.current?.loadBridgeParts(toLoad, bridgeElementIds(itemTree));
        mark("loaded");
        if (wireframe) viewerRef.current?.setWireframe(true);
      } catch (err) {
        console.error("loadBridgeParts failed:", err);
        mark("error");
      }
    } else {
      const toUnload = leafKeys.filter((k) => typeStates[k] === "loaded");
      toUnload.forEach((k) => viewerRef.current?.unloadItemType(k));
      setTypeStates((prev) => {
        const next = { ...prev };
        toUnload.forEach((k) => { next[k] = "idle"; });
        return next;
      });
    }
  }, [typeStates, wireframe, itemTree]);

  // ── Hide item type (Interact) ──────────────────────────────────────────────
  // The engine reports the clicked element's tree leaf key; unloading it goes through the same
  // toggleItemType as the tree checkbox. hiddenTypes is a stack for "Restore last" (Ctrl+Z).
  const hideType = (key) => {
    if (typeStates[key] !== "loaded") return;
    toggleItemType(key);
    setHiddenTypes((prev) => [...prev.filter((k) => k !== key), key]);
  };
  const hideTypeRef = useRef(hideType);
  hideTypeRef.current = hideType;

  const restoreLastHidden = useCallback(() => {
    const key = hiddenTypes[hiddenTypes.length - 1];
    if (!key) return;
    setHiddenTypes((prev) => prev.slice(0, -1));
    if (typeStates[key] === "idle" || typeStates[key] === "error") toggleItemType(key);
  }, [hiddenTypes, typeStates, toggleItemType]);

  const typeLabels = useMemo(() => buildTypeLabels(itemTree), [itemTree]);
  const typeLabelsRef = useRef(typeLabels);
  typeLabelsRef.current = typeLabels;

  // ── View → Zoom to (entity identifier, e.g. Bridge_01) ──────────────────────
  // Frames the entity's plan extents in top ortho view, from the .bim's spatial index
  // (/api/extents), so it works before anything is loaded; then loads whatever of the entity
  // isn't loaded yet, through the same toggles as the tree. The very first load of a file
  // re-fits to the whole model (refreshBoundsAfterLoad), so in that case frame again after.
  const zoomEntities = useMemo(() => collectZoomEntities(itemTree), [itemTree]);

  // ── Toolbar actions ───────────────────────────────────────────────────────
  const activateMode = useCallback((mode) => {
    setActiveMode(mode);
    if (mode !== "measure") setMeasureResult(null);
    if (mode !== "tracker") setTrackerData(null);
    // Cancel mid-pick when switching away; keep "active" so section plane stays visible
    if (mode !== "section") setSectionState((prev) => (prev === "active" ? "active" : "idle"));
    viewerRef.current?.setMode(mode);
  }, []);

  // Hide item type: Ctrl+Z restores the last hidden type, Esc leaves the mode.
  useEffect(() => {
    if (activeMode !== "hidetype") return;
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, [contenteditable]")) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); restoreLastHidden(); }
      else if (e.key === "Escape") activateMode("rotate");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeMode, restoreLastHidden, activateMode]);

  const clearSection = useCallback(() => {
    viewerRef.current?.stopSectionView();
    viewerRef.current?.clearSection();
    sectionPtsRef.current = null;
    setSectionCoord(null);
    setSectionPanActive(true); sectionPanRef.current = true;
    setSectionState("idle");
    setSectionOpen(false);
    setSectionFromStation(false);
    sectionFromStationRef.current = false;
    sectionDrawerReadyRef.current = false;
    sectionMeasureRef.current = false;
    setSectionMeasureActive(false);
    setActiveMode("rotate");
    viewerRef.current?.setMode("rotate");
  }, []);

  const flipSection = useCallback(() => viewerRef.current?.flipSection(), []);

  // ── Alignment tracking ────────────────────────────────────────────────────

  // Fetch alignment list from backend (lazy — only once per file)
  const fetchAlignments = useCallback(async () => {
    if (alignListRef.current !== null) return; // already fetched successfully — don't refetch
    setAlignLoading(true);
    try {
      const r = await fetch(`/api/alignments/${file.projectId}/${encodeURIComponent(file.fileName)}`);
      if (!r.ok) throw new Error(`Failed to load alignments (${r.status})`);
      const data = await r.json();
      const list = data.alignments || [];
      setAlignList(list);
      alignListRef.current = list; // only cache on a real success — a failed attempt must stay
                                    // retryable, since this same list feeds Tracking, Show
                                    // Alignment, Stations, QTO, and Profile all at once; caching
                                    // a failure here would silently break all five for the rest
                                    // of the session with no way to recover short of a reload.
    } catch {
      setAlignList([]);
      // alignListRef.current intentionally left untouched (still null) so the next tool that
      // calls fetchAlignments() retries instead of reusing a cached failure.
    } finally {
      setAlignLoading(false);
    }
  }, [file]);

  // Start tracking a chosen alignment (tessellate + hand to engine)
  const startTracking = useCallback((alignName) => {
    const list = alignListRef.current || [];
    const align = list.find(a => a.name === alignName);
    if (!align || !viewerRef.current) return;

    const tessellated = tessellateAlignment(align.path, align.startStation);

    viewerRef.current.startAlignmentTracking(tessellated, {
      onHover: (result) => setAlignHover(result ? { station: result.station, distance: result.distance, radius: result.radius } : null),
      onMark:  (result) => setAlignMark({ station: result.station, distance: result.distance }),
    }, alignLabelCanvasRef.current);

    setActiveAlignName(alignName);
    setAlignActive(true);
    setAlignHover(null);
    setAlignMark(null);
    setAlignLockedStation(null);

    // Switch to top ortho view — alignment tracking is a 2D operation. Skipped if already
    // there, same reasoning as startStationPick below: setView() always re-fits on the whole
    // model, discarding the user's current zoom/pan for no reason if the orientation was
    // already correct.
    if (!viewerRef.current.isTopOrthoView()) {
      viewerRef.current.setView("top");
      viewerRef.current.setCameraMode("ortho");
      setIsPerspective(false); // keep toolbar Ortho/Persp button in sync
    }
  }, []);

  const stopTracking = useCallback(() => {
    viewerRef.current?.stopAlignmentTracking();
    setAlignActive(false);
    setActiveAlignName(null);
    setAlignHover(null);
    setAlignMark(null);
    setAlignLockedStation(null);
    setAlignPickerOpen(false);
  }, []);

  const fitProfile = useCallback(() => {
    const canvas  = profileCanvasRef.current;
    const vaList  = profilePathRef.current;
    if (!canvas || !vaList || vaList.length === 0) return;
    const b = getCombinedBounds(vaList);
    const ML=65, MR=20, MT=14, MB=34;
    const DW = canvas.clientWidth - ML - MR;
    const DH = canvas.clientHeight - MT - MB;
    if (!DW || !DH) return;
    const fitVE = Math.max(1, Math.round(DH * b.stnRange / (DW * b.elevRange * 1.2)));
    profileVScaleRef.current = fitVE;
    setProfileVScale(fitVE);
    profileViewRef.current = { zoom: 1, cx_stn: (b.minStn+b.maxStn)/2, cy_elev: (b.minElev+b.maxElev)/2 };
    drawProfileCanvas(canvas, vaList, fitVE, profileViewRef.current, profileShowAnnotRef.current);
  }, []);

  // ── Satellite imagery ─────────────────────────────────────────────────────
  // M27 v6: imagery now follows the current viewport instead of fetching once for the whole
  // file's declared/computed extent. That extent turned out to be unreliable per-file — one
  // file's real modeled geometry stopped ~27km short of its own declared Extents on one edge,
  // so a fixed whole-file fetch either wasted resolution on empty area or silently ran out of
  // coverage right where the user was actually looking. Deriving the fetch area from the
  // camera itself sidesteps that data-quality question entirely, and as a bonus gives much
  // better effective resolution when zoomed in (the same fixed 2048×2048 raster now covers
  // whatever's on screen, not the whole multi-km corridor). Full story:
  // docs/digitwin/Satellite Imagery.md.
  const WGS84_DEF = "+proj=longlat +datum=WGS84 +no_defs";

  // Builds the ESRI URL + local mesh bounds/grid for whatever ground rectangle is currently
  // visible and hands it to the viewer. Shared by the initial toggle-on and every
  // camera-settle refresh afterward (see watchCamera below).
  const refreshImagery = useCallback(() => {
    const geo = imageryGeoRef.current;
    if (!geo?.bbox) return;
    const { bbox, proj4Def } = geo;
    const px = IMAGERY_QUALITY_PX[imageryQuality] || IMAGERY_QUALITY_PX.low;

    // Recentering offset — MUST stay derived from the declared bbox (dgn_Db/Extents), same
    // as geometry.js/modelOffset uses everywhere else, so the imagery plane lands in the
    // correct LOCAL position relative to already-rendered model geometry. Avoids dependency
    // on getModelOffset() which is 0,0,0 before any geometry is loaded.
    const offset = {
      x: (bbox.low[0] + bbox.high[0]) / 2,
      y: (bbox.low[1] + bbox.high[1]) / 2,
      z: (bbox.low[2] + bbox.high[2]) / 2,
    };
    const z = bbox.low[2] - offset.z - 1;

    if (proj4Def) {
      // Fast path: reproject the CURRENT viewport's ground footprint entirely client-side
      // (pure proj4 JS math, no server round-trip needed beyond the ESRI image fetch itself).
      const local = viewerRef.current?.getVisibleGroundBounds(z);
      if (!local) return; // camera too near-horizontal for a sane ground rect — keep showing whatever's already there

      // Pad the visible viewport by 20% so a small pan/zoom doesn't immediately outrun the
      // just-fetched image before the next debounced refresh (watchCamera below) catches up.
      const pad   = 0.2;
      const spanX = local.maxX - local.minX, spanY = local.maxY - local.minY;
      const padded = {
        minX: local.minX - spanX * pad, maxX: local.maxX + spanX * pad,
        minY: local.minY - spanY * pad, maxY: local.maxY + spanY * pad,
      };

      // Grid resolution must track REAL-WORLD SPAN, not just "some fixed point count" — a
      // Transverse Mercator grid isn't rotation-aligned with true lon/lat away from the central
      // meridian (meridian convergence, same root cause as the v2 flat-quad bug), and the mesh
      // only compensates for that AT each sampled grid point, linearly interpolating position/
      // UV in between. A flat n=17 was fine for a small zoomed-in viewport (tiny cells, tiny
      // interpolation error) but reintroduced visible position drift/warping once zoomed out
      // far enough that each of those 16 cells spans kilometers again — exactly the failure v5
      // already hit and fixed (by using 25×25) at the old whole-file (~54km) scale. Target a
      // fixed ~400m cell size instead so error stays roughly constant regardless of zoom level;
      // clamped since proj4 is cheap but not free — 41×41 (1681 points) is still sub-ms.
      const TARGET_CELL_M = 400;
      const spanForGrid = Math.max(padded.maxX - padded.minX, padded.maxY - padded.minY);
      const n = Math.min(41, Math.max(9, Math.ceil(spanForGrid / TARGET_CELL_M) + 1));
      const geoGridPts = [];
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
          const localX = padded.minX + (padded.maxX - padded.minX) * (i / (n - 1));
          const localY = padded.minY + (padded.maxY - padded.minY) * (j / (n - 1));
          const [lon, lat] = proj4(proj4Def, WGS84_DEF, [localX + offset.x, localY + offset.y]);
          geoGridPts.push({ lon, lat });
        }
      }
      const lons = geoGridPts.map((p) => p.lon), lats = geoGridPts.map((p) => p.lat);
      const minLon = Math.min(...lons), maxLon = Math.max(...lons);
      const minLat = Math.min(...lats), maxLat = Math.max(...lats);
      const bboxParam = `${minLon},${minLat},${maxLon},${maxLat}`;

      // Request pixels matching the bbox's OWN aspect ratio, not a fixed square. A local
      // (roughly) square viewport reprojects to a non-square WGS84 bbox at this latitude (1°
      // longitude ≈ 97.5km here vs 1° latitude ≈ 111.3km — a ~14% mismatch), and ArcGIS
      // Server's export commonly auto-adjusts a bbox/size aspect-ratio mismatch by silently
      // rendering a DIFFERENT (expanded) area than the one requested, to avoid distorting the
      // image — breaking our assumption that the returned image corresponds exactly to
      // [minLon,maxLon]x[minLat,maxLat]. Confirmed the bug tracks the *request shape* (Esri's
      // own viewer shows no equivalent drift at any zoom), so removing the mismatch removes
      // Esri's reason to adjust anything.
      const aspect = (maxLon - minLon) / (maxLat - minLat);
      const w = aspect >= 1 ? px : Math.max(1, Math.round(px * aspect));
      const h = aspect >= 1 ? Math.max(1, Math.round(px / aspect)) : px;
      const url = `/api/geo/imagery/esri?bbox=${encodeURIComponent(bboxParam)}&sr=4326&size=${w},${h}`;

      viewerRef.current?.loadImagery(url, { ...padded, z }, { n, points: geoGridPts });
    } else {
      // Fallback for a GCS shape buildProj4Def() (server) can't handle — no client-side
      // reprojection available, so fall back to the old whole-file-extent behavior using
      // whatever imageryBbox/geoGrid the initial fetch already computed server-side.
      const { imageryBbox, geoBbox, geoGrid } = geo;
      if (!imageryBbox || !geoBbox) return;
      const { sw, ne } = geoBbox;
      const bboxParam = `${sw.lon},${sw.lat},${ne.lon},${ne.lat}`;
      const url = `/api/geo/imagery/esri?bbox=${encodeURIComponent(bboxParam)}&sr=4326&size=${px},${px}`;
      const bounds = {
        minX: imageryBbox.low[0]  - offset.x, maxX: imageryBbox.high[0] - offset.x,
        minY: imageryBbox.low[1]  - offset.y, maxY: imageryBbox.high[1] - offset.y,
        z,
      };
      viewerRef.current?.loadImagery(url, bounds, geoGrid);
    }
  }, [imageryQuality]);

  const toggleImagery = useCallback(async () => {
    if (imageryOn) {
      imageryWatchStopRef.current?.();
      imageryWatchStopRef.current = null;
      viewerRef.current?.unloadImagery();
      setImageryOn(false);
      return;
    }

    // Fetch geo data once per file
    if (!imageryGeoRef.current) {
      setImageryLoading(true);
      try {
        const r = await fetch(`/api/geo/${file.projectId}/${encodeURIComponent(file.fileName)}`);
        if (!r.ok) return;
        imageryGeoRef.current = await r.json();
      } catch { return; }
      finally { setImageryLoading(false); }
    }
    if (!imageryGeoRef.current?.bbox) return; // no georef data in this file

    refreshImagery();
    setImageryOn(true);

    // Hide stale imagery the instant the camera moves (it no longer matches the viewport),
    // and fetch fresh imagery for wherever the user actually settles — not on every
    // intermediate frame while still panning/zooming/rotating.
    imageryWatchStopRef.current = viewerRef.current?.watchCamera({
      onMove:   () => viewerRef.current?.unloadImagery(),
      onSettle: refreshImagery,
      settleMs: 500,
    });
  }, [imageryOn, file, refreshImagery]);

  // Reset imagery when file changes
  useEffect(() => {
    imageryWatchStopRef.current?.();
    imageryWatchStopRef.current = null;
    imageryGeoRef.current = null;
    setImageryOn(false);
  }, [file]);

  // Reset annotate callouts when file changes
  useEffect(() => {
    setAnnotateActive(false);
    setAnnotatePanelOpen(false);
    setAnnotateSelectedTypes([]);
    setAnnotateWarnings([]);
    setStationsActive(false);
    setStationsPanelOpen(false);
    setStationsAlignName(null);
    setShowAlignActive(false);
    setShowAlignPanelOpen(false);
    setShowAlignName(null);
  }, [file]);

  // ── Quantity Takeoff ──────────────────────────────────────────────────────

  const computeQto = useCallback(async () => {
    // Guards against a second boundary compute stacking on top of one already running — each
    // one grabs a slot in the server's shared, cpuCount-1-capped worker pool (server/routes/qto.js),
    // so a user re-triggering Compute while a dense-section run is still going wouldn't crash
    // anything, but it would eat a second slot for a request whose result nobody's waited for,
    // at the expense of every other concurrent QTO user queuing longer behind it.
    if (qtoLoading) return;
    const alignName = qtoAlignName;
    if (!alignName || !alignListRef.current || !file) return;
    const alignData = alignListRef.current.find(a => a.name === alignName);
    if (!alignData) return;

    const startStn = qtoStartStn ?? 0;
    const endStn   = qtoEndStn   ?? 0;
    const leftOff  = qtoLeftOff  ?? 50;
    const rightOff = qtoRightOff ?? 50;
    if (startStn >= endStn) return;

    const tessellated = tessellateAlignment(alignData.path, alignData.startStation ?? 0);
    const offset      = viewerRef.current?.getModelOffset() || { x: 0, y: 0, z: 0 };

    // Build boundary polygon in project XY and draw it in the viewport (full density — only
    // the copy sent to the QTO compute endpoint below gets simplified).
    const boundaryPoly = buildBoundaryPolygon(tessellated, startStn, endStn, leftOff, rightOff);
    const threeJsPts   = boundaryPoly.map(([px, py]) => ({ x: px - offset.x, y: py - offset.y, z: 0 }));
    threeJsPts.push(threeJsPts[0]);
    viewerRef.current?.drawQtoBoundary(threeJsPts);
    viewerRef.current?.fitToBoundary(threeJsPts);

    // Candidate elements are discovered server-side from the boundary itself (native spatial
    // index) — no longer scoped to whatever's currently loaded in the viewer, since that was
    // silently missing elements from any tree branch the user hadn't checked.
    const simplifiedBoundary = simplifyBoundaryPolygon(boundaryPoly);

    setQtoLoading(true);
    try {
      const resp = await fetch(
        `/api/qto/${file.projectId}/${encodeURIComponent(file.fileName)}/compute`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ boundaryPts: simplifiedBoundary }),
        }
      );
      if (!resp.ok) throw new Error(`QTO compute failed (${resp.status})`);
      const data = await resp.json();
      setQtoRows(data.rows || []);
      setQtoResultReady(true);
    } catch {
      setQtoRows([]);
      setQtoResultReady(true);
    } finally {
      setQtoLoading(false);
    }
  }, [qtoLoading, qtoAlignName, qtoStartStn, qtoEndStn, qtoLeftOff, qtoRightOff, file]);

  // ── Annotate ──────────────────────────────────────────────────────────────
  // Callout cards (dot + leader line + "Name" property) over elements currently visible
  // in the viewport, limited to the item type(s) chosen in the (non-modal) Annotate
  // panel. One lightweight bulk lookup (grouped by aspect class, no geometry/quantity
  // computation) for just the Name values — no full QTO pipeline.
  // A bridge is one picker option ("bridge:<name>"), expanded here to its Substructure leaf keys —
  // only piers/abutments are labelled; leaves that aren't loaded simply have nothing visible. Caps
  // are sampled on their own (own 40-element budget, so piles can't crowd them out), anchored at the
  // centre of their top face and marked as the preferred anchor: every support's dot sits on top of
  // its cap, the same spot on every pier and abutment.
  //
  // Density guard: an item type with more than ANNOTATE_LIMIT elements in view (an IFC building's
  // coverings/bricks) is not labelled at all and is named in the panel's warning with its count —
  // dozens of overlapping callouts are useless, and the old silent 40-element sample made it look
  // as if those were the only elements. The other picked types still get labelled. Bridges count
  // per support (their caps), never per pile. Re-checked on every camera settle, so zooming in
  // brings a skipped type back once it's under the limit.
  const applyAnnotate = useCallback(async (selectedTypes, ifcLabel = "type") => {
    const viewer = viewerRef.current;
    if (!file || !viewer || !selectedTypes || selectedTypes.length === 0) return;

    const bridges = Object.fromEntries(collectBridges(itemTree).map((b) => [`bridge:${b.name}`, b]));
    const displayNames = Object.fromEntries(collectLeafItemTypes(itemTree).map((t) => [t.key, t.displayName]));
    const counts = viewer.countVisibleElements(selectedTypes.flatMap((k) => bridges[k]?.supportKeys || [k]));

    const dense = [], capTypes = [], supportTypes = [], plainTypes = [];
    for (const k of selectedTypes) {
      const b = bridges[k];
      if (b) {
        const caps = b.supportKeys.filter((s) => s.endsWith("/Cap"));
        const n = caps.reduce((sum, c) => sum + (counts[c] || 0), 0);
        if (n > ANNOTATE_LIMIT) { dense.push({ name: `${b.name} supports`, count: n }); continue; }
        capTypes.push(...caps);
        supportTypes.push(...b.supportKeys.filter((s) => !s.endsWith("/Cap")));
      } else {
        const n = counts[k] || 0;
        if (n > ANNOTATE_LIMIT) { dense.push({ name: displayNames[k] || k, count: n }); continue; }
        plainTypes.push(k);
      }
    }
    setAnnotateWarnings(dense);

    const visibleOf = (types, opts) => (types.length > 0 && viewer.getVisibleElements({ itemTypes: types, ...opts })) || [];
    const visible = [
      ...visibleOf(capTypes, { anchor: "top", maxCount: ANNOTATE_LIMIT }).map((v) => ({ ...v, preferred: true })),
      // other support parts only place the dot when a support's cap isn't loaded — a sample is enough
      ...visibleOf(supportTypes, { maxCount: ANNOTATE_LIMIT }),
      // every remaining type is at or under the limit, so nothing is sampled away
      ...visibleOf(plainTypes, { maxCount: ANNOTATE_LIMIT * plainTypes.length }),
    ];
    viewer.clearAnnotations();
    // Stay active while something was skipped, so zooming in re-applies
    if (visible.length === 0) { setAnnotateActive(dense.length > 0); return; }

    setAnnotateLoading(true);
    try {
      const resp = await fetch(
        `/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/names`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ elementIds: visible.map((v) => v.elementId), ifcLabel }),
        }
      );
      if (!resp.ok) throw new Error(`Names lookup failed (${resp.status})`);
      const data  = await resp.json();
      const names = data.names || {};

      // Elements with no "Name" property (rare) are skipped rather than shown blank.
      // A single named feature (e.g. one turnout "T1") is frequently made up of several
      // separate geometric elements — different rails/curves that all carry the same
      // Name property. Without grouping, each sub-element gets its own callout: several
      // duplicate labels for the same feature, each sitting at its own sub-element's
      // centroid rather than the feature's actual location. See clusterVisibleByName's own
      // comment for why this is spatially-clustered rather than a blind average.
      const items = clusterVisibleByName(visible, names);

      viewerRef.current?.renderAnnotations(items);
      setAnnotateActive(true);
    } catch {
      // silently no-op — annotate is a visual extra, not a critical path
    } finally {
      setAnnotateLoading(false);
    }
  }, [file, itemTree]);

  // Re-run Annotate automatically once the camera settles after a pan/zoom/rotate, while
  // labeling is active. Every anchor/grouping applyAnnotate computes is inherently tied to the
  // camera at the moment it ran (getVisibleElements only considers what's currently in the
  // frustum, and the anchor fix above picks whichever vertex is nearest the camera) — leaving
  // old callouts up after the view changes means they're showing stale positions for a view
  // that's no longer current. Reuses the same watchCamera({onSettle}) mechanism satellite
  // imagery already uses for "re-fetch once the user actually stops looking, not on every
  // intermediate frame while still moving" — see toggleImagery. Deliberately only re-applies on
  // settle, not on every onMove tick, so labels stay visible (if momentarily stale) through an
  // orbit/pan instead of flickering away and back.
  useEffect(() => {
    if (!annotateActive) return;
    const stop = viewerRef.current?.watchCamera({
      onSettle: () => applyAnnotate(annotateSelectedTypes, annotateIfcLabel),
      settleMs: 500,
    });
    return () => stop?.();
  }, [annotateActive, annotateSelectedTypes, annotateIfcLabel, applyAnnotate]);

  // Closing the panel removes any labeling that was applied — Annotate is a transient
  // view aid, not a persistent state.
  const closeAnnotatePanel = useCallback(() => {
    viewerRef.current?.clearAnnotations();
    setAnnotateActive(false);
    setAnnotateWarnings([]);
    setAnnotatePanelOpen(false);
  }, []);

  // ── IoT sensor simulation ────────────────────────────────────────────────────
  // No real sensor feed exists yet — this fakes an "open" reading per visible Turnout on a
  // timer, purely to illustrate what a live digital-twin sensor overlay looks like. Reuses
  // Annotate's exact pipeline (getVisibleElements → bulk Name lookup → group sub-elements by
  // Name so one physical turnout gets one point, not one per rail/curve sub-element — see
  // applyAnnotate's comment for why that grouping matters), just swaps the "roll a random
  // open/closed state, pulse only the open ones" step in for "draw every label".
  const applyIotSimulation = useCallback(async () => {
    if (!file || !viewerRef.current) return;
    // Match on displayName, not the raw EC class name in t.key (which for this item type is
    // "TurnoutsElementAspect", not "Turnouts" — displayName is what the tree actually shows).
    const turnoutKeys = collectLeafItemTypes(itemTree)
      .filter((t) => typeStates[t.key] === "loaded" && t.displayName === "Turnouts")
      .map((t) => t.key);
    if (turnoutKeys.length === 0) { viewerRef.current.clearPulses(); setIotSignals([]); return; }

    const visible = viewerRef.current.getVisibleElements({ maxCount: 60, itemTypes: turnoutKeys });
    if (visible.length === 0) { viewerRef.current.clearPulses(); setIotSignals([]); return; }

    try {
      const resp = await fetch(
        `/api/geometry/${file.projectId}/${encodeURIComponent(file.fileName)}/names`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ elementIds: visible.map((v) => v.elementId) }) }
      );
      if (!resp.ok) throw new Error(`Names lookup failed (${resp.status})`);
      const data  = await resp.json();
      const names = data.names || {};

      const grouped = clusterVisibleByName(visible, names);

      // Simulated sensor read: each physical turnout independently has ~40% odds of
      // reporting "open" this cycle. Re-rolled every tick — see the setInterval below.
      // The pulse (3D view) only shows OPEN ones; the live table lists every turnout
      // currently in view so both states are visible at a glance.
      const now = new Date();
      const openItems = [];
      const signals   = [];
      for (const g of grouped) {
        const isOpen = Math.random() < 0.4;
        if (isOpen) openItems.push({ position: g.position });
        signals.push({ name: g.label, signal: isOpen ? "OPEN" : "CLOSED", time: now });
      }
      signals.sort((a, b) => a.name.localeCompare(b.name));

      viewerRef.current.renderPulses(openItems);
      setIotSignals(signals);
    } catch {
      // silently no-op — this is a visual extra, not a critical path
    }
  }, [file, itemTree, typeStates]);

  // setInterval captures whatever function reference it's given at creation time — without
  // this ref indirection, loading more turnouts (or switching files) after the simulation
  // started would keep ticking the stale closure from before that change.
  const applyIotSimulationRef = useRef(applyIotSimulation);
  applyIotSimulationRef.current = applyIotSimulation;

  const openIotPanel = useCallback(() => {
    setIotPanelOpen(true);
    applyIotSimulationRef.current();
    if (iotIntervalRef.current) clearInterval(iotIntervalRef.current);
    iotIntervalRef.current = setInterval(() => applyIotSimulationRef.current(), 2000);
  }, []);

  // Closing the panel stops the simulated feed and clears the pulses — same transient-
  // view-aid behaviour as Annotate/Stations/Show Alignment.
  const closeIotPanel = useCallback(() => {
    if (iotIntervalRef.current) { clearInterval(iotIntervalRef.current); iotIntervalRef.current = null; }
    viewerRef.current?.clearPulses();
    setIotPanelOpen(false);
    setIotSignals([]);
  }, []);

  // ── Stations ──────────────────────────────────────────────────────────────
  // Perpendicular tick + station label every 100 m along the selected alignment,
  // limited to whatever's currently in view — same reasoning as Label Elements:
  // a long alignment can have hundreds of 100 m stations, so only the ones on
  // screen right now get drawn rather than computing/rendering the whole thing.
  const applyStations = useCallback((alignName) => {
    if (!alignName || !alignListRef.current) return;
    const alignData = alignListRef.current.find(a => a.name === alignName);
    if (!alignData) return;

    const tessellated = tessellateAlignment(alignData.path, alignData.startStation ?? 0);
    const { cumulLengths } = tessellated;
    if (!cumulLengths || cumulLengths.length === 0) return;

    const STEP  = 100;
    const start = cumulLengths[0];
    const end   = cumulLengths[cumulLengths.length - 1];
    const first = Math.ceil(start / STEP) * STEP;

    const items = [];
    for (let s = first; s <= end; s += STEP) {
      const pt = getPositionAtStation(tessellated, s);
      if (!pt) continue;
      items.push({ x: pt.x, y: pt.y, tx: pt.tx, ty: pt.ty, label: formatStation(s) });
    }

    viewerRef.current?.renderStations(items);
    setStationsActive(true);
    setStationsAlignName(alignName);
  }, []);

  // Re-run Stations automatically once the camera settles after a pan/zoom/rotate, while
  // active — same reasoning and same watchCamera({onSettle}) mechanism as Annotate above.
  // "The view-limiting happens entirely inside the engine's frustum filter" (see
  // docs/digitwin/Alignment.md) means the visible tick subset is only ever correct for the
  // camera state at the moment renderStations() was last called — without this, panning/zooming
  // away leaves stale ticks from the old view up instead of showing what's now on screen.
  useEffect(() => {
    if (!stationsActive || !stationsAlignName) return;
    const stop = viewerRef.current?.watchCamera({
      onSettle: () => applyStations(stationsAlignName),
      settleMs: 500,
    });
    return () => stop?.();
  }, [stationsActive, stationsAlignName, applyStations]);

  // Closing the panel removes the ticks/labels — same transient-view-aid behaviour as Annotate.
  const closeStationsPanel = useCallback(() => {
    viewerRef.current?.clearStations();
    setStationsActive(false);
    setStationsPanelOpen(false);
  }, []);

  // ── Show Alignment (M26 bullet 4) ────────────────────────────────────────────
  // Static thick black polyline for the whole selected alignment — unlike Tracking's
  // thin blue line (a live hover/tracking aid) or Stations (view-limited ticks), this
  // just draws the entire path once; a single line object is cheap regardless of length.
  const applyShowAlignment = useCallback((alignName) => {
    if (!alignName || !alignListRef.current) return;
    const alignData = alignListRef.current.find(a => a.name === alignName);
    if (!alignData) return;

    const tessellated = tessellateAlignment(alignData.path, alignData.startStation ?? 0);
    viewerRef.current?.showAlignmentPolyline(tessellated);

    // Frame the camera to the alignment — without this, if no item type geometry has been
    // loaded yet the camera is still wherever it started, and a freshly-drawn polyline (main8
    // alone spans 107 km) can easily be entirely outside the current view. Same mechanism QTO
    // uses to zoom to its boundary.
    const offset = viewerRef.current?.getModelOffset() || { x: 0, y: 0, z: 0 };
    const threeJsPts = tessellated.points.map(([x, y]) => ({ x: x - offset.x, y: y - offset.y, z: 0 }));
    viewerRef.current?.fitToBoundary(threeJsPts);

    setShowAlignActive(true);
    setShowAlignName(alignName);
  }, []);

  // Closing the panel removes the polyline — same transient-view-aid behaviour as Stations/Annotate.
  const closeShowAlignPanel = useCallback(() => {
    viewerRef.current?.clearAlignmentPolyline();
    setShowAlignActive(false);
    setShowAlignPanelOpen(false);
  }, []);

  const downloadQtoExcel = useCallback(() => {
    if (!qtoRows || qtoRows.length === 0) return;
    const hasPhysical = qtoRows.some(r => r.physicalCount != null);
    const headers = ["Item Type", "Name", ...(hasPhysical ? ["Count"] : []), "Volume m³", "Length m", "Planar m²", "Sloped m²"];
    const rows = qtoRows.map(r => [
      r.itemType ?? "", r.name ?? "",
      ...(hasPhysical ? [r.physicalCount ?? ""] : []),
      r.volume ?? "", r.length ?? "", r.planarArea ?? "", r.slopedArea ?? "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    const sheetName = [qtoAlignName, qtoStartStn != null ? formatStation(qtoStartStn) : "", qtoEndStn != null ? formatStation(qtoEndStn) : ""]
      .filter(Boolean).join(" ").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName || "QTO");
    XLSX.writeFile(wb, `QTO_${sheetName || "results"}.xlsx`);
  }, [qtoRows, qtoAlignName, qtoStartStn, qtoEndStn]);

  const previewQtoBoundary = useCallback(() => {
    const alignName = qtoAlignName;
    if (!alignName || !alignListRef.current || !viewerRef.current) return;
    const alignData = alignListRef.current.find(a => a.name === alignName);
    if (!alignData) return;
    const startStn = qtoStartStn ?? 0;
    const endStn   = qtoEndStn   ?? 0;
    if (startStn >= endStn) return;
    const tessellated = tessellateAlignment(alignData.path, alignData.startStation ?? 0);
    const offset = viewerRef.current.getModelOffset();
    const poly = buildBoundaryPolygon(tessellated, startStn, endStn, qtoLeftOff ?? 50, qtoRightOff ?? 50);
    const pts = poly.map(([px, py]) => ({ x: px - offset.x, y: py - offset.y, z: 0 }));
    pts.push(pts[0]);
    viewerRef.current.drawQtoBoundary(pts);
    viewerRef.current.fitToBoundary(pts);
  }, [qtoAlignName, qtoStartStn, qtoEndStn, qtoLeftOff, qtoRightOff]);

  // ── Profile view ──────────────────────────────────────────────────────────

  const fetchVerticals = useCallback(async (alignName) => {
    if (!alignName || !file) return;
    setProfileLoading(true);
    setProfileVerts([]);
    setProfileSelectedVAs([]);
    profilePathRef.current = [];
    profileInitRef.current = false;
    try {
      const r = await fetch(`/api/verticals/${file.projectId}/${encodeURIComponent(file.fileName)}/${encodeURIComponent(alignName)}`);
      if (!r.ok) throw new Error(`Failed to load verticals (${r.status})`);
      const data = await r.json();
      const verts = data.verticals || [];
      setProfileVerts(verts);
      const primary = verts.find(v => v.name === "Vertical") || verts[0];
      if (primary) setProfileSelectedVAs([primary.name]);
    } catch {
      setProfileVerts([]);
    } finally {
      setProfileLoading(false);
    }
  }, [file]);

  // Sync profile refs for use in stale event-handler closures
  useEffect(() => { profileVScaleRef.current      = profileVScale ?? 1;           }, [profileVScale]);
  useEffect(() => { profileTrackActiveRef.current  = profileTrackActive;            }, [profileTrackActive]);
  useEffect(() => { profileShowAnnotRef.current    = profileShowAnnotations;        }, [profileShowAnnotations]);

  // Draw (or auto-fit then draw) whenever selected VAs or vScale changes
  useEffect(() => {
    if (!profileOpen || profileSelectedVAs.length === 0 || !profileCanvasRef.current) return;
    const vaList = profileSelectedVAs.map(name => {
      const va = profileVerts.find(v => v.name === name);
      return va ? { name: va.name, path: va.path } : null;
    }).filter(Boolean);
    if (vaList.length === 0) return;
    profilePathRef.current = vaList;
    const canvas = profileCanvasRef.current;
    const raf = requestAnimationFrame(() => {
      if (!profileInitRef.current) {
        const b = getCombinedBounds(vaList);
        const ML=65, MR=20, MT=14, MB=34;
        const DW = canvas.clientWidth - ML - MR;
        const DH = canvas.clientHeight - MT - MB;
        if (DW > 0 && DH > 0) {
          const fitVE = Math.max(1, Math.round(DH * b.stnRange / (DW * b.elevRange * 1.2)));
          profileVScaleRef.current = fitVE;
          setProfileVScale(fitVE);
          profileViewRef.current = { zoom: 1, cx_stn: (b.minStn+b.maxStn)/2, cy_elev: (b.minElev+b.maxElev)/2 };
          profileInitRef.current = true;
          drawProfileCanvas(canvas, vaList, fitVE, profileViewRef.current, profileShowAnnotRef.current);
          return;
        }
      }
      drawProfileCanvas(canvas, vaList, profileVScaleRef.current, profileViewRef.current, profileShowAnnotRef.current);
    });
    return () => cancelAnimationFrame(raf);
  }, [profileOpen, profileSelectedVAs, profileVerts, profileVScale]);

  // Attach scroll-zoom, left-drag-pan, touch pinch-zoom/pan, and tracker to the profile canvas
  useEffect(() => {
    const canvas = profileCanvasRef.current;
    if (!canvas || !profileOpen || profileSelectedVAs.length === 0) return;
    let isDragging = false, lastMX = 0, lastMY = 0;
    let lastPinchDist = null;
    const ML=65, MR=20, MT=14, MB=34;

    const redraw = () => {
      const c = profileCanvasRef.current;
      const vl = profilePathRef.current;
      if (c && vl && vl.length > 0) drawProfileCanvas(c, vl, profileVScaleRef.current, profileViewRef.current, profileShowAnnotRef.current);
    };

    // Shared by wheel-zoom and pinch-zoom — zoom by `factor`, anchored at canvas-local (mx, my)
    // so the point under the cursor/pinch-midpoint stays put instead of the view drifting.
    const zoomAt = (mx, my, factor) => {
      const vs  = profileViewRef.current;
      const vsc = profileVScaleRef.current;
      const DW  = canvas.clientWidth  - ML - MR;
      const DH  = canvas.clientHeight - MT - MB;
      if (!DW || !DH) return;
      const b   = getCombinedBounds(profilePathRef.current || []);
      const pps = DW / b.stnRange * vs.zoom;
      const ppe = vsc * pps;
      const cursorStn  = vs.cx_stn  + (mx - ML - DW/2) / pps;
      const cursorElev = vs.cy_elev - (my - MT - DH/2) / ppe;
      const nz = vs.zoom * factor;
      const np = DW / b.stnRange * nz, ne = vsc * np;
      profileViewRef.current = {
        zoom: nz,
        cx_stn:  cursorStn  - (mx - ML - DW/2) / np,
        cy_elev: cursorElev + (my - MT - DH/2) / ne,
      };
    };

    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1/1.15;
      const rect = canvas.getBoundingClientRect();
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, factor);
      redraw();
    };

    const touchDist = (t0, t1) => Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
    const touchMid  = (t0, t1, rect) => ({
      x: (t0.clientX + t1.clientX) / 2 - rect.left,
      y: (t0.clientY + t1.clientY) / 2 - rect.top,
    });

    // One finger: pan (mirrors onMouseDown/onMouseMove below). Two fingers: pinch-zoom,
    // anchored at the pinch midpoint — there's no mouse-hover equivalent on touch, so the
    // tracker just doesn't run here, same tradeoff already accepted for the main 3D viewport's
    // own touch support.
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDragging = true;
        lastMX = e.touches[0].clientX; lastMY = e.touches[0].clientY;
      } else if (e.touches.length === 2) {
        isDragging = false;
        lastPinchDist = touchDist(e.touches[0], e.touches[1]);
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1 && isDragging) {
        const t = e.touches[0];
        const dx = t.clientX - lastMX, dy = t.clientY - lastMY;
        lastMX = t.clientX; lastMY = t.clientY;
        const vs  = profileViewRef.current;
        const vsc = profileVScaleRef.current;
        const DW  = canvas.clientWidth  - ML - MR;
        const DH  = canvas.clientHeight - MT - MB;
        if (!DW || !DH) return;
        const b   = getCombinedBounds(profilePathRef.current || []);
        const pps = DW / b.stnRange * vs.zoom;
        const ppe = vsc * pps;
        profileViewRef.current = { ...vs, cx_stn: vs.cx_stn - dx/pps, cy_elev: vs.cy_elev + dy/ppe };
        redraw();
      } else if (e.touches.length === 2) {
        const rect = canvas.getBoundingClientRect();
        const dist = touchDist(e.touches[0], e.touches[1]);
        const mid  = touchMid(e.touches[0], e.touches[1], rect);
        if (lastPinchDist != null) {
          zoomAt(mid.x, mid.y, dist / lastPinchDist);
          redraw();
        }
        lastPinchDist = dist;
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        isDragging = false; lastPinchDist = null;
      } else if (e.touches.length === 1) {
        // Dropped from two fingers to one — resume panning from the remaining touch's current
        // position instead of jumping (lastMX/lastMY would otherwise still be wherever the
        // pinch started).
        isDragging = true;
        lastMX = e.touches[0].clientX; lastMY = e.touches[0].clientY;
        lastPinchDist = null;
      }
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      isDragging = true; lastMX = e.clientX; lastMY = e.clientY;
      canvas.style.cursor = 'grabbing';
    };

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      // Tracker
      if (profileTrackActiveRef.current) {
        drawProfileTracker(
          profileTrackerCanvasRef.current, mx,
          profilePathRef.current || [], profileVScaleRef.current, profileViewRef.current
        );
      }
      // Pan
      if (!isDragging) return;
      const dx = e.clientX - lastMX, dy = e.clientY - lastMY;
      lastMX = e.clientX; lastMY = e.clientY;
      const vs  = profileViewRef.current;
      const vsc = profileVScaleRef.current;
      const DW  = canvas.clientWidth  - ML - MR;
      const DH  = canvas.clientHeight - MT - MB;
      if (!DW || !DH) return;
      const b   = getCombinedBounds(profilePathRef.current || []);
      const pps = DW / b.stnRange * vs.zoom;
      const ppe = vsc * pps;
      profileViewRef.current = { ...vs, cx_stn: vs.cx_stn - dx/pps, cy_elev: vs.cy_elev + dy/ppe };
      redraw();
    };

    const onMouseUp = () => { isDragging = false; canvas.style.cursor = 'grab'; };

    const onMouseLeave = () => {
      const tc = profileTrackerCanvasRef.current;
      if (tc) { const ctx = tc.getContext("2d"); ctx.clearRect(0, 0, tc.width, tc.height); }
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
    window.addEventListener('mouseup', onMouseUp);
    canvas.style.cursor = 'grab';

    return () => {
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('mouseup', onMouseUp);
      if (canvas) canvas.style.cursor = '';
      const tc = profileTrackerCanvasRef.current;
      if (tc) { const ctx = tc.getContext("2d"); ctx.clearRect(0, 0, tc.width, tc.height); }
    };
  }, [profileOpen, profileSelectedVAs.length]);

  // Core section-at-station logic — accepts an explicit station value so Prev/Next can call
  // it with stnInput±interval without waiting for a state update.
  const applyAtStation = useCallback((station) => {
    const list  = alignListRef.current || [];
    const align = list.find(a => a.name === stnAlignName);
    if (!align || station == null || !viewerRef.current) return;

    stopTracking();

    const tessellated = tessellateAlignment(align.path, align.startStation ?? 0);
    const pt = getPositionAtStation(tessellated, station);
    if (!pt) return;

    // Rotate the alignment tangent by (90° - angle) to get the cut-line direction.
    // 90° (default) = perpendicular cross section.
    const phi = (90 - stnAngle) * Math.PI / 180;
    const cos = Math.cos(phi), sin = Math.sin(phi);
    const cutDirX = pt.tx * cos - pt.ty * sin;
    const cutDirY = pt.tx * sin + pt.ty * cos;

    const modelOff = viewerRef.current.getModelOffset();
    const L = 1e5;
    const ox = pt.x - modelOff.x, oy = pt.y - modelOff.y;
    const p1 = { x: ox + cutDirX * L, y: oy + cutDirY * L, z: 0 };
    const p2 = { x: ox - cutDirX * L, y: oy - cutDirY * L, z: 0 };

    setStnInput(station);
    setSectionFromStation(true);
    sectionFromStationRef.current = true;
    viewerRef.current.stopSectionView();
    viewerRef.current.setSectionPlane(p1, p2);
    sectionPtsRef.current = { p1, p2 };
    setSectionToken(t => t + 1);
    setSectionState("active");
    setSectionOpen(true);
    setStnPickerOpen(false);
  }, [stnAlignName, stnAngle, stopTracking]);

  const applySectionAtStation = useCallback(() => {
    applyAtStation(stnInput);
  }, [applyAtStation, stnInput]);

  // "Pick on screen" for At Station — same underlying engine mechanism as the Tracking tool
  // (startAlignmentTracking's onMark, click-to-project-onto-alignment), just wired to a
  // one-shot pick instead of Tracking's own persistent hover/lock panel: hide the modal so
  // the plan view is clickable, project the click onto the chosen alignment, then apply the
  // section at the picked station immediately — same as typing a number and hitting Apply.
  const startStationPick = useCallback(() => {
    const list  = alignListRef.current || [];
    const align = list.find(a => a.name === stnAlignName);
    if (!align || !viewerRef.current) return;

    if (alignActive) stopTracking();
    setTopBarStnModalOpen(false);
    setStnPicking(true);

    const tessellated = tessellateAlignment(align.path, align.startStation ?? 0);
    viewerRef.current.startAlignmentTracking(tessellated, {
      onMark: (result) => {
        viewerRef.current?.stopAlignmentTracking();
        setStnPicking(false);
        applyAtStation(Math.round(result.station * 100) / 100);
      },
    });

    // Top ortho view for a clear plan-view pick, same as the Tracking tool defaults to — but
    // only if not already there, since setView() always re-centers/re-fits on the whole model
    // (no "just confirm orientation" mode) and would otherwise discard whatever the user had
    // already zoomed/panned to, right as they're about to make a precise pick.
    if (!viewerRef.current.isTopOrthoView()) {
      viewerRef.current.setView("top");
      viewerRef.current.setCameraMode("ortho");
      setIsPerspective(false);
    }
  }, [stnAlignName, alignActive, stopTracking, applyAtStation]);

  const cancelStationPick = useCallback(() => {
    viewerRef.current?.stopAlignmentTracking();
    setStnPicking(false);
    setTopBarStnModalOpen(true);
  }, []);

  // "Pick on screen" for QTO's Start/End Station — same mechanism as startStationPick above,
  // but fills one of two fields instead of auto-applying, since QTO needs both a start and
  // an end before Draw Boundary/Compute make sense; the user picks (or types) each one, then
  // triggers those explicitly. `which` is "start" or "end".
  const startQtoStationPick = useCallback((which) => {
    const list  = alignListRef.current || [];
    const align = list.find(a => a.name === qtoAlignName);
    if (!align || !viewerRef.current) return;

    if (alignActive) stopTracking();
    setQtoOpen(false);
    setQtoPicking(which);

    const tessellated = tessellateAlignment(align.path, align.startStation ?? 0);
    viewerRef.current.startAlignmentTracking(tessellated, {
      onMark: (result) => {
        viewerRef.current?.stopAlignmentTracking();
        setQtoPicking(null);
        const station = Math.round(result.station * 100) / 100;
        if (which === "start") setQtoStartStn(station); else setQtoEndStn(station);
        setQtoRows(null);
        viewerRef.current?.clearQtoBoundary();
        setQtoOpen(true);
      },
    });

    // Top ortho view for a clear plan-view pick, same as the Tracking tool defaults to — only
    // if not already there (see startStationPick above for why).
    if (!viewerRef.current.isTopOrthoView()) {
      viewerRef.current.setView("top");
      viewerRef.current.setCameraMode("ortho");
      setIsPerspective(false);
    }
  }, [qtoAlignName, alignActive, stopTracking]);

  const cancelQtoStationPick = useCallback(() => {
    viewerRef.current?.stopAlignmentTracking();
    setQtoPicking(null);
    setQtoOpen(true);
  }, []);

  // ── Tools panel data (M26) ────────────────────────────────────────────────
  // All Civil Tools task groups. Originally a Dropdown menu (bullet 2), then moved
  // into the right Sider (bullet 3) — the array below is the shared data source for
  // both the group icons and the item buttons rendered there; only the presentation
  // changed, not the underlying group/item structure or handleToolsMenuClick's logic.
  const toolsMenuItems = [
    { key: "g-align", type: "group", label: "Alignment", children: [
      { key: "align-tracking", label: "Tracking" },
      { key: "align-show",     label: "Show Alignment" },
    ]},
    { key: "g-cross", type: "group", label: "Cross Section", children: [
      { key: "cs-free",    label: "Free Pick" },
      { key: "cs-station", label: "At Station…" },
    ]},
    { key: "g-profile", type: "group", label: "Profile", children: [
      { key: "profile-open", label: "Open Profile View" },
    ]},
    { key: "g-qto", type: "group", label: "Quantities", children: [
      { key: "qto-open", label: "Quantity Takeoff from Boundary" },
    ]},
    { key: "g-bridge", type: "group", label: "Bridge Reports", children: [
      { key: "bridge-piles", label: "Pile Setting-Out Schedule" },
      { key: "bridge-boq",   label: "Bill of Quantities" },
    ]},
    { key: "g-annotate", type: "group", label: "Annotation", children: [
      { key: "annotate-open", label: "Label Elements" },
      { key: "stations-open", label: "Stations" },
    ]},
    { key: "g-iot", type: "group", label: "IoT", children: [
      { key: "iot-sensors", label: "Simulate Sensory Data" },
    ]},
  ];

  // Group icon paths (SvgIcon `d` prop) — keyed by the group's own key in toolsMenuItems above.
  const TOOL_GROUP_ICONS = {
    "g-align":    "M3 18C7 10 10 14 12 10S17 4 21 6",
    "g-cross":    "M2 19h20M2 19l5-10h10l5 10M7 9h10",
    "g-profile":  "M3 16l4-6 4 4 5-8 5 5",
    "g-qto":      "M4 4h16v16H4zM4 10h16M4 16h16M10 4v16M16 4v16",
    "g-bridge":   "M3 8h18M5 8v4M19 8v4M8 12h8v2H8zM9 14v7M15 14v7M12 14v7",
    "g-annotate": "M4 5h16v10H9l-5 4z",
    "g-iot":      "M5 12.5a10 10 0 0114 0M8 15.5a5.5 5.5 0 018 0M12 19h.01",
  };

  const handleToolsMenuClick = useCallback(({ key }) => {
    // Every tool here ends with the user needing to interact with the viewport itself (pick
    // a point, watch a tracking readout, view results) — the right panel (300px Sider on
    // desktop, an 86% overlay Drawer on mobile) just sits in the way after that point, and on
    // mobile it makes "Pick on screen" flows (cs-station, qto Start/End) all but unusable
    // since the Drawer covers almost the whole viewport. Auto-hide on every tool activation.
    setRightCollapsed(true);
    if (key === "align-tracking") {
      // Same logic as the old Civil Tools panel's Tracking button.
      setAnnotatePanelOpen(false);  // corner panels share a spot — keep only one open
      setStationsPanelOpen(false);
      setShowAlignPanelOpen(false);
      closeIotPanel();
      if (alignActive) {
        setAlignPickerOpen(true);
      } else {
        if (sectionState !== "idle") clearSection();
        setStnPickerOpen(false);
        fetchAlignments();
        setAlignPickerOpen(true);
      }
    } else if (key === "cs-free") {
      stopTracking();
      setStnPickerOpen(false);
      sectionFromStationRef.current = false;
      setSectionFromStation(false);
      if (sectionState === "active") { clearSection(); }
      else { activateMode("section"); setSectionState("picking1"); }
    } else if (key === "cs-station") {
      if (alignActive) stopTracking();
      setAlignPickerOpen(false);
      fetchAlignments();
      setTopBarStnModalOpen(true);
    } else if (key === "profile-open") {
      stopTracking();
      setStnPickerOpen(false);
      fetchAlignments();
      setProfileAlignName(null);
      setProfileVerts([]);
      setProfileSelectedVAs([]);
      profilePathRef.current = [];
      profileInitRef.current = false;
      setProfileOpen(true);
    } else if (key === "qto-open") {
      fetchAlignments();
      setQtoOpen(true);
    } else if (key === "bridge-piles") {
      setPileScheduleOpen(true);
    } else if (key === "bridge-boq") {
      setBridgeBoqOpen(true);
    } else if (key === "annotate-open") {
      setAlignPickerOpen(false);   // corner panels share a spot — keep only one open
      setStationsPanelOpen(false);
      setShowAlignPanelOpen(false);
      closeIotPanel();
      setAnnotateSelectedTypes([]);
      setAnnotateWarnings([]);
      setAnnotatePanelOpen(true);
    } else if (key === "stations-open") {
      setAlignPickerOpen(false);   // corner panels share a spot — keep only one open
      setAnnotatePanelOpen(false);
      setShowAlignPanelOpen(false);
      closeIotPanel();
      fetchAlignments();
      setStationsActive(false);
      viewerRef.current?.clearStations();
      setStationsPanelOpen(true);
    } else if (key === "align-show") {
      setAlignPickerOpen(false);   // corner panels share a spot — keep only one open
      setAnnotatePanelOpen(false);
      setStationsPanelOpen(false);
      closeIotPanel();
      fetchAlignments();
      setShowAlignPanelOpen(true);
    } else if (key === "iot-sensors") {
      setAlignPickerOpen(false);   // corner panels share a spot — keep only one open
      setAnnotatePanelOpen(false);
      setStationsPanelOpen(false);
      setShowAlignPanelOpen(false);
      openIotPanel();
    }
  }, [alignActive, sectionState, clearSection, stopTracking, activateMode, fetchAlignments, closeIotPanel, openIotPanel]);

  const fitToView       = useCallback(() => viewerRef.current?.fitToView(), []);
  const zoomBack        = useCallback(() => viewerRef.current?.zoomBack(), []);
  const setView         = useCallback((v) => viewerRef.current?.setView(v), []);
  const toggleWireframe = useCallback(() => {
    setWireframe((prev) => { const n = !prev; viewerRef.current?.setWireframe(n); return n; });
  }, []);
  const toggleCamera = useCallback(() => {
    setIsPerspective((prev) => {
      const n = !prev;
      viewerRef.current?.setCameraMode(n ? "perspective" : "ortho");
      return n;
    });
  }, []);

  const fileLabel = file.fileName.replace(/\.bim$/i, "");

  // Shared by "Back to Projects" and the top-nav guard below (registered via
  // useRegisterNavGuard) — same real consequence either way (unmounting this component
  // disposes every loaded item type), so one confirm, two trigger points. Only interrupts
  // navigation when something's actually loaded — an empty viewer leaves without asking.
  const confirmLeaveIfLoaded = (proceed) => {
    const hasLoadedModels = Object.values(typeStates).some((s) => s === "loaded");
    if (!hasLoadedModels) { proceed(); return; }
    Modal.confirm({
      title: "Leave the viewer?",
      content: "All loaded models will be unloaded when you leave the viewer.",
      okText: "Leave",
      cancelText: "Stay",
      onOk: proceed,
    });
  };
  useRegisterNavGuard(confirmLeaveIfLoaded);

  const handleBackToProjects = () => confirmLeaveIfLoaded(onBack);

  // ── Left-panel filter — narrows the tree to matching models and opens them ──
  const filterOptions  = useMemo(() => buildFilterOptions(collectModels(itemTree), filterGroups), [itemTree, filterGroups]);
  const filterActive   = isFilterActive(treeFilter);
  const matchedFacets  = useMemo(
    () => (filterActive ? filterOptions.facets.filter((m) => matchesFilter(m, treeFilter)) : filterOptions.facets),
    [filterOptions, treeFilter, filterActive]
  );
  const matchedRows    = useMemo(() => entriesByNode(matchedFacets), [matchedFacets]);
  const visibleTree    = useMemo(
    () => (filterActive ? pruneTree(itemTree, new Map(matchedRows.map((r) => [r.node, r.itemTypes]))) : itemTree),
    [itemTree, matchedRows, filterActive]
  );

  // Filtering opens every branch down to the matching models; clearing it brings back what the
  // user had open before.
  useEffect(() => {
    if (filterActive) {
      setModelsOpen(true); // picking a filter shows its result
      setExpandedKeys((prev) => {
        if (preFilterExpandedRef.current === null) preFilterExpandedRef.current = prev;
        return branchKeysOf(visibleTree);
      });
    } else if (preFilterExpandedRef.current !== null) {
      setExpandedKeys(preFilterExpandedRef.current);
      preFilterExpandedRef.current = null;
    }
  }, [visibleTree, filterActive]);

  // Load / unload one model row — whole model, or only the item types the filter narrowed it to
  const toggleRow = (row, load) => (row.node.bridge
    ? toggleBridgeGroup(row.node.bridge.leafKeys, load)
    : toggleDiscipline(row.itemTypes || row.node.itemTypes, load));

  const zoomToEntity = async (entity) => {
    setZoomToOpen(false);
    const box = unionModelBoxes(entity.models.flatMap((m) => m.modelIds || []), modelExtents);
    const frame = () => {
      if (!box || !viewerRef.current) return;
      viewerRef.current.fitToBoundary([{ x: box.minX, y: box.minY, z: box.minZ }, { x: box.maxX, y: box.maxY, z: box.maxZ }]);
      viewerRef.current.setCameraMode("ortho");
      setIsPerspective(false);
    };
    frame();
    const toLoad = entity.models.filter((m) => modelLeafKeys(m).some((k) => typeStates[k] !== "loaded" && typeStates[k] !== "loading"));
    if (toLoad.length === 0) return;
    const wasEmpty = !Object.values(typeStates).some((st) => st === "loaded");
    await Promise.all(toLoad.map((m) => toggleRow({ node: m }, true)));
    if (wasEmpty) frame();
  };

  // "Load N models" — every matching model in one go (item types one by one as the discipline
  // checkbox does, bridges as one batched request each); "Unload" once they're all in. With a
  // filter group picked (Earthworks) only that group's item types load, not the whole model.
  const matchedLeafKeys  = matchedFacets.flatMap((m) => m.leafKeys);
  const matchedAllLoaded = matchedLeafKeys.length > 0 && matchedLeafKeys.every((k) => typeStates[k] === "loaded");
  const matchedLoading   = matchedLeafKeys.some((k) => typeStates[k] === "loading");
  const loadMatching = async () => {
    for (const row of matchedRows) await toggleRow(row, !matchedAllLoaded);
  };
  const filterLoadAction = {
    label:    `${matchedAllLoaded ? "Unload" : "Load"} ${pluralModels(matchedRows.length)}`,
    onClick:  loadMatching,
    disabled: !viewerReady || matchedLoading,
    loading:  matchedLoading,
  };

  // Unload all (whole scene, filter or not) and Unload others (loaded item types the filter hides —
  // they'd otherwise sit in the 3D view with no tree row to untick). Explicit buttons rather than
  // auto-unloading on filter change: the filter is for finding, not for deciding the scene.
  const isEntryLoaded = (m) => m.leafKeys.some((k) => typeStates[k] === "loaded");
  const matchedSet    = new Set(matchedFacets);
  const loadedRows    = entriesByNode(filterOptions.facets.filter(isEntryLoaded)).map((r) => ({ ...r, itemTypes: null }));
  const hiddenRows    = filterActive
    ? entriesByNode(filterOptions.facets.filter((m) => !matchedSet.has(m) && isEntryLoaded(m)))
    : [];
  const anyLoading    = Object.values(typeStates).some((s) => s === "loading");
  const filterUnloadAll = {
    count:    loadedRows.length,
    onClick:  () => loadedRows.forEach((r) => toggleRow(r, false)),
    disabled: anyLoading,
  };
  const filterUnloadOthers = {
    count:    hiddenRows.length,
    onClick:  () => hiddenRows.forEach((r) => toggleRow(r, false)),
    disabled: anyLoading,
  };

  // ── Tree data — build from 5-level hierarchy ──────────────────────────────
  const treeData = buildTreeData(visibleTree, typeStates, viewerReady, toggleDiscipline, toggleBridgeGroup,
    file ? `/api/elements/${file.projectId}/${encodeURIComponent(file.fileName)}` : "");

  const checkedKeys = Object.entries(typeStates)
    .filter(([, s]) => s === "loaded")
    .map(([k]) => k);

  // Item types currently opened in the viewer — options for the Annotate panel's picker. Each bridge
  // with anything loaded is one option ("bridge:<name>"), labelled per assembly by the server.
  const loadedItemTypeOptions = [
    ...collectLeafItemTypes(itemTree).filter(t => typeStates[t.key] === "loaded"),
    ...collectBridges(itemTree)
      .filter((b) => b.supportKeys.some((k) => typeStates[k] === "loaded"))
      .map((b) => ({ key: `bridge:${b.name}`, displayName: `${b.name} (bridge)` })),
  ];
  // IFC classes are the custom IFC connector's "Ifc…ElementAspect" item types
  const annotateHasIfc = annotateSelectedTypes.some((k) => /^Ifc/.test(k));

  // Whether a given Tools panel item is the one currently running/open — drives the
  // highlighted state in the right Tools Sider (M26 bullet 3).
  const isToolActive = (key) => {
    switch (key) {
      case "align-tracking": return alignPickerOpen || alignActive;
      case "align-show":     return showAlignPanelOpen || showAlignActive;
      case "cs-free":        return sectionState === "active" && !sectionFromStation;
      case "cs-station":     return topBarStnModalOpen || (sectionState === "active" && sectionFromStation);
      case "profile-open":   return profileOpen;
      case "qto-open":       return qtoOpen || qtoModalOpen || !!qtoPicking || qtoLoading || qtoResultReady;
      case "annotate-open":  return annotatePanelOpen;
      case "stations-open":  return stationsPanelOpen;
      case "iot-sensors":    return iotPanelOpen;
      default:               return false;
    }
  };

  // How many tools are currently running, across every group — shown as a badge on the
  // Tools toggle button so it's visible even while the panel itself is collapsed.
  const activeToolsCount = toolsMenuItems.reduce(
    (n, group) => n + group.children.filter((item) => isToolActive(item.key)).length,
    0
  );

  const handleCheck = useCallback((_keys, info) => {
    toggleItemType(info.node.key);
  }, [toggleItemType]);

  // ── Properties panel content ──────────────────────────────────────────────
  const propsContent = () => {
    if (!selectedEl && !loadingProps) {
      return (
        <div style={{ padding: "12px 16px", fontSize: 12, color: "rgba(255,255,255,0.3)", lineHeight: 1.7 }}>
          Activate <strong>Select</strong> mode and click an element.
        </div>
      );
    }
    if (loadingProps) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 16px" }}>
          <Spin />
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>Loading…</span>
        </div>
      );
    }

    // Only show user-defined item type aspects; exclude Bentley template classes.
    // Filter on displayName (decoded, ElementAspect suffix already stripped) — not
    // className, which retains the raw DB suffix and would miss the pattern.
    const filteredAspects = (selectedEl._aspects || []).filter(
      (a) => !a.displayName.endsWith("TemplateProperties")
    );

    const headerName = filteredAspects[0]?.displayName || selectedEl._name;
    const multiAspect = filteredAspects.length > 1;

    const sectionLabel = (name) => (
      <div style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: "#4f9cf9", opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.08em", padding: "10px 0 4px" }}>
        {name}
      </div>
    );

    // raw: full-precision values of formatted bridge numbers (label → value), shown on hover
    const propRows = (entries, raw) => entries.map(([k, v]) => (
      <div key={k} style={{ display: "flex", alignItems: "center", minHeight: 28, borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "3px 0" }}>
        <div style={{ flex: "0 0 45%", fontFamily: "'DM Mono', monospace", fontSize: 11, textTransform: "uppercase", color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em", paddingRight: 8 }}>
          {k}
        </div>
        <div title={raw?.[k]} style={{ flex: 1, fontFamily: "'DM Mono', monospace", fontSize: 11, fontVariantNumeric: "tabular-nums", wordBreak: "break-word", color: v === "—" ? "rgba(255,255,255,0.2)" : undefined }}>
          {String(v)}
        </div>
      </div>
    ));

    // OpenBridge part (docs/digitwin/Bridges Roadmap.md, Phase 3): the server adds an identity section
    // (bridge / assembly / part type, rebuilt by bridgeClassifier.js) plus the part's native CifBridge
    // data and its assembly's metadata. Shown as collapsible sections; any other element keeps the
    // flat item-type layout below.
    const bridgeIdentity = filteredAspects.find((a) => a.bridge === "identity");
    if (bridgeIdentity) {
      const SECTION_TAG = { native: "OpenBridge", rule: "OBM rule", assembly: "Assembly" };
      const sections = filteredAspects.filter((a) => a !== bridgeIdentity);
      const id = bridgeIdentity.properties;
      const context = [id.Bridge, id.Headline, id.Assembly, id.Span && `Span ${id.Span}`].filter(Boolean).join(" · ");
      return (
        <div style={{ padding: "8px 16px 12px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>{bridgeIdentity.displayName}</div>
          <div style={{ paddingBottom: 8, fontSize: 11, fontFamily: "'DM Mono', monospace", color: "rgba(255,255,255,0.45)" }}>{context}</div>
          {propRows(Object.entries(id))}
          <Collapse
            size="small"
            ghost
            style={{ marginTop: 8 }}
            activeKey={bridgeOpenKeys}
            onChange={(keys) => setBridgeOpenKeys(keys)}
            items={sections.map((s, i) => ({
              key: `s${i}`,
              label: <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: "#4f9cf9", textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.displayName}</span>,
              extra: <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: "rgba(255,255,255,0.3)" }}>{SECTION_TAG[s.bridge] || "Item type"}</span>,
              styles: { header: { padding: "6px 0", alignItems: "center" }, body: { padding: "0 0 6px" } },
              children: propRows(Object.entries(s.properties), s.raw),
            }))}
          />
        </div>
      );
    }

    return (
      <div style={{ padding: "8px 16px" }}>
        {/* Item type name as the primary header */}
        <div style={{ paddingBottom: 8, fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
          {headerName}
        </div>

        {filteredAspects.length === 0 ? (
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", fontFamily: "'DM Mono', monospace" }}>
            No item type properties defined.
          </div>
        ) : (
          filteredAspects.map((aspect) => (
            <div key={aspect.className}>
              {multiAspect && sectionLabel(aspect.displayName)}
              {propRows(Object.entries(aspect.properties))}
            </div>
          ))
        )}
      </div>
    );
  };

  // Left item-type panel and right Tools panel content — built once and reused by both the
  // desktop Sider (push-panel) and the mobile Drawer (overlay) below, so there's a single
  // source of truth instead of two copies that could drift apart.
  const leftPanelBody = (
    <div className="dt-left">
      {/* Header — logo + the open file, straight on the panel background */}
      <div className="dt-left-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DigiTwinLogo size={20} />
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 14, color: "#4f9cf9", whiteSpace: "nowrap" }}>DigiTwin</span>
        </div>
        <div className="dt-left-file" title={file.fileName}>{fileLabel}</div>
      </div>

      {/* Filter card — asset tiles, discipline/status chips, search */}
      {itemTree.length > 0 && (
        <TreeFilter
          options={filterOptions}
          filter={treeFilter}
          onChange={setTreeFilter}
          matched={matchedRows.length}
          loadAction={filterLoadAction}
          unloadOthers={filterUnloadOthers}
        />
      )}

      {/* Models card — the item type tree */}
      <div className={`dt-card dt-models${modelsOpen ? " open" : ""}`}>
        <div className="dt-card-head">
          <button type="button" className="dt-filter-title" onClick={() => setModelsOpen((o) => !o)} aria-expanded={modelsOpen}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" className={`dt-filter-chevron${modelsOpen ? " open" : ""}`}>
              <path d="M9 6l6 6-6 6" />
            </svg>
            <span className="dt-card-title">Models</span>
          </button>
          <span className="dt-card-meta">
            {filterActive ? `${matchedRows.length} of ${filterOptions.models}` : filterOptions.models || ""}
          </span>
          {filterUnloadAll.count > 0 && (
            <Tooltip title={`Unload all ${pluralModels(filterUnloadAll.count)} from the view`} placement="bottom">
              <button type="button" className="dt-filter-clear unload" style={{ marginLeft: "auto" }}
                onClick={filterUnloadAll.onClick} disabled={filterUnloadAll.disabled}>
                Unload all · {filterUnloadAll.count}
              </button>
            </Tooltip>
          )}
        </div>
        {modelsOpen && <div className="dt-models-body">
          {itemTree.length === 0 ? (
            <div style={{ padding: "12px 14px", fontSize: 11, color: "rgba(255,255,255,0.25)" }}>
              No item types found in this file.
            </div>
          ) : visibleTree.length === 0 ? (
            <div style={{ padding: "12px 14px", fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
              No models match this filter.
            </div>
          ) : (
            <Tree
              checkable
              showLine={{ showLeafIcon: false }}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              checkedKeys={checkedKeys}
              onCheck={handleCheck}
              treeData={treeData}
              selectable={false}
              style={{ background: "transparent", padding: "2px 2px 6px" }}
            />
          )}
        </div>}
      </div>

      {/* Back to Projects — bottom of the panel */}
      <div className="dt-left-foot">
        <Button
          block
          size="small"
          onClick={handleBackToProjects}
          style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}
        >
          ← Back to Projects
        </Button>
      </div>
    </div>
  );

  const rightPanelBody = (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header row */}
      <div style={{
        padding: "14px 16px", borderBottom: "1px solid #252a33", flexShrink: 0,
        display: "flex", alignItems: "center", gap: 8,
        background: "linear-gradient(180deg, rgba(79,156,249,0.08), rgba(79,156,249,0) 70%)",
      }}>
        <SvgIcon d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" size={17} />
        <span style={{
          fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13,
          letterSpacing: "0.1em", textTransform: "uppercase", color: "#4f9cf9",
        }}>
          Tools
        </span>
      </div>

      {/* Groups (accordion) */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "6px 0 10px" }}>
        {toolsMenuItems.map((group, gi) => {
          const isOpen = openToolGroup === group.key;
          const groupHasActive = group.children.some((item) => isToolActive(item.key));
          return (
            <div key={group.key} style={{ padding: "4px 0" }}>
              <div
                className="dt-tool-group-header"
                onClick={() => setOpenToolGroup((prev) => (prev === group.key ? null : group.key))}
                style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 16px" }}
              >
                <span style={{ color: "#4f9cf9", opacity: 0.85, display: "flex", flexShrink: 0 }}>
                  <SvgIcon d={TOOL_GROUP_ICONS[group.key]} size={17} />
                </span>
                <span style={{
                  fontFamily: "'DM Mono', monospace", fontSize: 10.5, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.6)",
                  flex: 1,
                }}>
                  {group.label}
                </span>
                {groupHasActive && (
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                    background: "#4f9cf9", boxShadow: "0 0 5px 1px rgba(79,156,249,0.7)",
                  }} />
                )}
                <span className={`dt-tool-chevron${isOpen ? " open" : ""}`} style={{ color: "rgba(255,255,255,0.35)" }}>
                  <SvgIcon d="M9 6l6 6-6 6" size={13} />
                </span>
              </div>

              {isOpen && (
                <div className="dt-tool-group-body" style={{ display: "flex", flexDirection: "column", gap: 2, padding: "2px 10px 4px" }}>
                  {group.children.map((item) => {
                    const active = isToolActive(item.key);
                    return (
                      <button
                        key={item.key}
                        className={`dt-tool-item${active ? " active" : ""}`}
                        disabled={!viewerReady}
                        onClick={() => handleToolsMenuClick({ key: item.key })}
                        style={{
                          height: 34, paddingLeft: 12, paddingRight: 10, borderRadius: 6,
                          fontFamily: "'DM Mono', monospace", fontSize: 12.5, textAlign: "left",
                          display: "flex", alignItems: "center", gap: 8,
                          border: "none", borderLeft: "2px solid transparent",
                          background: "transparent", color: "rgba(255,255,255,0.78)",
                        }}
                      >
                        {item.label}
                        {active && (
                          <span style={{
                            marginLeft: "auto", width: 6, height: 6, borderRadius: "50%",
                            background: "#4f9cf9", boxShadow: "0 0 5px 1px rgba(79,156,249,0.7)", flexShrink: 0,
                          }} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {gi < toolsMenuItems.length - 1 && (
                <div style={{ height: 1, background: "#252a33", margin: "6px 16px 0" }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Layout style={{ height: "100%", overflow: "hidden" }}>

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <Header style={{ height: 52, lineHeight: "52px", padding: "0 12px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>

        {/* Left: sider toggle + project name + status */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <button
            className={`dt-panel-toggle${!leftCollapsed ? " open" : ""}`}
            onClick={() => setLeftCollapsed((v) => !v)}
            title={leftCollapsed ? "Show panel" : "Hide panel"}
          >
            <span className="dt-panel-toggle-icon">
              <SvgIcon d="M19 12H5M12 5l-7 7 7 7" size={15} />
            </span>
          </button>
          {!isMobile && (
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap" }}>
              {file.projectName}
            </span>
          )}
          {!isMobile && !viewerReady && !viewerError && (
            <Space size={6} style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
              <Spin size="small" />
              <span>Initializing…</span>
            </Space>
          )}
          {!isMobile && viewerReady && !viewerError && <Tag color="success" style={{ margin: 0 }}>Ready</Tag>}
          {viewerError && <Tag color="error" style={{ margin: 0 }}>Error</Tag>}
        </div>

        {/* Center: View + Interact tool groups — horizontally scrollable on mobile
            (minWidth:0 lets a flex:1 child actually shrink below its content width;
            without it the row would just push the header wider than the viewport
            instead of scrolling, clipping the Tools toggle off-screen). */}
        <div className="dt-header-tools-scroll" style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center", gap: 8,
          overflowX: isMobile ? "auto" : "visible",
        }}>

          {/* ── VIEW tools ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
            {[
              { title: "Fit all",     icon: "M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4M9 12h6M12 9v6",                                onClick: fitToView,                   active: false },
              { title: "Rotate",      icon: "M12 2a10 10 0 1 0 10 10M12 2v4M12 2l3 3M12 2l-3 3",                                   onClick: () => activateMode("rotate"), active: activeMode === "rotate" },
              { title: "Prev view",   icon: "M19 12H5M12 5l-7 7 7 7",                                                               onClick: zoomBack,                    active: false },
              { title: "Window zoom", icon: "M11 3H3v8M3 3l7 7M21 13v8h-8M21 21l-7-7",                                              onClick: () => activateMode("wzoom"), active: activeMode === "wzoom" },
              { title: "Top view",    icon: "M4 18h16M12 4v10M8 10l4 4 4-4",                                                        onClick: () => setView("top"),        active: false },
              { title: "Front view",  icon: "M3 3h18v18H3zM3 9h18M9 3v18",                                                          onClick: () => setView("front"),      active: false, visible: false },
              { title: "Side view",   icon: "M3 3h18v18H3zM12 3v18M3 12h18",                                                        onClick: () => setView("side"),       active: false, visible: false },
              { title: "Wireframe",   icon: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",                             onClick: toggleWireframe,             active: wireframe },
              { title: isPerspective ? "→ Ortho" : "→ Persp",
                                      icon: isPerspective ? "M3 20L12 4L21 20M6 14h12" : "M3 3h18v18H3zM3 3l4 4M21 3l-4 4M21 21l-4-4M3 21l4-4",
                                                                                                                                     onClick: toggleCamera,                active: !isPerspective },
            ].filter(({ visible = true }) => visible).map(({ title, icon, onClick, active }) => (
              <Tooltip key={title} title={title} placement="bottom" mouseEnterDelay={0.5}>
                <Button
                  type={active ? "primary" : "default"}
                  disabled={!viewerReady}
                  onClick={onClick}
                  icon={<SvgIcon d={icon} />}
                  style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              </Tooltip>
            ))}
            {/* BG colour picker — Ant Design's own ColorPicker (a React-rendered panel), not a
                hidden native <input type="color"> triggered via .click(): that relied on the
                OS-level picker trusting a programmatic click as "within a user gesture", which
                iOS Safari didn't reliably honor through the Tooltip wrapper — first tap only
                surfaced the tooltip, second tap opened nothing. A real React panel has no such
                native-gesture requirement on any platform. */}
            <Tooltip title="Background color" placement="bottom" mouseEnterDelay={0.5}>
              <ColorPicker
                value={bgColor}
                disabled={!viewerReady}
                onChange={(_, css) => { setBgColor(css); viewerRef.current?.setBackground(css); }}
              >
                <Button
                  type="default"
                  disabled={!viewerReady}
                  icon={<span style={{ display: "block", width: 13, height: 13, borderRadius: "50%", background: bgColor, border: "1px solid rgba(255,255,255,0.3)", flexShrink: 0 }} />}
                  style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              </ColorPicker>
            </Tooltip>
            {/* Satellite imagery — right-click for the resolution/speed tradeoff (see
                IMAGERY_QUALITY_PX): fixed presets only, never a free-typed size, so this can't
                get stuck requesting something huge and slow. */}
            <Dropdown
              trigger={["contextMenu"]}
              menu={{
                selectedKeys: [imageryQuality],
                onClick: ({ key }) => {
                  setImageryQuality(key);
                  if (imageryOn) refreshImagery();
                },
                items: [
                  { key: "low",    label: "Low (fastest)" },
                  { key: "medium", label: "Medium" },
                  { key: "high",   label: "High (slowest)" },
                ],
              }}
            >
              <Tooltip title={imageryOn ? "Hide imagery (right-click for quality)" : "Satellite imagery (right-click for quality)"} placement="bottom" mouseEnterDelay={0.5}>
                <Button
                  type={imageryOn ? "primary" : "default"}
                  disabled={!viewerReady || imageryLoading}
                  loading={imageryLoading}
                  onClick={toggleImagery}
                  icon={<SvgIcon d="M12 2a10 10 0 100 20A10 10 0 0012 2zM2 12h4M18 12h2M12 2v4M12 18v2M6.34 6.34l1.42 1.42M15.66 15.66l1.42 1.42M6.34 17.66l1.42-1.42M15.66 8.34l1.42-1.42" />}
                  style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              </Tooltip>
            </Dropdown>
          </div>

          {/* Separator */}
          <div style={{ width: 2, height: 24, background: "rgba(79,156,249,0.55)", flexShrink: 0, borderRadius: 1 }} />

          {/* ── INTERACT tools ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
            {[
              { title: "Select element",   icon: "M4 4l6 16 3-6 6-3z",                                              onClick: () => activateMode("select"),  active: activeMode === "select" },
              { title: "Measure distance", icon: "M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4",              onClick: () => activateMode("measure"), active: activeMode === "measure" },
              { title: "XYZ tracker",      icon: "M22 12a10 10 0 01-20 0 10 10 0 0120 0zM12 8v8M8 12h8",           onClick: () => activateMode("tracker"), active: activeMode === "tracker" },
            ].map(({ title, icon, onClick, active }) => (
              <Tooltip key={title} title={title} placement="bottom" mouseEnterDelay={0.5}>
                <Button
                  type={active ? "primary" : "default"}
                  disabled={!viewerReady}
                  onClick={onClick}
                  icon={<SvgIcon d={icon} />}
                  style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              </Tooltip>
            ))}
          </div>

          {/* Separator */}
          <div style={{ width: 2, height: 24, background: "rgba(79,156,249,0.55)", flexShrink: 0, borderRadius: 1 }} />

          {/* ── MODEL tools — Zoom to entity, Hide item type ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
            {/* Zoom to entity — list of entity identifiers (Bridge_01, Maintrack …), see zoomToEntity */}
            <Popover
              trigger="click"
              placement="bottom"
              open={zoomToOpen}
              onOpenChange={setZoomToOpen}
              destroyOnHidden
              content={<ZoomToList entities={zoomEntities} typeStates={typeStates} onPick={zoomToEntity} />}
            >
              <Tooltip title="Zoom to entity" placement="bottom" mouseEnterDelay={0.5}>
                <Button
                  type={zoomToOpen ? "primary" : "default"}
                  disabled={!viewerReady || zoomEntities.length === 0}
                  icon={<SvgIcon d="M10.5 3a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM21 21l-5.2-5.2M7.5 8.5v-1h1M12.5 7.5h1v1M13.5 12.5v1h-1M8.5 13.5h-1v-1" />}
                  style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                />
              </Tooltip>
            </Popover>
            <Tooltip title="Hide item type" placement="bottom" mouseEnterDelay={0.5}>
              <Button
                type={activeMode === "hidetype" ? "primary" : "default"}
                disabled={!viewerReady}
                onClick={() => activateMode(activeMode === "hidetype" ? "rotate" : "hidetype")}
                icon={<SvgIcon d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 9a3 3 0 100 6 3 3 0 000-6zM3 3l18 18" />}
                style={{ width: 28, height: 28, borderRadius: "50%", padding: 0, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              />
            </Tooltip>
          </div>

        </div>

        {/* Right: Tools panel toggle (M26 bullets 3/5) — opens the right Sider instead of a
            Dropdown flyout. Custom-styled (not a plain antd Button) — icon chip that rotates
            open, glowing pill background, and a live badge for how many tools are running,
            visible even while the panel itself is collapsed. */}
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {/* QTO background-compute status — a boundary-wide calc can take tens of seconds on
              dense sections, so it no longer pins the Quantities form Modal open with a spinner
              (see computeQto). This pill only renders while there's something to report: a
              pulsing "processing" dot while the request is in flight, a "success" dot once the
              result has landed, with a Popover to actually open it. */}
          {(qtoLoading || qtoResultReady) && (
            <Popover
              trigger="click"
              placement="bottomRight"
              open={qtoStatusPopoverOpen}
              onOpenChange={setQtoStatusPopoverOpen}
              content={
                qtoLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'DM Mono', monospace", fontSize: 12, padding: 2 }}>
                    <Spin size="small" /> Calculation in progress…
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 2 }}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                      Quantity takeoff finished.
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setQtoModalOpen(true);
                        setQtoResultReady(false);
                        setQtoStatusPopoverOpen(false);
                      }}
                    >
                      Show the requested result
                    </Button>
                  </div>
                )
              }
            >
              <Badge dot status={qtoLoading ? "processing" : "success"} offset={[-3, 3]} style={{ marginRight: 8 }}>
                <button className={`dt-tools-toggle${isMobile ? " compact" : ""}`}>
                  <span className="dt-tools-toggle-icon">
                    <SvgIcon d="M4 19h4v-7H4v7zM10 19h4V9h-4v10zM16 19h4V5h-4v14z" size={13} />
                  </span>
                  {!isMobile && (qtoLoading ? "Calculating…" : "QTO Ready")}
                </button>
              </Badge>
            </Popover>
          )}
          <button
            className={`dt-tools-toggle${!rightCollapsed ? " open" : ""}${isMobile ? " compact" : ""}`}
            disabled={!viewerReady}
            onClick={() => setRightCollapsed((v) => !v)}
          >
            <span className="dt-tools-toggle-icon">
              <SvgIcon d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" size={13} />
            </span>
            {!isMobile && "Tools"}
            {activeToolsCount > 0 && (
              <span className="dt-tools-badge">{activeToolsCount}</span>
            )}
          </button>
        </div>

      </Header>

      {/* ── Main row ─────────────────────────────────────────────────────── */}
      <Layout style={{ flex: 1, overflow: "hidden" }}>

        {/* ── Left panel: Sider on desktop (push-panel), Drawer overlay on mobile ──
            (400px is most of the screen on a phone — an overlay that floats over the
            viewport instead of pushing it works far better there). */}
        {isMobile ? (
          <Drawer
            placement="left"
            width="86%"
            open={!leftCollapsed}
            onClose={() => setLeftCollapsed(true)}
            closable={false}
            styles={{ body: { padding: 0, background: "#0d0f13" }, header: { display: "none" }, content: { background: "#0d0f13" } }}
          >
            {leftPanelBody}
          </Drawer>
        ) : (
          <Sider
            collapsible
            width={400}
            collapsedWidth={0}
            collapsed={leftCollapsed}
            trigger={null}
            style={{ background: "#0d0f13", borderRight: "1px solid #252a33", overflow: "hidden", flexShrink: 0 }}
          >
            {leftPanelBody}
          </Sider>
        )}

        {/* ── Viewport ─────────────────────────────────────────────────── */}
        <Content style={{ flex: 1, overflow: "hidden", minWidth: 0, position: "relative" }}>
          <div className="viewer-viewport">
            {/* Alignment mark label overlay — always mounted so ref is ready before startTracking */}
            <canvas ref={alignLabelCanvasRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 5 }} />

            {viewerReady && (
              <>
                {/* ── Hide item type status bar ─────────────────────── */}
                {activeMode === "hidetype" && (
                  <div className="dt-hidetype-bar" style={{ bottom: alignActive ? 28 : 0 }}>
                    <Text type="secondary" style={{ fontFamily: "'DM Mono',monospace", fontSize: 10 }}>
                      {isMobile ? "Tap an element to hide its item type" : "Click an element to hide its item type"}
                    </Text>
                    {hiddenTypes.length > 0 && (
                      <Button size="small" onClick={restoreLastHidden}
                        style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, height: 20, padding: "0 8px" }}>
                        Restore {prettyLabel((typeLabels.get(hiddenTypes[hiddenTypes.length - 1]) || "last").split(" — ")[0])}
                        {!isMobile && <span style={{ opacity: 0.5, marginLeft: 6 }}>Ctrl+Z</span>}
                      </Button>
                    )}
                  </div>
                )}

                {/* ── XYZ status bar ────────────────────────────────── */}
                {activeMode === "tracker" && (
                  <div style={{
                    position: "absolute", bottom: alignActive ? 28 : 0, left: 0, right: 0, height: 28,
                    background: "rgba(13,15,18,0.88)", borderTop: "1px solid rgba(255,255,255,0.07)",
                    backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
                    padding: "0 14px", zIndex: 20, pointerEvents: "none", gap: 16,
                  }}>
                    {trackerData ? (
                      <Space size={16}>
                        <Tag color="success" style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, margin: 0 }}>SNAPPED</Tag>
                        {["x", "y", "z"].map(axis => (
                          <Space size={4} key={axis}>
                            <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: "#4f9cf9", textTransform: "uppercase" }}>{axis}</Text>
                            <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>{trackerData[axis].toFixed(3)}</Text>
                          </Space>
                        ))}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontFamily: "'DM Mono',monospace", fontSize: 10 }}>
                        Hover over model to track coordinates
                      </Text>
                    )}
                  </div>
                )}

                {/* ── Alignment status bar ─────────────────────────── */}
                {alignActive && (
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0, height: 28,
                    background: "rgba(13,15,18,0.92)", borderTop: "1px solid rgba(79,156,249,0.25)",
                    backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
                    padding: "0 14px", zIndex: 20, pointerEvents: "none", gap: 12,
                  }}>
                    <Tag color="blue" style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, margin: 0 }}>
                      {activeAlignName}
                    </Tag>
                    {alignLockedStation !== null && (
                      <Tag color="gold" style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, margin: 0 }}>LOCKED</Tag>
                    )}
                    {alignHover ? (
                      <Space size={12}>
                        <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                          {formatStation(alignHover.station)}
                        </Text>
                        <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.45)" }}>
                          ⊥ {alignHover.distance.toFixed(2)} m
                        </Text>
                        {alignHover.radius != null && (
                          <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                            R {isFinite(alignHover.radius) ? `${Math.round(alignHover.radius).toLocaleString()} m` : '∞'}
                          </Text>
                        )}
                      </Space>
                    ) : (
                      <Text type="secondary" style={{ fontFamily: "'DM Mono',monospace", fontSize: 10 }}>
                        Hover to track station
                      </Text>
                    )}
                    {alignMark && (
                      <Space size={4} style={{ marginLeft: "auto" }}>
                        <Tag color="gold" style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, margin: 0 }}>MARK</Tag>
                        <Text style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontVariantNumeric: "tabular-nums", color: "#ffcc00" }}>
                          {formatStation(alignMark.station)}
                        </Text>
                      </Space>
                    )}
                  </div>
                )}

                {/* ── Alignment: Tracking panel (M26) ─────────────────────────
                    Not a Modal on purpose — tracking needs the user to hover/
                    click the viewport (and reach "Stop tracking") while this
                    stays open, so it can't block interaction like a Modal mask
                    would. A small persistent floating panel instead, same spot
                    the old Civil Tools panel used to occupy. */}
                {alignPickerOpen && (
                  <div style={{
                    position: "absolute", top: 12, left: isMobile ? 12 : undefined, right: 12, zIndex: 15, width: isMobile ? "auto" : 240,
                    background: "rgba(8,11,16,0.96)", backdropFilter: "blur(10px)",
                    border: "1px solid rgba(79,156,249,0.22)", borderRadius: 8, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "5px 6px 5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      borderBottom: "1px solid rgba(79,156,249,0.15)", background: "rgba(79,156,249,0.06)",
                    }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.72)", userSelect: "none" }}>
                        Alignment — Tracking
                      </span>
                      <Button
                        type="text" size="small"
                        icon={<SvgIcon d="M18 6L6 18M6 6l12 12" />}
                        style={{ color: "rgba(255,255,255,0.45)", width: 22, height: 22, minWidth: 0, padding: 0 }}
                        onClick={stopTracking}
                      />
                    </div>
                    <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {alignLoading ? (
                        <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div>
                      ) : alignList && alignList.length > 0 ? (
                        <Select
                          autoFocus
                          placeholder="Choose alignment…"
                          size="small"
                          style={{ width: "100%" }}
                          options={alignList.map(a => ({ value: a.name, label: a.name }))}
                          value={activeAlignName}
                          onChange={(name) => { startTracking(name); }}
                        />
                      ) : alignList !== null ? (
                        <Text style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.4)" }}>
                          No alignments found in this file.
                        </Text>
                      ) : null}
                      {alignActive && (
                        <div>
                          <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block", marginBottom: 3 }}>
                            Lock station (m)
                          </Text>
                          <InputNumber
                            size="small"
                            style={{ width: "100%" }}
                            placeholder="free tracking…"
                            value={alignLockedStation}
                            min={0}
                            precision={0}
                            onChange={(v) => {
                              const val = v ?? null;
                              setAlignLockedStation(val);
                              viewerRef.current?.setAlignmentLockedStation(val);
                            }}
                          />
                          {alignLockedStation != null && (
                            <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,200,40,0.7)", display: "block", marginTop: 2 }}>
                              {formatStation(alignLockedStation)}
                            </Text>
                          )}
                        </div>
                      )}
                      {alignActive && (
                        <Button
                          size="small" danger block
                          onClick={() => { stopTracking(); setAlignPickerOpen(false); }}
                          style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
                        >
                          Stop tracking
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Annotation: Label Elements panel (M27) ──────────────────
                    Not a Modal — same reasoning as Tracking: the user picks item
                    types, hits Apply, and the callouts sit over the live viewport,
                    so the panel must not block interaction. Closing it (✕) removes
                    any labeling that was applied — Annotate is a transient view aid. */}
                {annotatePanelOpen && (
                  <div style={{
                    position: "absolute", top: 12, left: isMobile ? 12 : undefined, right: 12, zIndex: 15, width: isMobile ? "auto" : 240,
                    background: "rgba(8,11,16,0.96)", backdropFilter: "blur(10px)",
                    border: "1px solid rgba(79,156,249,0.22)", borderRadius: 8, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "5px 6px 5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      borderBottom: "1px solid rgba(79,156,249,0.15)", background: "rgba(79,156,249,0.06)",
                    }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.72)", userSelect: "none" }}>
                        Annotation — Label Elements
                      </span>
                      <Button
                        type="text" size="small"
                        icon={<SvgIcon d="M18 6L6 18M6 6l12 12" />}
                        style={{ color: "rgba(255,255,255,0.45)", width: 22, height: 22, minWidth: 0, padding: 0 }}
                        onClick={closeAnnotatePanel}
                      />
                    </div>
                    <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {loadedItemTypeOptions.length > 0 ? (
                        <>
                          <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block" }}>
                            Item type(s) to label
                          </Text>
                          <Select
                            mode="multiple"
                            placeholder="Choose item type(s)…"
                            size="small"
                            style={{ width: "100%" }}
                            maxTagCount="responsive"
                            options={loadedItemTypeOptions.map(t => ({ value: t.key, label: t.displayName }))}
                            value={annotateSelectedTypes}
                            onChange={setAnnotateSelectedTypes}
                          />
                          {annotateHasIfc && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)" }}>
                                IFC label
                              </Text>
                              <Segmented
                                size="small"
                                options={[{ label: "Type", value: "type" }, { label: "Instance", value: "instance" }]}
                                value={annotateIfcLabel}
                                onChange={setAnnotateIfcLabel}
                                style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
                              />
                            </div>
                          )}
                          <Button
                            type="primary" size="small" block
                            loading={annotateLoading}
                            disabled={annotateSelectedTypes.length === 0}
                            onClick={() => applyAnnotate(annotateSelectedTypes, annotateIfcLabel)}
                            style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
                          >
                            Apply
                          </Button>
                          {annotateWarnings.length > 0 && (
                            <div style={{
                              padding: "6px 8px", borderRadius: 6, display: "flex", flexDirection: "column", gap: 3,
                              background: "rgba(250,173,20,0.08)", border: "1px solid rgba(250,173,20,0.3)",
                              fontFamily: "'DM Mono',monospace", fontSize: 10, color: "rgba(255,255,255,0.75)",
                            }}>
                              {annotateWarnings.map((w) => (
                                <span key={w.name}>
                                  <span style={{ color: "#faad14" }}>⚠ {w.name}</span> — {w.count.toLocaleString()} in view
                                </span>
                              ))}
                              <span style={{ color: "rgba(255,255,255,0.45)" }}>
                                Too many to label (over {ANNOTATE_LIMIT}). Zoom in closer.
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <Text style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.4)" }}>
                          No item types opened. Load item types from the left panel first.
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Annotation: Stations panel (M27) ─────────────────────────
                    Not a Modal — same reasoning as Label Elements. Ticks/labels are
                    computed every 100 m along the whole alignment but only drawn for
                    whichever fall in the current view (renderStations culls to the
                    camera frustum) — a long alignment can have hundreds of stations,
                    so this avoids drawing (and needing to declash) all of them at once.
                    Closing the panel (✕) clears the ticks — transient view aid. */}
                {stationsPanelOpen && (
                  <div style={{
                    position: "absolute", top: 12, left: isMobile ? 12 : undefined, right: 12, zIndex: 15, width: isMobile ? "auto" : 240,
                    background: "rgba(8,11,16,0.96)", backdropFilter: "blur(10px)",
                    border: "1px solid rgba(79,156,249,0.22)", borderRadius: 8, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "5px 6px 5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      borderBottom: "1px solid rgba(79,156,249,0.15)", background: "rgba(79,156,249,0.06)",
                    }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.72)", userSelect: "none" }}>
                        Annotation — Stations
                      </span>
                      <Button
                        type="text" size="small"
                        icon={<SvgIcon d="M18 6L6 18M6 6l12 12" />}
                        style={{ color: "rgba(255,255,255,0.45)", width: 22, height: 22, minWidth: 0, padding: 0 }}
                        onClick={closeStationsPanel}
                      />
                    </div>
                    <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {alignLoading ? (
                        <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div>
                      ) : alignList && alignList.length > 0 ? (
                        <>
                          <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block" }}>
                            Alignment (100 m steps)
                          </Text>
                          <Select
                            placeholder="Choose alignment…"
                            size="small"
                            style={{ width: "100%" }}
                            options={alignList.map(a => ({ value: a.name, label: a.name }))}
                            value={stationsAlignName}
                            onChange={setStationsAlignName}
                          />
                          <Button
                            type="primary" size="small" block
                            disabled={!stationsAlignName}
                            onClick={() => applyStations(stationsAlignName)}
                            style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
                          >
                            Apply
                          </Button>
                        </>
                      ) : (
                        <Text style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.4)" }}>
                          No alignments found in this file.
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Alignment: Show Alignment panel (M26 bullet 4) ───────────
                    Not a Modal — same corner-panel reasoning as the others. Draws
                    the whole selected alignment once as a static thick black
                    polyline (viewerEngine's showAlignmentPolyline, via Line2/
                    LineMaterial for genuine screen-space thickness — a plain
                    THREE.Line always renders 1px regardless of `linewidth`).
                    Distinct from Tracking's thin blue live-preview line; both
                    can be shown together. Closing the panel (✕) clears it. */}
                {showAlignPanelOpen && (
                  <div style={{
                    position: "absolute", top: 12, left: isMobile ? 12 : undefined, right: 12, zIndex: 15, width: isMobile ? "auto" : 240,
                    background: "rgba(8,11,16,0.96)", backdropFilter: "blur(10px)",
                    border: "1px solid rgba(79,156,249,0.22)", borderRadius: 8, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "5px 6px 5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      borderBottom: "1px solid rgba(79,156,249,0.15)", background: "rgba(79,156,249,0.06)",
                    }}>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.72)", userSelect: "none" }}>
                        Alignment — Show Alignment
                      </span>
                      <Button
                        type="text" size="small"
                        icon={<SvgIcon d="M18 6L6 18M6 6l12 12" />}
                        style={{ color: "rgba(255,255,255,0.45)", width: 22, height: 22, minWidth: 0, padding: 0 }}
                        onClick={closeShowAlignPanel}
                      />
                    </div>
                    <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {alignLoading ? (
                        <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div>
                      ) : alignList && alignList.length > 0 ? (
                        <>
                          <Select
                            placeholder="Choose alignment…"
                            size="small"
                            style={{ width: "100%" }}
                            options={alignList.map(a => ({ value: a.name, label: a.name }))}
                            value={showAlignName}
                            onChange={setShowAlignName}
                          />
                          <Button
                            type="primary" size="small" block
                            disabled={!showAlignName}
                            onClick={() => applyShowAlignment(showAlignName)}
                            style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
                          >
                            Apply
                          </Button>
                        </>
                      ) : (
                        <Text style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.4)" }}>
                          No alignments found in this file.
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {/* ── IoT: Simulate Sensory Data ────────────────────────────────
                    Not a Modal — same corner-panel reasoning as the others. No form
                    input needed (nothing to configure), so opening the panel starts
                    the simulated feed immediately; closing it (✕) stops the timer
                    and clears the pulses — see openIotPanel/closeIotPanel. */}
                {iotPanelOpen && (() => {
                  const turnoutsLoaded = collectLeafItemTypes(itemTree).some(
                    (t) => typeStates[t.key] === "loaded" && t.displayName === "Turnouts"
                  );
                  const openCount = iotSignals.filter((s) => s.signal === "OPEN").length;
                  return (
                    <div style={{
                      position: "absolute", top: 12, left: isMobile ? 12 : undefined, right: 12, zIndex: 15, width: isMobile ? "auto" : 320,
                      background: "rgba(8,11,16,0.96)", backdropFilter: "blur(10px)",
                      border: "1px solid rgba(79,156,249,0.22)", borderRadius: 8, overflow: "hidden",
                    }}>
                      <div style={{
                        padding: "5px 6px 5px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                        borderBottom: "1px solid rgba(79,156,249,0.15)", background: "rgba(79,156,249,0.06)",
                      }}>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.72)", userSelect: "none" }}>
                          IoT — Simulate Sensory Data
                        </span>
                        <Button
                          type="text" size="small"
                          icon={<SvgIcon d="M18 6L6 18M6 6l12 12" />}
                          style={{ color: "rgba(255,255,255,0.45)", width: 22, height: 22, minWidth: 0, padding: 0 }}
                          onClick={closeIotPanel}
                        />
                      </div>
                      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                        {!turnoutsLoaded ? (
                          <Text style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.4)" }}>
                            Load the Turnouts item type in the left panel to simulate sensor data.
                          </Text>
                        ) : (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{
                                width: 8, height: 8, borderRadius: "50%", background: "#4f9cf9", flexShrink: 0,
                                boxShadow: "0 0 6px 2px rgba(79,156,249,0.6)", animation: "dtPulseExpand 1.8s ease-out infinite",
                              }} />
                              <Text style={{ fontSize: 12, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.85)" }}>
                                {openCount} turnout{openCount !== 1 ? "s" : ""} reporting OPEN
                              </Text>
                            </div>

                            {iotSignals.length > 0 && (
                              <div style={{
                                maxHeight: 220, overflowY: "auto", border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 6, background: "rgba(255,255,255,0.02)",
                              }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Mono', monospace", fontSize: 10.5 }}>
                                  <thead>
                                    <tr style={{ position: "sticky", top: 0, background: "rgba(13,16,22,0.98)" }}>
                                      {["Turnout", "Signal", "Time"].map((h) => (
                                        <th key={h} style={{
                                          textAlign: "left", padding: "6px 8px", fontWeight: 500,
                                          textTransform: "uppercase", letterSpacing: "0.06em",
                                          color: "rgba(255,255,255,0.35)", borderBottom: "1px solid rgba(255,255,255,0.08)",
                                        }}>
                                          {h}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {iotSignals.map((s) => (
                                      <tr key={s.name}>
                                        <td style={{ padding: "5px 8px", color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>
                                          {s.name}
                                        </td>
                                        <td style={{ padding: "5px 8px", color: s.signal === "OPEN" ? "#4f9cf9" : "rgba(255,255,255,0.35)", fontWeight: s.signal === "OPEN" ? 600 : 400 }}>
                                          {s.signal}
                                        </td>
                                        <td style={{ padding: "5px 8px", color: "rgba(255,255,255,0.45)" }}>
                                          {s.time.toLocaleTimeString([], { hour12: false })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.3)" }}>
                              Simulated feed — no live sensor connected. Turnouts currently in view are re-sampled every 2s.
                            </Text>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* Section pick instruction */}
            {(sectionState === "picking1" || sectionState === "picking2") && (
              <div style={{
                position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
                background: "rgba(13,15,18,0.88)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(79,156,249,0.4)", borderRadius: 8,
                padding: "7px 20px", zIndex: 20, pointerEvents: "none",
                fontFamily: "'DM Mono', monospace", fontSize: 11,
                color: "#4f9cf9", letterSpacing: "0.08em", whiteSpace: "nowrap",
              }}>
                {sectionState === "picking1" ? "► Click first point on screen" : "► Click second point on screen"}
              </div>
            )}

            {/* At Station "Pick on screen" instruction — same click-to-project-onto-alignment
                mechanism as the Tracking tool, wired to a one-shot pick (see startStationPick) */}
            {stnPicking && (
              <div style={{
                position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
                background: "rgba(13,15,18,0.88)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(79,156,249,0.4)", borderRadius: 8,
                padding: "7px 12px 7px 20px", zIndex: 20, display: "flex", alignItems: "center", gap: 12,
                fontFamily: "'DM Mono', monospace", fontSize: 11,
                color: "#4f9cf9", letterSpacing: "0.08em", whiteSpace: "nowrap",
              }}>
                <span style={{ pointerEvents: "none" }}>► Click a point on the alignment to set the station</span>
                <Button
                  type="text" size="small"
                  icon={<SvgIcon d="M18 6L6 18M6 6l12 12" size={12} />}
                  style={{ color: "rgba(255,255,255,0.5)", width: 20, height: 20, minWidth: 0, padding: 0 }}
                  onClick={cancelStationPick}
                />
              </div>
            )}

            {/* QTO "Pick on screen" instruction — same mechanism, for whichever of Start/End
                Station is currently being picked (see startQtoStationPick) */}
            {qtoPicking && (
              <div style={{
                position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
                background: "rgba(13,15,18,0.88)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(79,156,249,0.4)", borderRadius: 8,
                padding: "7px 12px 7px 20px", zIndex: 20, display: "flex", alignItems: "center", gap: 12,
                fontFamily: "'DM Mono', monospace", fontSize: 11,
                color: "#4f9cf9", letterSpacing: "0.08em", whiteSpace: "nowrap",
              }}>
                <span style={{ pointerEvents: "none" }}>
                  ► Click a point on the alignment to set the {qtoPicking === "start" ? "Start" : "End"} Station
                </span>
                <Button
                  type="text" size="small"
                  icon={<SvgIcon d="M18 6L6 18M6 6l12 12" size={12} />}
                  style={{ color: "rgba(255,255,255,0.5)", width: 20, height: 20, minWidth: 0, padding: 0 }}
                  onClick={cancelQtoStationPick}
                />
              </div>
            )}

            {/* Three.js canvas */}
            <div ref={containerRef} className="viewer-canvas" />

            {/* Esri World Imagery attribution — required whenever the imagery is on. Needs its
                own dark backing pill, not just translucent text — satellite imagery underneath
                varies from pale desert sand to dark water, so plain light-colored text alone
                disappears against light backgrounds (same reasoning as the XYZ/alignment status
                bars' solid backdrop, just sized to fit the text instead of a full-width bar). */}
            {imageryOn && (
              <div style={{
                position: "absolute", bottom: 4, left: 8, zIndex: 5,
                background: "rgba(13,15,18,0.72)", backdropFilter: "blur(4px)",
                borderRadius: 4, padding: "2px 7px",
                fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.55)",
                pointerEvents: "none",
              }}>
                Imagery: Esri, Vantor, Earthstar Geographics, GIS User Community
              </div>
            )}

            {/* Measure result */}
            {measureResult && (
              <div className="measure-result">
                <span className="measure-label">Distance</span>
                <span className="measure-value">{measureResult.dist} {measureResult.unit}</span>
                <button className="measure-clear" onClick={() => { setMeasureResult(null); viewerRef.current?.clearMeasure(); }}>✕</button>
              </div>
            )}

            {/* Loading overlay */}
            {!viewerReady && !viewerError && (
              <div className="viewport-overlay">
                <div className="loading-ring" />
                <p>Initializing 3D engine…</p>
              </div>
            )}

            {/* Error overlay */}
            {viewerError && (
              <div className="viewport-overlay error">
                <span className="overlay-icon">⚠</span>
                <p>Failed to initialize viewer</p>
                <code>{viewerError}</code>
              </div>
            )}
          </div>
        </Content>

        {/* ── Right Tools panel: Sider on desktop (push-panel), Drawer overlay on
            mobile — same reasoning as the left panel above. */}
        {isMobile ? (
          <Drawer
            placement="right"
            width="86%"
            open={!rightCollapsed}
            onClose={() => setRightCollapsed(true)}
            closable={false}
            styles={{ body: { padding: 0, background: "#13161b" }, header: { display: "none" }, content: { background: "#13161b" } }}
          >
            {rightPanelBody}
          </Drawer>
        ) : (
          <Sider
            collapsible
            width={300}
            collapsedWidth={0}
            collapsed={rightCollapsed}
            trigger={null}
            reverseArrow
            style={{ background: "#13161b", borderLeft: "1px solid #252a33", overflow: "hidden", flexShrink: 0 }}
          >
            {rightPanelBody}
          </Sider>
        )}

      </Layout>

      {/* ── Properties Modal (M26 bullet 3 — was a right-side Drawer; the right side
          is now the Tools Sider, and a Modal matches the same "click something, get
          a results dialog" pattern already used for QTO) ──────────────────────── */}
      <Modal
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Properties
          </span>
        }
        width={isMobile ? "94vw" : 440}
        open={selectedEl !== null}
        onCancel={() => { setSelectedEl(null); setLoadingProps(false); }}
        footer={null}
        styles={{ body: { padding: 0, overflowX: "hidden", maxHeight: "70vh", overflowY: "auto" } }}
      >
        {propsContent()}
      </Modal>

      {/* ── Bridge Reports: pile setting-out schedule ───────────────────────── */}
      <PileScheduleModal
        open={pileScheduleOpen}
        onClose={() => setPileScheduleOpen(false)}
        file={file}
        isMobile={isMobile}
        alignList={alignList}
        fetchAlignments={fetchAlignments}
      />
      <BridgeQuantitiesModal
        open={bridgeBoqOpen}
        onClose={() => setBridgeBoqOpen(false)}
        file={file}
        isMobile={isMobile}
      />

      {/* ── QTO Results Modal ──────────────────────────────────────────────── */}
      <Modal
        open={qtoModalOpen}
        onCancel={() => { setQtoModalOpen(false); viewerRef.current?.clearQtoBoundary(); }}
        footer={qtoRows && qtoRows.length > 0 ? (
          <Button icon={<DownloadOutlined />} onClick={downloadQtoExcel} style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
            Download Excel
          </Button>
        ) : null}
        width={isMobile ? "96vw" : 1200}
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em", display: "flex", alignItems: "center", gap: 8 }}>
            Quantity Takeoff — {qtoAlignName} &nbsp;
            {qtoStartStn != null && qtoEndStn != null &&
              <span style={{ fontWeight: 400, color: "rgba(255,255,255,0.5)" }}>
                {formatStation(qtoStartStn)} → {formatStation(qtoEndStn)}
              </span>}
            <Popover
              trigger="click"
              placement="bottomLeft"
              title={<span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }}>How quantities are calculated</span>}
              content={
                <div style={{ maxWidth: 340, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, lineHeight: 1.7, color: "rgba(255,255,255,0.75)" }}>
                  <p style={{ margin: "0 0 10px" }}>Your boundary (alignment + station range + offsets) is projected as an infinite vertical column in 3D space.</p>
                  <p style={{ margin: "0 0 6px", fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>Step 1 — Geometry ratio</p>
                  <p style={{ margin: "0 0 10px" }}>Every triangle of each loaded element's 3D mesh is checked against the column walls. Triangles crossing the boundary are split at the exact intersection line. The result is a new mesh containing only the portion inside.</p>
                  <ul style={{ margin: "0 0 10px", paddingLeft: 18 }}>
                    <li><b>Solid elements</b> (pavement, rail structure…): ratio = inside volume / full volume</li>
                    <li><b>Surface elements</b> (slopes, earthworks…): ratio = inside area / full area</li>
                  </ul>
                  <p style={{ margin: "0 0 6px", fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>Step 2 — Apply to item type values</p>
                  <p style={{ margin: 0 }}>Each element carries an <b>item type</b> — a set of properties (volume, length, area…) that ORD computes and attaches to the element automatically. The ratio from Step 1 is multiplied by these values. For example: a pavement element whose item type reports 1 500 m³ and is 60% inside your boundary contributes 900 m³ to the result.</p>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ fontSize: 14, color: "rgba(255,255,255,0.35)", cursor: "pointer", textTransform: "none", letterSpacing: 0, fontWeight: 400 }} />
            </Popover>
          </span>
        }
      >
        {qtoRows && qtoRows.length === 0 ? (
          <Text style={{ fontFamily: "'DM Mono', monospace", color: "rgba(255,255,255,0.35)", display: "block", textAlign: "center", padding: "24px 0" }}>
            No elements found in boundary
          </Text>
        ) : (
          <Table
            size="small"
            dataSource={qtoRows || []}
            pagination={false}
            scroll={{ x: "max-content" }}
            style={{ fontFamily: "'DM Mono', monospace" }}
            columns={[
              { title: <span style={{ whiteSpace: "nowrap" }}>Item Type</span>, dataIndex: "itemType", key: "itemType", width: 160,
                render: v => <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{v}</span> },
              ...((qtoRows || []).some(r => r.name != null) ? [{
                title: <span style={{ whiteSpace: "nowrap" }}>Name</span>, dataIndex: "name", key: "name", width: 130,
                render: v => <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{v ?? "—"}</span>,
              }] : []),
              ...((qtoRows || []).some(r => r.physicalCount != null) ? [{
                title: <span style={{ whiteSpace: "nowrap" }}>Count</span>, dataIndex: "physicalCount", key: "physicalCount", width: 105,
                render: v => <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{v != null ? v.toLocaleString() : "—"}</span>,
              }] : (qtoRows || []).some(r => r.count != null && r.volume == null && r.length == null && r.planarArea == null && r.slopedArea == null) ? [{
                title: <span style={{ whiteSpace: "nowrap" }}>Count</span>, dataIndex: "count", key: "count", width: 90,
                render: v => <span style={{ fontSize: 11 }}>{v ?? "—"}</span>,
              }] : []),
              ...[ ["Volume m³","volume"], ["Length m","length"], ["Planar m²","planarArea"], ["Sloped m²","slopedArea"] ]
                .filter(([,k]) => (qtoRows || []).some(r => r[k] != null))
                .map(([title, key]) => ({
                  title: <span style={{ whiteSpace: "nowrap" }}>{title}</span>, dataIndex: key, key, width: 165,
                  render: v => <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}</span>,
                })),
            ]}
          />
        )}
      </Modal>

      {/* ── Cross Section: At Station (M26 top-bar Tools menu entry) ────────
          Same state/handler as the Civil Tools panel's "At Station" Popover —
          just a Modal instead, since it's reached from a menu, not a button
          it can anchor a Popover to. */}
      <Modal
        open={topBarStnModalOpen}
        onCancel={() => setTopBarStnModalOpen(false)}
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Cross Section — At Station
          </span>
        }
        width={isMobile ? "92vw" : 340}
        footer={
          <Button
            type="primary"
            block
            disabled={!stnAlignName || stnInput == null}
            onClick={() => { applySectionAtStation(); setTopBarStnModalOpen(false); }}
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}
          >
            Apply
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {alignLoading ? (
            <div style={{ textAlign: "center", padding: "4px 0" }}><Spin size="small" /></div>
          ) : (
            <Select
              placeholder="Choose alignment…"
              style={{ width: "100%" }}
              options={(alignList || []).map(a => ({ value: a.name, label: a.name }))}
              value={stnAlignName}
              onChange={setStnAlignName}
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.45)", flexShrink: 0, minWidth: 72 }}>Station (m)</span>
            <InputNumber
              min={0}
              style={{ flex: 1 }}
              placeholder="e.g. 20001"
              value={stnInput}
              onChange={setStnInput}
              onPressEnter={() => { applySectionAtStation(); setTopBarStnModalOpen(false); }}
            />
            <Tooltip title="Pick on screen">
              <Button
                icon={<AimOutlined />}
                disabled={!stnAlignName}
                onClick={startStationPick}
                style={{ width: 32, height: 32, minWidth: 0, padding: 0, flexShrink: 0 }}
              />
            </Tooltip>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.45)", flexShrink: 0, minWidth: 72 }}>Angle (°)</span>
            <InputNumber
              min={0}
              max={179}
              style={{ flex: 1 }}
              value={stnAngle}
              onChange={(v) => setStnAngle(v ?? 0)}
            />
          </div>
        </div>
      </Modal>

      {/* ── Quantities: Quantity Takeoff from Boundary (M26 top-bar Tools menu
          entry) — same state/handler as the old Civil Tools panel's Quantities
          Popover; reuses qtoOpen directly as this Modal's open state. Compute
          closes this form modal and opens the results modal (qtoModalOpen). */}
      <Modal
        open={qtoOpen}
        onCancel={() => { setQtoOpen(false); viewerRef.current?.clearQtoBoundary(); }}
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            Quantities — Quantity Takeoff from Boundary
          </span>
        }
        width={isMobile ? "92vw" : 360}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              style={{ flex: 1, fontFamily: "'DM Mono', monospace", fontSize: 12 }}
              disabled={!qtoAlignName || qtoStartStn == null || qtoEndStn == null || qtoStartStn >= qtoEndStn}
              onClick={previewQtoBoundary}
            >
              Draw Boundary
            </Button>
            <Button
              type="primary"
              style={{ flex: 1, fontFamily: "'DM Mono', monospace", fontSize: 12 }}
              disabled={qtoLoading || !qtoAlignName || qtoStartStn == null || qtoEndStn == null || qtoStartStn >= qtoEndStn}
              onClick={() => { setQtoOpen(false); computeQto(); }}
            >
              Compute
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {alignLoading ? (
            <div style={{ textAlign: "center" }}><Spin size="small" /></div>
          ) : (
            <Select
              placeholder="Choose alignment…"
              style={{ width: "100%" }}
              options={(alignList || []).map(a => ({ value: a.name, label: a.name }))}
              value={qtoAlignName}
              onChange={v => { setQtoAlignName(v); setQtoRows(null); viewerRef.current?.clearQtoBoundary(); }}
            />
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              ["Start Stn (m)", qtoStartStn, setQtoStartStn, "start"],
              ["End Stn (m)",   qtoEndStn,   setQtoEndStn,   "end"],
              ["Left Offset",   qtoLeftOff,  setQtoLeftOff,  null],
              ["Right Offset",  qtoRightOff, setQtoRightOff, null],
            ].map(([label, val, setter, pickKey]) => (
              <div key={label}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>{label}</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <InputNumber
                    style={{ flex: 1, minWidth: 0 }}
                    min={0}
                    placeholder="—"
                    value={val}
                    onChange={v => { setter(v); setQtoRows(null); viewerRef.current?.clearQtoBoundary(); }}
                  />
                  {pickKey && (
                    <Tooltip title="Pick on screen">
                      <Button
                        icon={<AimOutlined />}
                        disabled={!qtoAlignName}
                        onClick={() => startQtoStationPick(pickKey)}
                        style={{ width: 32, height: 32, minWidth: 0, padding: 0, flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
          {qtoAlignName && qtoStartStn != null && qtoEndStn != null && (
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
              {formatStation(qtoStartStn)} → {formatStation(qtoEndStn)}
            </span>
          )}
        </div>
      </Modal>

      {/* ── Cross-section Drawer (bottom) ─────────────────────────────────── */}
      <Drawer
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            Cross Section
          </span>
        }
        extra={
          <Tooltip title={sectionControlsCollapsed ? "Show panel" : "Hide panel"}>
            <Button
              type="text"
              size="small"
              icon={sectionControlsCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSectionControlsCollapsed((v) => !v)}
            />
          </Tooltip>
        }
        /* CSS min(), not an isMobile ternary — isMobile is a *width* check (innerWidth < 768),
           which flips to false in landscape on a phone (innerWidth becomes ~850+, the portrait
           height turned sideways) and would silently fall back to the fixed 416px, which is
           taller than an entire landscape viewport. min() reacts to the actual live viewport
           height regardless of orientation or width, with no JS/resize-tracking needed — stays
           416px normally, only caps down when the viewport itself is short. */
        placement="bottom"
        height="min(416px, 72vh)"
        mask={false}
        open={sectionOpen}
        onClose={clearSection}
        // The reliable trigger for the section view's *first* build — fires exactly when the
        // open transition has actually finished, not a guessed delay. Same reasoning as the
        // Profile View Drawer below, which already uses this. A fixed setTimeout(320) used to
        // gate this instead; on a slower machine (weaker GPU, background AV/EDR, RDP session)
        // the Drawer could still be mid-transition at 320ms, so canvas.getBoundingClientRect()
        // read a stale/zero size and startSectionView() silently fell back to a hardcoded
        // 400×200 canvas size for its camera frustum — permanently wrong for that session, since
        // nothing ever re-measures unless the canvas's own CSS size later changes (it doesn't;
        // antd's Drawer slides via transform, not a width/height transition, so the canvas was
        // already at its final layout size the whole time — only the *rect read* was too early).
        // Reported as "clicking a snap point always shows 0,0" on one specific machine while
        // working everywhere else, including the main viewport's own Measure tool on that same
        // machine — consistent with this race being specific to the Drawer-gated section canvas.
        afterOpenChange={(open) => {
          if (!open) return;
          sectionDrawerReadyRef.current = true;
          runStartSectionView();
        }}
        styles={{ body: { padding: "12px 16px", display: "flex", gap: 16, overflow: "hidden" } }}
      >
        {/* Live section view: elevation strip (left) + canvas column + offset strip (bottom) */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "row" }}>
          {/* Elevation axis strip — dedicated black strip, only for At Station sections */}
          {sectionFromStation && (
            <div style={{ width: 22, flexShrink: 0, background: "#13161b", borderRadius: "6px 0 0 6px", border: "1px solid rgba(255,255,255,0.07)", borderRight: "none", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(220,220,220,0.8)", letterSpacing: "0.08em", userSelect: "none", transform: "rotate(-90deg)", whiteSpace: "nowrap" }}>
                ELEVATION (m)
              </span>
            </div>
          )}
          {/* Canvas + offset strip column */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {/* WebGL canvas area */}
            <div style={{ flex: 1, background: "#13161b", overflow: "hidden", position: "relative",
              borderRadius: sectionFromStation ? "0 6px 0 0" : 6,
              border: "1px solid rgba(255,255,255,0.07)",
              borderLeft: sectionFromStation ? "none" : undefined,
              borderBottom: sectionFromStation ? "none" : undefined,
            }}>
              <canvas
                ref={sectionCanvasRef}
                style={{ width: "100%", height: "100%", display: "block" }}
              />
              {/* 2D label + snap overlay */}
              <canvas
                ref={gridLabelCanvasRef}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}
              />
              {/* Section toolbar */}
              <div style={{ position: "absolute", top: 8, right: 8, display: "flex", flexDirection: "column", gap: 4, zIndex: 10 }}>
                <Tooltip title="Fit view" placement="left">
                  <Button size="small" icon={<FullscreenOutlined />}
                    style={{ width: 28, height: 28, padding: 0, minWidth: 0 }}
                    onClick={() => viewerRef.current?.fitSectionView()} />
                </Tooltip>
                <Tooltip title="Pan" placement="left">
                  <Button size="small" type={sectionPanActive ? "primary" : "default"} icon={<DragOutlined />}
                    style={{ width: 28, height: 28, padding: 0, minWidth: 0 }}
                    onClick={() => { const v = !sectionPanActive; setSectionPanActive(v); sectionPanRef.current = v; }} />
                </Tooltip>
                <Tooltip title="Measure H + V" placement="left">
                  <Button size="small" type={sectionMeasureActive ? "primary" : "default"} icon={<ColumnWidthOutlined />}
                    style={{ width: 28, height: 28, padding: 0, minWidth: 0 }}
                    onClick={() => {
                      const v = !sectionMeasureActive;
                      setSectionMeasureActive(v);
                      sectionMeasureRef.current = v;
                      if (v) { setSectionPanActive(false); sectionPanRef.current = false; }
                      else { viewerRef.current?.clearSectionMeasure(); }
                    }} />
                </Tooltip>
              </div>
              {/* Station label — top-center, At Station sections only */}
              {sectionFromStation && stnInput !== null && stnAlignName && (
                <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.72)", color: "rgba(200,200,200,0.9)", fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "3px 10px", borderRadius: 4, pointerEvents: "none", whiteSpace: "nowrap" }}>
                  {stnAlignName} &nbsp;·&nbsp; {formatStation(stnInput)}
                </div>
              )}
              {/* Coordinate badge */}
              {sectionCoord !== null && (
                <div style={{ position: "absolute", top: sectionFromStation ? 36 : 8, left: 8, background: "rgba(0,0,0,0.72)", color: sectionCoord.snapped ? "rgba(255,160,40,0.95)" : "#fff", fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "4px 10px", borderRadius: 4, pointerEvents: "none", letterSpacing: "0.05em", display: "flex", gap: 10, alignItems: "center" }}>
                  {sectionCoord.offset != null && (
                    <span>Off {sectionCoord.offset >= 0 ? "+" : ""}{sectionCoord.offset.toFixed(2)} m</span>
                  )}
                  <span>Z {sectionCoord.z.toFixed(3)} m</span>
                  {sectionCoord.snapped && <span style={{ fontSize: 9, opacity: 0.8 }}>⦿ SNAP</span>}
                </div>
              )}
              <div style={{ position: "absolute", bottom: 4, right: 8, fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", pointerEvents: "none" }}>
                scroll · zoom &nbsp;|&nbsp; click · snap
              </div>
            </div>
            {/* Offset axis strip — dedicated black strip below canvas, At Station only */}
            {sectionFromStation && (
              <div style={{ height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#13161b", borderRadius: "0 0 6px 0", border: "1px solid rgba(255,255,255,0.07)", borderTop: "none", borderLeft: "none", pointerEvents: "none" }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(220,220,220,0.8)", letterSpacing: "0.08em", userSelect: "none" }}>
                  OFFSET (m)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Controls — collapsible (extra button above) so the canvas can reclaim the 180px on a
            narrow phone; the ResizeObserver on sectionCanvasRef already re-fits the WebGL view
            whenever the canvas itself resizes, so hiding/showing this needs no extra wiring. */}
        {!sectionControlsCollapsed && (
        <div style={{ width: 180, display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
          <Tag color="processing" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.08em", alignSelf: "flex-start" }}>
            SECTION ACTIVE
          </Tag>
          <Text style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>
            Navigate freely in 3D. The clipping plane stays in place.
          </Text>

          {/* Station navigation — only for At Station sections */}
          {sectionFromStation && stnInput !== null && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ height: 1, background: "rgba(255,255,255,0.07)" }} />
              <Text style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
                Step (m)
              </Text>
              <InputNumber
                min={0.1} step={1} value={sectionInterval}
                onChange={v => setSectionInterval(v ?? 10)}
                size="small" style={{ width: "100%", fontFamily: "'DM Mono', monospace", fontSize: 11 }}
              />
              <div style={{ display: "flex", gap: 4 }}>
                <Button
                  block size="small"
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, flex: 1 }}
                  onClick={() => applyAtStation(prevStepStation(stnInput || 0, sectionInterval))}
                >
                  ◀ Prev
                </Button>
                <Button
                  block size="small"
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, flex: 1 }}
                  onClick={() => applyAtStation(nextStepStation(stnInput || 0, sectionInterval))}
                >
                  Next ▶
                </Button>
              </div>
            </div>
          )}

          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            <Button block size="small" onClick={flipSection} style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
              Flip side
            </Button>
            <Button block size="small" onClick={() => { clearSection(); activateMode("section"); setSectionState("picking1"); }} style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
              Redefine
            </Button>
            <Button block size="small" type="primary" danger onClick={clearSection} style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
              Clear
            </Button>
          </div>
        </div>
        )}
      </Drawer>

      {/* ── Profile View Drawer (bottom) ──────────────────────────────────── */}
      <Drawer
        title={
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>
            Profile View
          </span>
        }
        extra={
          <Tooltip title={profileControlsCollapsed ? "Show panel" : "Hide panel"}>
            <Button
              type="text"
              size="small"
              icon={profileControlsCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setProfileControlsCollapsed((v) => !v)}
            />
          </Tooltip>
        }
        placement="bottom"
        height="min(624px, 82vh)"
        mask={false}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        afterOpenChange={(open) => {
          if (!open || !profileCanvasRef.current || profileSelectedVAs.length === 0) return;
          const vaList = profileSelectedVAs.map(name => {
            const va = profileVerts.find(v => v.name === name);
            return va ? { name: va.name, path: va.path } : null;
          }).filter(Boolean);
          if (vaList.length === 0) return;
          profilePathRef.current = vaList;
          if (!profileInitRef.current) {
            const b = getCombinedBounds(vaList);
            const ML=65, MR=20, MT=14, MB=34;
            const DW = profileCanvasRef.current.clientWidth - ML - MR;
            const DH = profileCanvasRef.current.clientHeight - MT - MB;
            if (DW > 0 && DH > 0) {
              const fitVE = Math.max(1, Math.round(DH * b.stnRange / (DW * b.elevRange * 1.2)));
              profileVScaleRef.current = fitVE;
              setProfileVScale(fitVE);
              profileViewRef.current = { zoom: 1, cx_stn: (b.minStn+b.maxStn)/2, cy_elev: (b.minElev+b.maxElev)/2 };
              profileInitRef.current = true;
              drawProfileCanvas(profileCanvasRef.current, vaList, fitVE, profileViewRef.current, profileShowAnnotRef.current);
              return;
            }
          }
          drawProfileCanvas(profileCanvasRef.current, vaList, profileVScaleRef.current, profileViewRef.current, profileShowAnnotRef.current);
        }}
        styles={{ body: { padding: "12px 16px", display: "flex", gap: 16, overflow: "hidden" } }}
      >
        {/* Left: ELEVATION strip + canvas column + STATION strip */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "row" }}>
          {/* Elevation label strip */}
          <div style={{ width: 22, flexShrink: 0, background: "#13161b", borderRadius: "6px 0 0 6px", border: "1px solid rgba(255,255,255,0.07)", borderRight: "none", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(220,220,220,0.8)", letterSpacing: "0.08em", userSelect: "none", transform: "rotate(-90deg)", whiteSpace: "nowrap" }}>
              ELEVATION (m)
            </span>
          </div>
          {/* Canvas + station strip column */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, background: "#13161b", overflow: "hidden", position: "relative", borderRadius: "0 6px 0 0", border: "1px solid rgba(255,255,255,0.07)", borderLeft: "none", borderBottom: "none" }}>
              <canvas
                ref={profileCanvasRef}
                style={{ width: "100%", height: "100%", display: "block", touchAction: "none" }}
              />
              <canvas
                ref={profileTrackerCanvasRef}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
              />
              {profileSelectedVAs.length === 0 && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)", fontFamily: "'DM Mono', monospace", fontSize: 11, pointerEvents: "none" }}>
                  Select an alignment →
                </div>
              )}
            </div>
            {/* Station axis strip */}
            <div style={{ height: 18, flexShrink: 0, background: "#13161b", borderRadius: "0 0 6px 0", border: "1px solid rgba(255,255,255,0.07)", borderLeft: "none", borderTop: "none", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "rgba(220,220,220,0.8)", letterSpacing: "0.08em", userSelect: "none" }}>
                STATION (m)
              </span>
            </div>
          </div>
        </div>

        {/* Right: controls column — collapsible (extra button above), same reasoning as the
            Cross Section Drawer's controls panel; the ResizeObserver on profileCanvasRef
            already redraws at the new size once this reclaims/gives back its 180px. */}
        {!profileControlsCollapsed && (
        <div style={{ width: 180, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block", marginBottom: 3 }}>Alignment</Text>
            <Select
              size="small"
              style={{ width: "100%" }}
              placeholder={alignLoading ? "Loading…" : "Choose alignment…"}
              loading={alignLoading}
              options={(alignList || []).map(a => ({ value: a.name, label: a.name }))}
              value={profileAlignName}
              onChange={(name) => {
                setProfileAlignName(name);
                fetchVerticals(name);
              }}
            />
          </div>
          {profileVerts.length > 0 && (
            <div>
              <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block", marginBottom: 3 }}>Vertical alignments</Text>
              <div style={{ maxHeight: 110, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, padding: "4px 6px", background: "rgba(255,255,255,0.04)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.08)" }}>
                {profileVerts.map((va, i) => (
                  <Checkbox
                    key={va.name}
                    checked={profileSelectedVAs.includes(va.name)}
                    onChange={(e) => {
                      const prev = profileSelectedVAs;
                      const next = e.target.checked
                        ? [...prev, va.name]
                        : prev.filter(n => n !== va.name);
                      if (prev.length === 0 && next.length > 0) profileInitRef.current = false;
                      setProfileSelectedVAs(next);
                    }}
                  >
                    <span style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.7)" }}>
                      {va.name}
                    </span>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}
          {profileLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Spin size="small" />
              <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.35)" }}>Loading…</Text>
            </div>
          )}
          <div>
            <Text style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.45)", display: "block", marginBottom: 3 }}>Vertical exaggeration</Text>
            <InputNumber
              size="small"
              style={{ width: "100%" }}
              min={1}
              max={100000}
              step={1}
              precision={0}
              value={profileVScale}
              placeholder="auto"
              onChange={v => {
                const val = v ?? 1;
                profileVScaleRef.current = val;
                setProfileVScale(val);
              }}
            />
            <Text style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.25)", display: "block", marginTop: 2 }}>
              VE 1 = 1:1 · scroll to zoom · drag to pan
            </Text>
          </div>
          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            <Button
              block size="small"
              icon={<FullscreenOutlined />}
              style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
              onClick={fitProfile}
            >
              Fit view
            </Button>
            <Button
              block size="small"
              type={profileTrackActive ? "primary" : "default"}
              style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
              onClick={() => {
                const next = !profileTrackActive;
                setProfileTrackActive(next);
                if (!next) {
                  const tc = profileTrackerCanvasRef.current;
                  if (tc) { const ctx2 = tc.getContext("2d"); ctx2.clearRect(0, 0, tc.width, tc.height); }
                }
              }}
            >
              {profileTrackActive ? "Tracking on" : "Tracker"}
            </Button>
            <Button
              block size="small"
              type={profileShowAnnotations ? "primary" : "default"}
              style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}
              onClick={() => {
                const next = !profileShowAnnotations;
                setProfileShowAnnotations(next);
                const c = profileCanvasRef.current;
                const vl = profilePathRef.current;
                if (c && vl && vl.length > 0) drawProfileCanvas(c, vl, profileVScaleRef.current, profileViewRef.current, next);
              }}
            >
              {profileShowAnnotations ? "Annot. on" : "Annot. off"}
            </Button>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {Object.entries(PROF_CLR).map(([type, color]) => (
                <span key={type} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <span style={{ width: 14, height: 2, background: color, display: "inline-block", borderRadius: 1 }} />
                  <Text style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: "rgba(255,255,255,0.35)" }}>
                    {type === 'lineSegment' ? 'Line' : type === 'arc' ? 'Arc' : type === 'bcurve' ? 'Parabola' : 'String'}
                  </Text>
                </span>
              ))}
            </div>
          </div>
        </div>
        )}
      </Drawer>

    </Layout>
  );
}

// ── QTO boundary polygon ──────────────────────────────────────────────────────
// Builds a closed polygon in project XY coords around the alignment corridor.
// Returns [[px, py], ...] (not closed — caller closes if needed).
function buildBoundaryPolygon(tessellated, startStn, endStn, leftOff, rightOff) {
  const { cumulLengths } = tessellated;
  const n = cumulLengths.length;
  const leftPts = [], rightPts = [];

  // Tangent direction comes from getExactTangentAtStation — the analytic per-primitive
  // direction (line/arc/spiral), not a finite-difference between sampled tessellation points.
  // That distinction matters specifically through spirals (continuously-changing curvature —
  // a secant between two nearby samples isn't the true tangent there) and gets amplified by
  // the offset distance, which is why this was previously producing a boundary polygon that
  // measurably undercounted volumes against ORD's own named-boundary QTO on this alignment.
  const pushAt = (s) => {
    const pos = getPositionAtStation(tessellated, s);
    if (!pos) return;
    const tan = getExactTangentAtStation(tessellated, s) || pos;
    leftPts.push( [pos.x + leftOff  * (-tan.ty), pos.y + leftOff  * tan.tx]);
    rightPts.push([pos.x - rightOff * (-tan.ty), pos.y - rightOff * tan.tx]);
  };

  pushAt(startStn);
  for (let i = 0; i < n; i++) {
    const s = cumulLengths[i];
    if (s <= startStn || s >= endStn) continue;
    pushAt(s);
  }
  pushAt(endStn);

  return [...leftPts, ...rightPts.reverse()];
}

// Douglas-Peucker line simplification, used only on the polygon sent to the QTO compute
// endpoint (not on anything drawn — the viewport boundary line stays at full density).
// tessellateAlignment samples arcs at 1°/step, tuned for smooth on-screen drawing; a boundary
// with several curved sections can inherit hundreds of points from that. qto.js's curve-length
// fallback (linear/virtual item types with no mesh) does a hand-rolled point-in-polygon /
// segment-clip test against every boundary edge, with no spatial indexing, so QTO compute time
// scales with boundary point count — arc-heavy boundaries were making it noticeably slow.
// toleranceM is the max perpendicular deviation allowed from the simplified line; small relative
// to corridor scale, so this doesn't meaningfully change QTO results.
function simplifyBoundaryPolygon(points, toleranceM = 0.1) {
  if (points.length <= 3) return points;

  const perpendicularDist = (p, a, b) => {
    const [px, py] = p, [ax, ay] = a, [bx, by] = b;
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(px - ax, py - ay);
    const t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
    const cx = ax + t * dx, cy = ay + t * dy;
    return Math.hypot(px - cx, py - cy);
  };

  const dp = (pts) => {
    if (pts.length <= 2) return pts;
    let maxDist = 0, maxIdx = 0;
    const first = pts[0], last = pts[pts.length - 1];
    for (let i = 1; i < pts.length - 1; i++) {
      const d = perpendicularDist(pts[i], first, last);
      if (d > maxDist) { maxDist = d; maxIdx = i; }
    }
    if (maxDist > toleranceM) {
      const left  = dp(pts.slice(0, maxIdx + 1));
      const right = dp(pts.slice(maxIdx));
      return left.slice(0, -1).concat(right);
    }
    return [first, last];
  };

  return dp(points);
}

// ── Profile canvas drawing ────────────────────────────────────────────────────

const VA_COLORS = ['#4f9cf9', '#4ade80', '#fb923c', '#e879f9', '#fde047', '#38bdf8'];
const PROF_CLR  = { lineSegment: '#4f9cf9', arc: '#f97316', bcurve: '#4ade80', lineString: '#22d3ee' };

function niceStep(raw) {
  if (!raw || raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  if (n < 1.5) return mag;
  if (n < 3.5) return 2 * mag;
  if (n < 7.5) return 5 * mag;
  return 10 * mag;
}

// Compute sample-point bounds for a single VA path (station × elevation)
function getBoundsFromPath(path) {
  const pts = [];
  for (const seg of path) {
    if (seg.lineSegment) {
      pts.push(seg.lineSegment[0], seg.lineSegment[1]);
    } else if (seg.lineString) {
      seg.lineString.forEach(p => pts.push(p));
    } else if (seg.arc) {
      const { center, vectorX, vectorY, sweepStartEnd } = seg.arc;
      const R  = Math.hypot(vectorX[0], vectorX[1]);
      const ux = vectorX[0]/R, uy = vectorX[1]/R;
      const wx = vectorY[0]/R, wy = vectorY[1]/R;
      for (let i = 0; i <= 20; i++) {
        const deg = sweepStartEnd[0] + (i/20)*(sweepStartEnd[1]-sweepStartEnd[0]);
        const rad = deg * Math.PI / 180;
        pts.push([
          center[0] + R*(ux*Math.cos(rad) + wx*Math.sin(rad)),
          center[1] + R*(uy*Math.cos(rad) + wy*Math.sin(rad)),
          0,
        ]);
      }
    } else if (seg.bcurve) {
      const [p0, p1, p2] = seg.bcurve.points;
      for (let i = 0; i <= 20; i++) {
        const t = i/20;
        pts.push([
          (1-t)*(1-t)*p0[0] + 2*t*(1-t)*p1[0] + t*t*p2[0],
          (1-t)*(1-t)*p0[1] + 2*t*(1-t)*p1[1] + t*t*p2[1],
          0,
        ]);
      }
    }
  }
  if (pts.length === 0) return { minStn:0, maxStn:1, minElev:0, maxElev:1, stnRange:1, elevRange:1 };
  const minStn  = Math.min(...pts.map(p => p[0]));
  const maxStn  = Math.max(...pts.map(p => p[0]));
  const minElev = Math.min(...pts.map(p => p[1]));
  const maxElev = Math.max(...pts.map(p => p[1]));
  return { minStn, maxStn, minElev, maxElev, stnRange: maxStn-minStn||1, elevRange: maxElev-minElev||1 };
}

// Combine bounds across all selected VAs
function getCombinedBounds(vaList) {
  if (!vaList || vaList.length === 0) {
    return { minStn:0, maxStn:1, minElev:0, maxElev:1, stnRange:1, elevRange:1 };
  }
  let minStn = Infinity, maxStn = -Infinity, minElev = Infinity, maxElev = -Infinity;
  for (const va of vaList) {
    const b = getBoundsFromPath(va.path || []);
    minStn  = Math.min(minStn,  b.minStn);
    maxStn  = Math.max(maxStn,  b.maxStn);
    minElev = Math.min(minElev, b.minElev);
    maxElev = Math.max(maxElev, b.maxElev);
  }
  if (!isFinite(minStn)) return { minStn:0, maxStn:1, minElev:0, maxElev:1, stnRange:1, elevRange:1 };
  return { minStn, maxStn, minElev, maxElev, stnRange: maxStn-minStn||1, elevRange: maxElev-minElev||1 };
}

// Compute the PI (tangent-line intersection) for a vertical circular arc
function arcTangentPI(center, R, ux, uy, wx, wy, a0, a1) {
  const sx = center[0] + R*(ux*Math.cos(a0) + wx*Math.sin(a0));
  const sy = center[1] + R*(uy*Math.cos(a0) + wy*Math.sin(a0));
  const sign = Math.sign(a1 - a0) || 1;
  const tx0 =  sign*(-ux*Math.sin(a0) + wx*Math.cos(a0));
  const ty0 =  sign*(-uy*Math.sin(a0) + wy*Math.cos(a0));
  const ex = center[0] + R*(ux*Math.cos(a1) + wx*Math.sin(a1));
  const ey = center[1] + R*(uy*Math.cos(a1) + wy*Math.sin(a1));
  const tx1 =  sign*(-ux*Math.sin(a1) + wx*Math.cos(a1));
  const ty1 =  sign*(-uy*Math.sin(a1) + wy*Math.cos(a1));
  const det = tx0*(-ty1) - (-tx1)*ty0;
  if (Math.abs(det) < 1e-9) return null;
  const dx = ex - sx, dy = ey - sy;
  const t = (dx*(-ty1) - (-tx1)*dy) / det;
  return { stn: sx + t*tx0, elev: sy + t*ty0 };
}

// Draw a PI marker + multi-line annotation box.
// lines[] drawn top-to-bottom, 13 px line spacing, with extra vertical gap between entries.
function drawPIAnnotation(ctx, px, py, cW, cH, color, lines) {
  if (!lines || lines.length === 0) return;
  if (px < -150 || px > cW + 150 || py < -150 || py > cH + 150) return;
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(px, py, 2.5, 0, Math.PI*2); ctx.fill();
  ctx.strokeStyle = color;
  ctx.setLineDash([2, 3]);
  ctx.lineWidth = 0.8;
  ctx.globalAlpha = 0.5;
  ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py - 20); ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
  const LS = 13;
  ctx.font = '8px "DM Mono",monospace';
  ctx.textAlign = "center";
  const bw = Math.max(...lines.map(l => ctx.measureText(l).width)) + 10;
  const boxH = lines.length * LS + 6;
  const boxY = py - 22 - boxH;
  ctx.fillStyle = "rgba(8,11,16,0.85)";
  ctx.fillRect(px - bw/2, boxY, bw, boxH);
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], px, boxY + 3 + i * LS);
  }
  ctx.restore();
}

// Compute elevation for a given station by interpolating along a VA path.
function getElevationAtStation(path, station) {
  if (!path) return null;
  for (const seg of path) {
    if (seg.lineSegment) {
      const [[s1, e1], [s2, e2]] = seg.lineSegment;
      const lo = Math.min(s1, s2), hi = Math.max(s1, s2);
      if (station < lo - 0.1 || station > hi + 0.1) continue;
      const t = Math.abs(s2 - s1) < 1e-9 ? 0.5 : (station - s1) / (s2 - s1);
      return e1 + Math.max(0, Math.min(1, t)) * (e2 - e1);
    } else if (seg.lineString) {
      const pts = seg.lineString;
      for (let i = 0; i < pts.length - 1; i++) {
        const s1 = pts[i][0], e1 = pts[i][1];
        const s2 = pts[i+1][0], e2 = pts[i+1][1];
        const lo = Math.min(s1, s2), hi = Math.max(s1, s2);
        if (station < lo - 0.1 || station > hi + 0.1) continue;
        const t = Math.abs(s2 - s1) < 1e-9 ? 0.5 : (station - s1) / (s2 - s1);
        return e1 + Math.max(0, Math.min(1, t)) * (e2 - e1);
      }
    } else if (seg.arc) {
      const { center, vectorX, vectorY, sweepStartEnd } = seg.arc;
      const R  = Math.hypot(vectorX[0], vectorX[1]);
      const ux = vectorX[0]/R, uy = vectorX[1]/R;
      const wx = vectorY[0]/R, wy = vectorY[1]/R;
      const a0 = sweepStartEnd[0] * Math.PI / 180;
      const a1 = sweepStartEnd[1] * Math.PI / 180;
      const sSt  = center[0] + R*(ux*Math.cos(a0) + wx*Math.sin(a0));
      const sEnd = center[0] + R*(ux*Math.cos(a1) + wx*Math.sin(a1));
      if (station < Math.min(sSt, sEnd) - 0.1 || station > Math.max(sSt, sEnd) + 0.1) continue;
      // Closed-form: ux²+wx²=1 (row of orthogonal matrix) → cos(θ−φ) = (stn−center[0])/R
      const phi = Math.atan2(wx, ux);
      const cosV = (station - center[0]) / R;
      if (Math.abs(cosV) > 1.0001) continue;
      const dT = Math.acos(Math.max(-1, Math.min(1, cosV)));
      const loA = Math.min(a0, a1), hiA = Math.max(a0, a1);
      let theta = null;
      outer: for (const cand of [phi + dT, phi - dT]) {
        for (const n of [0, 1, -1, 2, -2]) {
          const tt = cand + n * 2 * Math.PI;
          if (tt >= loA - 0.001 && tt <= hiA + 0.001) { theta = tt; break outer; }
        }
      }
      if (theta === null) continue;
      return center[1] + R*(uy*Math.cos(theta) + wy*Math.sin(theta));
    } else if (seg.bcurve) {
      const [p0, p1, p2] = seg.bcurve.points;
      const lo = Math.min(p0[0], p2[0]), hi = Math.max(p0[0], p2[0]);
      if (station < lo - 0.1 || station > hi + 0.1) continue;
      const s0 = p0[0], s1 = p1[0], s2 = p2[0];
      const a = s0 - 2*s1 + s2, b = 2*(s1 - s0), c = s0 - station;
      let t;
      if (Math.abs(a) < 1e-9) {
        t = Math.abs(b) < 1e-9 ? 0.5 : -c / b;
      } else {
        const disc = b*b - 4*a*c;
        if (disc < 0) continue;
        const sq = Math.sqrt(disc);
        const t1 = (-b + sq) / (2*a), t2 = (-b - sq) / (2*a);
        t = (t1 >= -0.001 && t1 <= 1.001) ? t1 : t2;
      }
      t = Math.max(0, Math.min(1, t));
      const [e0, e1, e2] = [p0[1], p1[1], p2[1]];
      return (1-t)*(1-t)*e0 + 2*t*(1-t)*e1 + t*t*e2;
    }
  }
  return null;
}

// Draw the elevation tracker overlay on a transparent canvas positioned over the profile canvas.
function drawProfileTracker(trackerCanvas, mx, vaList, vScale, viewState) {
  if (!trackerCanvas) return;
  const W = trackerCanvas.clientWidth || trackerCanvas.width;
  const H = trackerCanvas.clientHeight || trackerCanvas.height;
  if (!W || !H) return;
  const dpr = window.devicePixelRatio || 1;
  const bw  = Math.round(W * dpr), bh = Math.round(H * dpr);
  if (trackerCanvas.width  !== bw) trackerCanvas.width  = bw;
  if (trackerCanvas.height !== bh) trackerCanvas.height = bh;
  const ctx = trackerCanvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  if (!vaList || vaList.length === 0 || !viewState) return;
  const ML=65, MR=20, MT=14, MB=34;
  const DW = W - ML - MR;
  const DH = H - MT - MB;
  if (DW <= 0 || DH <= 0 || mx < ML || mx > ML + DW) return;
  const b   = getCombinedBounds(vaList);
  const ve  = vScale || 1;
  const pps = DW / b.stnRange * viewState.zoom;
  const ppe = ve * pps;
  const cxPx = ML + DW / 2;
  const cyPx = MT + DH / 2;
  const toY  = e => cyPx - (e - viewState.cy_elev) * ppe;
  const station = viewState.cx_stn + (mx - cxPx) / pps;
  // Vertical dashed line (clipped)
  ctx.save();
  ctx.beginPath(); ctx.rect(ML, MT, DW, DH); ctx.clip();
  ctx.setLineDash([4, 3]);
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(mx, MT); ctx.lineTo(mx, MT+DH); ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
  // Station label below axis
  ctx.font = '9px "DM Mono",monospace';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(formatStation(station), mx, MT+DH+4);
  // Elevation markers per VA
  const marks = [];
  for (let vi = 0; vi < vaList.length; vi++) {
    const elev = getElevationAtStation(vaList[vi].path, station);
    if (elev === null) continue;
    const y = toY(elev);
    marks.push({ vi, y, elev, name: vaList[vi].name, inRange: y >= MT - 2 && y <= MT + DH + 2 });
  }
  // Dots on the vertical line (clipped)
  ctx.save();
  ctx.beginPath(); ctx.rect(ML, MT, DW, DH); ctx.clip();
  for (const { vi, y, inRange } of marks) {
    if (!inRange) continue;
    const color = VA_COLORS[vi % VA_COLORS.length];
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(mx, y, 3.5, 0, Math.PI*2); ctx.fill();
    ctx.strokeStyle = 'rgba(8,11,16,0.9)';
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
  ctx.restore();
  // Elevation labels (right of line, unconstrained by clip)
  for (const { vi, y, elev, name } of marks) {
    const color = VA_COLORS[vi % VA_COLORS.length];
    const text  = `${name}: ${elev.toFixed(2)} m`;
    ctx.font = '8px "DM Mono",monospace';
    const tw = ctx.measureText(text).width + 6;
    const labelX = Math.min(mx + 8, W - tw - 4);
    const labelY = Math.max(MT + 2, Math.min(MT + DH - 14, y - 6));
    ctx.fillStyle = 'rgba(8,11,16,0.82)';
    ctx.fillRect(labelX, labelY, tw, 14);
    ctx.fillStyle = color;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(text, labelX + 3, labelY + 3);
  }
}

// Draw the profile view.
// vaList = [{name, path}]; vScale = VE; viewState = {zoom, cx_stn, cy_elev}.
// showAnnotations: slope % labels + PI markers; false = curves only.
function drawProfileCanvas(canvas, vaList, vScale, viewState, showAnnotations = true) {
  if (!canvas || !vaList || vaList.length === 0 || !viewState) return;
  const W = canvas.clientWidth  || canvas.width;
  const H = canvas.clientHeight || canvas.height;
  if (!W || !H) return;
  // Backing buffer needs to be devicePixelRatio× the CSS size on a HiDPI screen (any phone) —
  // sizing it 1:1 with clientWidth/clientHeight left the browser upscaling a lower-res buffer
  // to fill the same box, reading as blurry/pixelated rather than a crisp technical drawing.
  // setTransform (not scale) so re-running this on an already-correctly-sized canvas doesn't
  // compound the scale on every redraw.
  const dpr = window.devicePixelRatio || 1;
  const bw  = Math.round(W * dpr), bh = Math.round(H * dpr);
  if (canvas.width  !== bw) canvas.width  = bw;
  if (canvas.height !== bh) canvas.height = bh;

  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const ML=65, MR=20, MT=14, MB=34;
  const DW = W - ML - MR;
  const DH = H - MT - MB;
  if (DW <= 0 || DH <= 0) return;

  const b   = getCombinedBounds(vaList);
  const { zoom, cx_stn, cy_elev } = viewState;
  const ve  = vScale || 1;
  const pps = DW / b.stnRange * zoom;
  const ppe = ve * pps;

  const cxPx = ML + DW / 2;
  const cyPx = MT + DH / 2;
  const toX  = s => cxPx + (s - cx_stn) * pps;
  const toY  = e => cyPx - (e - cy_elev) * ppe;

  const visStnMin  = cx_stn  - DW/2 / pps;
  const visStnMax  = cx_stn  + DW/2 / pps;
  const visElevMin = cy_elev - DH/2 / ppe;
  const visElevMax = cy_elev + DH/2 / ppe;

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#13161b";
  ctx.fillRect(0, 0, W, H);

  // Grid
  const stnStep  = niceStep((visStnMax - visStnMin) / 8);
  const elevStep = niceStep((visElevMax - visElevMin) / 5);
  ctx.setLineDash([3, 4]);
  ctx.lineWidth = 1;
  const stnStart  = Math.ceil(visStnMin  / stnStep)  * stnStep;
  const elevStart = Math.ceil(visElevMin / elevStep) * elevStep;

  for (let s = stnStart; s <= visStnMax + 0.001; s += stnStep) {
    const x = toX(s);
    if (x < ML-1 || x > ML+DW+1) continue;
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath(); ctx.moveTo(x, MT); ctx.lineTo(x, MT+DH); ctx.stroke();
  }
  for (let e = elevStart; e <= visElevMax + 0.001; e += elevStep) {
    const y = toY(e);
    if (y < MT-1 || y > MT+DH+1) continue;
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath(); ctx.moveTo(ML, y); ctx.lineTo(ML+DW, y); ctx.stroke();
  }
  ctx.setLineDash([]);

  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1;
  ctx.strokeRect(ML, MT, DW, DH);

  // Axis labels
  ctx.fillStyle = "rgba(210,210,210,0.75)";
  ctx.font = '10px "DM Mono",monospace';
  ctx.textAlign = "center"; ctx.textBaseline = "top";
  for (let s = stnStart; s <= visStnMax + 0.001; s += stnStep) {
    const x = toX(s);
    if (x < ML-1 || x > ML+DW+1) continue;
    const km = Math.floor(s/1000);
    const m  = String(Math.round(s - km*1000)).padStart(3,"0");
    ctx.fillText(`${km}+${m}`, x, MT+DH+4);
  }
  const elevDec = Math.max(0, -Math.floor(Math.log10(elevStep || 1)));
  ctx.textAlign = "right"; ctx.textBaseline = "middle";
  for (let e = elevStart; e <= visElevMax + 0.001; e += elevStep) {
    const y = toY(e);
    if (y < MT-1 || y > MT+DH+1) continue;
    ctx.fillText(e.toFixed(elevDec), ML-5, y);
  }

  // ── Curves (type-based colour, clipped) ────────────────────────────────────
  const piAnnotations = [];

  ctx.save();
  ctx.beginPath(); ctx.rect(ML, MT, DW, DH); ctx.clip();

  for (const { path } of vaList) {
    if (!path || path.length === 0) continue;

    for (const seg of path) {

      if (seg.lineSegment) {
        const [[s1,e1],[s2,e2]] = seg.lineSegment;
        ctx.strokeStyle = PROF_CLR.lineSegment;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(toX(s1), toY(e1)); ctx.lineTo(toX(s2), toY(e2)); ctx.stroke();
        if (showAnnotations) {
          const dStn = s2 - s1;
          if (Math.abs(dStn) > 0.1) {
            const slope = (e2 - e1) / dStn * 100;
            ctx.save();
            ctx.font = '9px "DM Mono",monospace';
            ctx.fillStyle = PROF_CLR.lineSegment;
            ctx.globalAlpha = 0.9;
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            ctx.fillText(`${slope >= 0 ? "+" : ""}${slope.toFixed(2)}%`, toX((s1+s2)/2), toY((e1+e2)/2) - 4);
            ctx.restore();
          }
        }

      } else if (seg.lineString) {
        ctx.strokeStyle = PROF_CLR.lineString;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        seg.lineString.forEach((p, i) => {
          if (i === 0) ctx.moveTo(toX(p[0]), toY(p[1]));
          else         ctx.lineTo(toX(p[0]), toY(p[1]));
        });
        ctx.stroke();

      } else if (seg.arc) {
        const { center, vectorX, vectorY, sweepStartEnd } = seg.arc;
        const R  = Math.hypot(vectorX[0], vectorX[1]);
        const ux = vectorX[0]/R, uy = vectorX[1]/R;
        const wx = vectorY[0]/R, wy = vectorY[1]/R;
        const N  = Math.max(40, Math.ceil(Math.abs(sweepStartEnd[1]-sweepStartEnd[0]) * 4));
        ctx.strokeStyle = PROF_CLR.arc;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i <= N; i++) {
          const deg = sweepStartEnd[0] + (i/N)*(sweepStartEnd[1]-sweepStartEnd[0]);
          const rad = deg * Math.PI / 180;
          const stn = center[0] + R*(ux*Math.cos(rad)+wx*Math.sin(rad));
          const elv = center[1] + R*(uy*Math.cos(rad)+wy*Math.sin(rad));
          if (i===0) ctx.moveTo(toX(stn), toY(elv));
          else       ctx.lineTo(toX(stn), toY(elv));
        }
        ctx.stroke();
        if (showAnnotations) {
          const a0 = sweepStartEnd[0] * Math.PI / 180;
          const a1 = sweepStartEnd[1] * Math.PI / 180;
          const pi = arcTangentPI(center, R, ux, uy, wx, wy, a0, a1);
          if (pi) {
            const arcL = R * Math.abs(a1 - a0);
            piAnnotations.push({
              px: toX(pi.stn), py: toY(pi.elev), color: PROF_CLR.arc,
              lines: [formatStation(pi.stn), `R = ${R.toFixed(0)} m`, `L = ${arcL.toFixed(0)} m`],
            });
          }
        }

      } else if (seg.bcurve) {
        const [p0, p1, p2] = seg.bcurve.points;
        ctx.strokeStyle = PROF_CLR.bcurve;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(toX(p0[0]), toY(p0[1]));
        ctx.quadraticCurveTo(toX(p1[0]), toY(p1[1]), toX(p2[0]), toY(p2[1]));
        ctx.stroke();
        if (showAnnotations) {
          const L = Math.abs(p2[0] - p0[0]);
          piAnnotations.push({
            px: toX(p1[0]), py: toY(p1[1]), color: PROF_CLR.bcurve,
            lines: [formatStation(p1[0]), `L = ${L.toFixed(0)} m`],
          });
        }
      }
    }
  }

  ctx.restore();

  // ── PI annotations (after clip restore — can extend into margins) ──────────
  for (const ann of piAnnotations) {
    drawPIAnnotation(ctx, ann.px, ann.py, W, H, ann.color, ann.lines);
  }
}

// ── Tree helpers ──────────────────────────────────────────────────────────────

// Collect all leaf keys ("ClassName::subjectId") from the hierarchy tree.
function collectLeafKeys(nodes) {
  if (!nodes) return [];
  const keys = [];
  for (const node of nodes) {
    if (node.itemTypes) keys.push(...node.itemTypes.map((t) => t.key));
    if (node.bridge)    keys.push(...node.bridge.leafKeys);
    if (node.children)  keys.push(...collectLeafKeys(node.children));
  }
  return keys;
}

// Collect all leaf item types ({key, displayName}) from the hierarchy tree — used to
// build the Annotate panel's item type picker (filtered down to "loaded" ones by the caller).
// View → Zoom to: one entry per entity identifier — a tree node whose children are models (job
// subjects carrying item types or a bridge tree), e.g. Transportation_System › Bridge › Bridge_01.
// Grouped by the path above it. Nothing project-specific: it follows the job subject names.
function collectZoomEntities(nodes, path = [], out = []) {
  for (const node of nodes || []) {
    const models = (node.children || []).filter((c) => c.itemTypes || c.bridge);
    if (models.length) out.push({ key: [...path, node.label].join("|"), group: path.map(prettyLabel).join(" › "), label: prettyLabel(node.label), models });
    if (node.children) collectZoomEntities(node.children, [...path, node.label], out);
  }
  return out;
}

function unionModelBoxes(modelIds, extents) {
  let box = null;
  for (const id of modelIds) {
    const b = extents[id];
    if (!b) continue;
    if (!box) { box = { ...b }; continue; }
    box.minX = Math.min(box.minX, b.minX); box.maxX = Math.max(box.maxX, b.maxX);
    box.minY = Math.min(box.minY, b.minY); box.maxY = Math.max(box.maxY, b.maxY);
    box.minZ = Math.min(box.minZ, b.minZ); box.maxZ = Math.max(box.maxZ, b.maxZ);
  }
  return box;
}

function ZoomToList({ entities, typeStates, onPick }) {
  const [q, setQ] = useState("");
  const terms = q.toLowerCase().replace(/_/g, " ").split(/\s+/).filter(Boolean);
  const shown = entities.filter((e) => terms.every((t) => `${e.group} ${e.label}`.toLowerCase().includes(t)));
  const groups = [];
  for (const e of shown) {
    const g = groups[groups.length - 1];
    if (g && g.name === e.group) g.items.push(e); else groups.push({ name: e.group, items: [e] });
  }
  const loadState = (e) => {
    const keys = e.models.flatMap(modelLeafKeys);
    const n = keys.filter((k) => typeStates[k] === "loaded").length;
    return n === 0 ? "" : n === keys.length ? "loaded" : "partial";
  };
  return (
    <div className="dt-zoomto">
      <Input size="small" allowClear autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="dt-zoomto-list">
        {groups.map((g) => (
          <div key={g.name}>
            <div className="dt-zoomto-group">{g.name}</div>
            {g.items.map((e) => {
              const st = loadState(e);
              return (
                <button type="button" key={e.key} className="dt-zoomto-item" onClick={() => onPick(e)}
                  title={st === "loaded" ? "Zoom to" : "Zoom to and load"}>
                  <span>{e.label}</span>
                  {st && <span className={`dt-zoomto-dot dt-zoomto-dot-${st}`} />}
                </button>
              );
            })}
          </div>
        ))}
        {groups.length === 0 && <div className="dt-zoomto-empty">No match</div>}
      </div>
    </div>
  );
}

// Tree leaf key → "Name — Parent › Discipline" for the Hide item type hover tooltip. Item types
// take their two nearest tree levels (asset instance › discipline); bridge leaves their bridge path.
function buildTypeLabels(nodes, path = [], out = new Map()) {
  for (const node of nodes || []) {
    const p = [...path, node.label];
    const where = p.slice(-2).join(" › ");
    for (const t of node.itemTypes || []) out.set(t.key, `${prettyLabel(t.displayName)} — ${where}`);
    if (node.bridge) {
      const walk = (n, bp) => (n.children
        ? n.children.forEach((c) => walk(c, [...bp, n.label]))
        : out.set(n.key, `${n.label} — ${bp.slice(-2).join(" › ")}`));
      node.bridge.nodes.forEach((n) => walk(n, p));
    }
    if (node.children) buildTypeLabels(node.children, p, out);
  }
  return out;
}

function collectLeafItemTypes(nodes) {
  if (!nodes) return [];
  const out = [];
  for (const node of nodes) {
    if (node.itemTypes) out.push(...node.itemTypes.map((t) => ({ key: t.key, displayName: t.displayName })));
    if (node.children)  out.push(...collectLeafItemTypes(node.children));
  }
  return out;
}

// ── Left-panel filter ─────────────────────────────────────────────────────────────────────────
// A "model" is a tree node that carries geometry (itemTypes, or a grafted bridge) — one job
// subject. Facets come from its own path, so they follow whatever the .bim's job subject names
// say: asset = the segment two above the discipline (System|[range]|Asset|Instance|Discipline,
// with or without the station range), discipline = the last segment, status = the IFC model
// check. OR inside a group, AND across groups.
// Filter groups (front matter "filter-group: Earthworks = A, B, …" in [Project].md) regroup item
// types for the filter only: a model holding some of them becomes two filter entries — the group's
// item types under the group's own tile, the rest under its asset tile. Tree and .bim untouched.
// Detail: docs/digitwin/Tree Filter.md.
const EMPTY_FILTER = { assets: [], disciplines: [], statuses: [], text: "" };

const isFilterActive = (f) => f.assets.length + f.disciplines.length + f.statuses.length > 0 || f.text.trim() !== "";
const prettyLabel    = (s) => String(s).replace(/_/g, " ");
const nameKey        = (s) => String(s).toLowerCase().replace(/[_\s]+/g, " ").trim();

function collectModels(nodes, path = []) {
  const out = [];
  for (const node of nodes || []) {
    const p = [...path, node.label];
    if (node.itemTypes || node.bridge) out.push({ node, path: p });
    if (node.children) out.push(...collectModels(node.children, p));
  }
  return out;
}

// Asset tile per System›Asset. A system holding a catch-all "All" asset (Ground: Rail + All) is
// one tile named after the system — its second level is a split, not distinct asset types.
function assetKeyOf(path, wholeSystems) {
  if (wholeSystems.has(path[0]) || path.length < 3) return path[0];
  return `${path[0]}›${path[path.length - 3]}`;
}

const ASSET_GLYPHS = [
  { match: /earth|cut|fill|excavat/, color: "#d4a373", d: "M2 19h20M4.5 19l4.5-7.5h6l4.5 7.5M7 15.5h10M9 11.5l1.5-4h3l1.5 4" },
  { match: /bridge/,                 color: "#2dd4bf", d: "M2 8h20M3 8v12M21 8v12M3 20c3-9 15-9 18 0M8 8v3M16 8v3" },
  { match: /signal|telecom/,         color: "#f472b6", d: "M3 21h18M5 21v-9h9v9M8.5 15.5h2M17 21V8M14.5 5.5a3.5 3.5 0 0 1 5 0M12.5 3.2a6.5 6.5 0 0 1 9 0" },
  { match: /fuel|energy|power/,      color: "#fbbf24", d: "M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M3 21h12M6.5 8h5M14 10h2a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0V8l-3-3" },
  { match: /hydrant|fire/,           color: "#fb923c", d: "M7 21h10M9 21V10h6v11M7.5 10h9M10 10V7a2 2 0 0 1 4 0v3M6 13.5h3M15 13.5h3M12 3v2" },
  { match: /road|highway/,           color: "#a78bfa", d: "M6 21L10 3M18 21L14 3M12 5v2.5M12 11v2.5M12 17v2.5" },
  { match: /ground|terrain|survey/,  color: "#84cc16", d: "M2 18l5-6 4 3 5-7 6 10M2 21h20" },
  { match: /rail|track/,             color: "#4f9cf9", d: "M8 2v20M16 2v20M5 5h14M5 9.5h14M5 14h14M5 18.5h14" },
];
const DEFAULT_GLYPH = { color: "#94a3b8", d: "M12 2l9 5v10l-9 5-9-5V7zM3 7l9 5 9-5M12 12v10" };
const glyphFor = (key) => ASSET_GLYPHS.find((g) => g.match.test(key.toLowerCase())) || DEFAULT_GLYPH;

// Split each model into filter entries ({node, path, asset, itemTypes|null, leafKeys, ...});
// itemTypes null = the whole model (bridges, and models no filter group touches).
function buildFilterOptions(models, filterGroups = []) {
  const wholeSystems = new Set(models.filter((m) => m.path.length >= 3 && m.path[m.path.length - 3] === "All").map((m) => m.path[0]));
  const groupOf = new Map(); // item type name → group asset key
  for (const g of filterGroups) for (const t of g.itemTypes) groupOf.set(nameKey(t), `group:${g.name}`);

  const assets = new Map(), groupAssets = new Map(), disciplines = new Map(), statuses = new Map();
  const facets = [];
  for (const m of models) {
    const discipline = m.path[m.path.length - 1];
    disciplines.set(discipline, { key: discipline, label: prettyLabel(discipline) });
    const status = m.node.check?.statusKey;
    if (status === "REVISE" || status === "REVIEW") {
      statuses.set(status, { key: status, label: status === "REVISE" ? "To revise" : "To review", color: CHECK_COLORS[status] });
    }

    // item types per asset: grouped ones to their group, the rest to the model's own asset
    const ownAsset = assetKeyOf(m.path, wholeSystems);
    const parts = new Map();
    if (m.node.itemTypes) {
      for (const t of m.node.itemTypes) {
        const asset = groupOf.get(nameKey(t.displayName)) || ownAsset;
        if (!parts.has(asset)) parts.set(asset, []);
        parts.get(asset).push(t);
      }
    } else {
      parts.set(ownAsset, null);
    }
    const whole = parts.size === 1 && parts.has(ownAsset);

    for (const [asset, itemTypes] of parts) {
      const isGroup = asset.startsWith("group:");
      const tiles   = isGroup ? groupAssets : assets;
      if (!tiles.has(asset)) {
        const name = isGroup ? asset.slice(6) : asset.includes("›") ? asset.split("›")[1] : asset;
        const full = prettyLabel(name.replace(/_System$/, ""));
        const g    = isGroup && filterGroups.find((x) => `group:${x.name}` === asset);
        tiles.set(asset, {
          key: asset, label: full.split(" ")[0],
          title: g ? `${full} — ${g.itemTypes.map(prettyLabel).join(", ")}` : full,
          ...glyphFor(asset),
        });
      }
      const types    = whole ? null : itemTypes;
      const leafKeys = types ? types.map((t) => t.key) : modelLeafKeys(m.node);
      // Free-text search covers the whole path and this entry's item type names
      const text = [...m.path, ...(types || m.node.itemTypes || []).map((t) => t.displayName), m.node.bridge?.name || ""]
        .join(" ").replace(/_/g, " ").toLowerCase();
      facets.push({ ...m, asset, itemTypes: types, leafKeys, discipline, status, text });
    }
  }
  return {
    facets,
    models:      models.length,
    assets:      [...assets.values(), ...groupAssets.values()],
    disciplines: [...disciplines.values()].sort((a, b) => a.label.localeCompare(b.label)),
    statuses:    ["REVISE", "REVIEW"].filter((s) => statuses.has(s)).map((s) => statuses.get(s)),
  };
}

// skip = a group to ignore, for faceted counts ("how many would match if I picked this too")
function matchesFilter(m, f, skip) {
  if (skip !== "assets"      && f.assets.length      && !f.assets.includes(m.asset))           return false;
  if (skip !== "disciplines" && f.disciplines.length && !f.disciplines.includes(m.discipline)) return false;
  if (skip !== "statuses"    && f.statuses.length    && !f.statuses.includes(m.status))        return false;
  const terms = f.text.toLowerCase().replace(/_/g, " ").split(/\s+/).filter(Boolean);
  return terms.every((t) => m.text.includes(t));
}

// Filter entries → one row per model node: {node, itemTypes} with itemTypes null = the whole
// model, else the union of the entries' item types (Rail + Earthworks picked = whole model again).
function entriesByNode(entries) {
  const byNode = new Map();
  for (const e of entries) {
    const cur = byNode.get(e.node);
    if (!e.itemTypes || cur === null) { byNode.set(e.node, null); continue; }
    const merged = [...(cur || []), ...e.itemTypes];
    byNode.set(e.node, merged.length === e.node.itemTypes.length ? null : merged);
  }
  return [...byNode].map(([node, itemTypes]) => ({ node, itemTypes }));
}

// Keep the given models (whole, or narrowed to some item types) and every branch above them.
function pruneTree(nodes, keep) {
  const out = [];
  for (const node of nodes || []) {
    if (keep.has(node)) {
      const itemTypes = keep.get(node);
      out.push(itemTypes ? { ...node, itemTypes } : node);
      continue;
    }
    const children = node.children ? pruneTree(node.children, keep) : [];
    if (children.length > 0) out.push({ ...node, children, itemTypes: undefined, bridge: undefined, check: undefined });
  }
  return out;
}

// Keys of every branch above the models (same scheme as buildTreeData) — the filter's "open
// everything that matches"; model nodes themselves stay closed so the list stays readable.
function branchKeysOf(nodes, keyPrefix = "b") {
  const keys = [];
  for (const node of nodes || []) {
    if (node.itemTypes || node.bridge || !node.children) continue;
    const key = `${keyPrefix}/${node.label}`;
    keys.push(key, ...branchKeysOf(node.children, key));
  }
  return keys;
}

// Leaf keys a model loads (item types, or a bridge's assembly parts)
function modelLeafKeys(node) {
  return node.bridge ? node.bridge.leafKeys : (node.itemTypes || []).map((t) => t.key);
}

// ── Bridge trees (OpenBridge Modeler — docs/digitwin/Bridges Roadmap.md) ──────────────────────
// A bridge's Structural subject shows its assembly tree (Substructure / Bearings / Superstructure /
// Misc, from /api/bridges/.../tree) instead of its item-type list. Leaf keys start with "bridge:".

const isBridgeKey = (key) => typeof key === "string" && key.startsWith("bridge:");

// Every grafted bridge in the tree → [{ name, supportKeys }] — the leaf keys under its Substructure
// headline (piers and abutments, the only bridge parts Annotate labels)
function collectBridges(nodes, out = []) {
  for (const node of nodes || []) {
    if (node.bridge) {
      const sub = node.bridge.nodes.find((n) => n.key === `bridge:${node.bridge.name}:sub`);
      out.push({ name: node.bridge.name, supportKeys: sub ? sub.leafKeys : [] });
    } else if (node.children) collectBridges(node.children, out);
  }
  return out;
}

// Replace the item-type list of every hierarchy leaf whose subjects are a bridge with that bridge's
// tree. Each bridge group node gets the leaf keys underneath it (for its group checkbox).
function graftBridgeTrees(nodes, bridges) {
  if (bridges.length === 0) return nodes;
  const sameSubjects = (a = [], b = []) => a.length === b.length && a.every((id) => b.includes(id));
  const withLeafKeys = (n) => {
    if (!n.children) return { ...n, leafKeys: [n.key] };
    const children = n.children.map(withLeafKeys);
    return { ...n, children, leafKeys: children.flatMap((c) => c.leafKeys) };
  };
  return nodes.map((node) => {
    const bridge = node.itemTypes && bridges.find((b) => sameSubjects(b.subjectIds, node.subjectIds));
    if (bridge) {
      const bridgeNodes = bridge.nodes.map(withLeafKeys);
      const { itemTypes: _hidden, ...rest } = node;
      return { ...rest, bridge: { name: bridge.name, nodes: bridgeNodes, leafKeys: bridgeNodes.flatMap((n) => n.leafKeys) } };
    }
    return node.children ? { ...node, children: graftBridgeTrees(node.children, bridges) } : node;
  });
}

// leaf key → element ids, for every bridge in the tree (the viewer splits one batched response with it)
function bridgeElementIds(nodes, out = {}) {
  for (const node of nodes || []) {
    if (node.bridge) {
      const walk = (n) => (n.children ? n.children.forEach(walk) : (out[n.key] = n.elementIds));
      node.bridge.nodes.forEach(walk);
    }
    if (node.children) bridgeElementIds(node.children, out);
  }
  return out;
}

function buildBridgeNodes(nodes, typeStates, viewerReady, onToggleBridgeGroup) {
  return nodes.map((n) => {
    if (!n.children) {
      const state = typeStates[n.key] ?? "idle";
      return {
        key:             n.key,
        title:           <LeafTitle t={{ displayName: n.label, count: n.count }} state={state} />,
        disableCheckbox: state === "loading" || !viewerReady,
      };
    }
    return {
      key:        n.key,
      title:      <BridgeGroupLabel label={n.label} count={n.count} leafKeys={n.leafKeys}
                    typeStates={typeStates} viewerReady={viewerReady} onToggle={onToggleBridgeGroup} />,
      selectable: false,
      checkable:  false,
      children:   buildBridgeNodes(n.children, typeStates, viewerReady, onToggleBridgeGroup),
    };
  });
}

// Group checkbox for a bridge node — same look as DisciplineLabel, plus the element count
function BridgeGroupLabel({ label, count, leafKeys, typeStates, viewerReady, onToggle, discipline = false }) {
  const loaded        = leafKeys.filter((k) => typeStates[k] === "loaded").length;
  const anyLoading    = leafKeys.some((k) => typeStates[k] === "loading");
  const checked       = leafKeys.length > 0 && loaded === leafKeys.length;
  const indeterminate = loaded > 0 && !checked;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        disabled={anyLoading || !viewerReady}
        onChange={() => onToggle(leafKeys, !checked)}
      />
      {anyLoading && <Spin size="small" />}
      <span style={discipline ? {
        fontFamily: "'DM Mono', monospace", fontSize: 10.5, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(255,255,255,0.85)",
      } : { fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>
        {label}
      </span>
      {count != null && <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>({count})</span>}
    </span>
  );
}

// Build Ant Design Tree treeData from the 5-level hierarchy.
// Level-5 (discipline) nodes get a checkbox that controls all their item types at once.
// `depth` (0-indexed, schema=0) drives BranchLabel's per-level type tier — see BRANCH_TIERS.
function buildTreeData(nodes, typeStates, viewerReady, onToggleDiscipline, onToggleBridgeGroup, apiBase, keyPrefix = "b", depth = 0) {
  if (!nodes || nodes.length === 0) return [];

  // Path-based keys (sibling labels are unique) so a branch keeps its key when the filter
  // prunes its siblings — expansion state survives filtering.
  return nodes.map((node) => {
    const branchKey = `${keyPrefix}/${node.label}`;

    // Bridge subject — its OpenBridge assembly tree instead of the item-type list
    if (node.bridge) {
      return {
        key:        branchKey,
        title:      <BridgeGroupLabel label={node.label} leafKeys={node.bridge.leafKeys} discipline
                      typeStates={typeStates} viewerReady={viewerReady} onToggle={onToggleBridgeGroup} />,
        selectable: false,
        checkable:  false,
        children:   buildBridgeNodes(node.bridge.nodes, typeStates, viewerReady, onToggleBridgeGroup),
      };
    }

    // Discipline node — carries itemTypes; has a group checkbox + individual leaf checkboxes
    if (node.itemTypes && node.itemTypes.length > 0) {
      return {
        key:        branchKey,
        title:      <DisciplineLabel
                      label={node.label}
                      itemTypes={node.itemTypes}
                      typeStates={typeStates}
                      viewerReady={viewerReady}
                      onToggle={onToggleDiscipline}
                      check={node.check}
                      apiBase={apiBase}
                    />,
        selectable: false,
        checkable:  false,
        children:   node.itemTypes.map((t) => {
          const state = typeStates[t.key] ?? "idle";
          return {
            key:             t.key,
            title:           <LeafTitle t={t} state={state} />,
            disableCheckbox: state === "loading" || !viewerReady,
          };
        }),
      };
    }

    // Branch node — recurse into children
    return {
      key:        branchKey,
      title:      <BranchLabel label={node.label} depth={depth} checks={countChecks(node)} />,
      selectable: false,
      checkable:  false,
      children:   node.children
        ? buildTreeData(node.children, typeStates, viewerReady, onToggleDiscipline, onToggleBridgeGroup, apiBase, branchKey, depth + 1)
        : [],
    };
  });
}

// Per-depth type tiers for the 4 recursive branch levels (schema → job/subject → discipline
// → facility) — graduated size/weight/color instead of one faint style for all of them, so
// each step in the hierarchy actually reads as a distinct level rather than blurring together.
const BRANCH_TIERS = [
  { fontSize: 11,   fontWeight: 700, letterSpacing: "0.05em", color: "rgba(255,255,255,0.82)" }, // schema
  { fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", color: "rgba(255,255,255,0.68)" }, // job/subject
  { fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", color: "rgba(255,255,255,0.62)" }, // discipline
  { fontSize: 10.5, fontWeight: 500, letterSpacing: "0.06em", color: "rgba(255,255,255,0.56)" }, // facility
];

// ── Left-panel filter UI ──────────────────────────────────────────────────────
// Asset tiles (one line-drawn glyph per asset type, glowing in its own accent when picked),
// discipline/status chips and a search box. Counts are faceted: each option shows how many
// models it would match together with the other groups' current picks.
const pluralModels = (n) => `${n} model${n === 1 ? "" : "s"}`;

function TreeFilter({ options, filter, onChange, matched, loadAction, unloadOthers }) {
  const [open, setOpen] = useState(false); // starts collapsed — the header still shows what's picked
  const active = isFilterActive(filter);

  const toggle = (group, key) => {
    const list = filter[group];
    onChange({ ...filter, [group]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key] });
  };
  // distinct models — a model split by a filter group is two entries but one model
  const countFor = (group, field, key) =>
    new Set(options.facets.filter((m) => m[field] === key && matchesFilter(m, filter, group)).map((m) => m.node)).size;

  const pickedCount = filter.assets.length + filter.disciplines.length + filter.statuses.length;

  // What's picked, in words — shown in the header while the card is collapsed
  const labelOf = (list, key) => list.find((o) => o.key === key)?.label ?? key;
  const picks = [
    ...filter.assets.map((k) => labelOf(options.assets, k)),
    ...filter.disciplines.map((k) => labelOf(options.disciplines, k)),
    ...filter.statuses.map((k) => labelOf(options.statuses, k)),
    ...(filter.text.trim() ? [`"${filter.text.trim()}"`] : []),
  ];

  return (
    <div className={`dt-card dt-filter${active ? " active" : ""}`}>
      <div className="dt-card-head">
        <button type="button" className="dt-filter-title" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" className={`dt-filter-chevron${open ? " open" : ""}`}>
            <path d="M9 6l6 6-6 6" />
          </svg>
          <span className="dt-card-title">Filter</span>
          {pickedCount > 0 && <span className="dt-filter-count">{pickedCount}</span>}
        </button>
        {!open && picks.length > 0 && (
          <span className="dt-filter-picks" title={picks.join(", ")}>{picks.join(" · ")}</span>
        )}
        {active && (
          <button type="button" className="dt-filter-clear" style={{ marginLeft: "auto" }}
            onClick={() => onChange(EMPTY_FILTER)}>Clear</button>
        )}
      </div>

      {open && (
        <>
          <div className="dt-filter-tiles">
            {options.assets.map((a) => {
              const on    = filter.assets.includes(a.key);
              const count = countFor("assets", "asset", a.key);
              return (
                <Tooltip key={a.key} title={a.title} placement="bottom" mouseEnterDelay={0.4}>
                  <button
                    type="button"
                    className={`dt-filter-tile${on ? " on" : ""}${count === 0 && !on ? " empty" : ""}`}
                    style={{ "--accent": a.color }}
                    aria-pressed={on}
                    onClick={() => toggle("assets", a.key)}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d={a.d} />
                    </svg>
                    <span className="dt-filter-tile-label">{a.label}</span>
                    <span className="dt-filter-tile-count">{count}</span>
                  </button>
                </Tooltip>
              );
            })}
          </div>

          <div className="dt-filter-chips">
            {options.disciplines.map((d) => {
              const on    = filter.disciplines.includes(d.key);
              const count = countFor("disciplines", "discipline", d.key);
              return (
                <button key={d.key} type="button" aria-pressed={on}
                  className={`dt-filter-chip${on ? " on" : ""}${count === 0 && !on ? " empty" : ""}`}
                  onClick={() => toggle("disciplines", d.key)}>
                  {d.label}<span className="dt-filter-chip-count">{count}</span>
                </button>
              );
            })}
            {options.statuses.map((s) => {
              const on    = filter.statuses.includes(s.key);
              const count = countFor("statuses", "status", s.key);
              return (
                <button key={s.key} type="button" aria-pressed={on}
                  className={`dt-filter-chip status${on ? " on" : ""}${count === 0 && !on ? " empty" : ""}`}
                  style={{ "--accent": s.color }}
                  onClick={() => toggle("statuses", s.key)}>
                  <span className="dt-filter-dot" />{s.label}<span className="dt-filter-chip-count">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="dt-filter-search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
            <input
              value={filter.text}
              onChange={(e) => onChange({ ...filter, text: e.target.value })}
              placeholder="Search models or item types, e.g. bridge 03, sleeper"
              aria-label="Search models"
            />
            {filter.text && (
              <button type="button" className="dt-filter-search-clear" aria-label="Clear search"
                onClick={() => onChange({ ...filter, text: "" })}>×</button>
            )}
          </div>
        </>
      )}

      {active && (matched > 0 || unloadOthers?.count > 0) && (
        <div className="dt-filter-actions">
          {matched > 0 && loadAction && (
            <Button
              size="small"
              className="dt-filter-load"
              onClick={loadAction.onClick}
              disabled={loadAction.disabled}
              loading={loadAction.loading}
            >
              {loadAction.label}
            </Button>
          )}
          {unloadOthers?.count > 0 && (
            <Tooltip title="Loaded models this filter hides from the tree" placement="bottom">
              <Button size="small" className="dt-filter-unload" onClick={unloadOthers.onClick} disabled={unloadOthers.disabled}>
                Unload {unloadOthers.count} other{unloadOthers.count === 1 ? "" : "s"}
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

// ── IFC model check status ────────────────────────────────────────────────────
// IFC models exported by the custom IFC connector carry a check record on their job subject
// (node.check, from /itemtypes). REVISE/REVIEW get a dot on the discipline node, with the
// record's own text in a popover; OK and unchecked get nothing — "No Issues Found" is not an
// approval of the design. Parent branches show a count so a collapsed tree still points the way.
// Detail: docs/digitwin/IFC Model Check Status.md.
const CHECK_COLORS = { REVISE: "#e5484d", REVIEW: "#f5a524" };

function countChecks(node) {
  const counts = { revise: 0, review: 0 };
  const walk = (n) => {
    if (n.check?.statusKey === "REVISE") counts.revise++;
    if (n.check?.statusKey === "REVIEW") counts.review++;
    (n.children || []).forEach(walk);
  };
  walk(node);
  return counts;
}

function CheckCount({ checks }) {
  if (!checks || (checks.revise === 0 && checks.review === 0)) return null;
  const item = (n, key, text) => n > 0 && (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: CHECK_COLORS[key] }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: CHECK_COLORS[key] }} />
      {n} {text}
    </span>
  );
  return (
    <span style={{
      display: "inline-flex", gap: 8, marginLeft: 8, fontFamily: "'DM Mono', monospace",
      fontSize: 10, fontWeight: 500, textTransform: "none", letterSpacing: 0,
    }}>
      {item(checks.revise, "REVISE", "to revise")}
      {item(checks.review, "REVIEW", "to review")}
    </span>
  );
}

function CheckBadge({ check, apiBase }) {
  const color = CHECK_COLORS[check?.statusKey];
  if (!color) return null;

  const row = (label, value) => value && (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, textTransform: "uppercase",
        letterSpacing: "0.06em", color: "rgba(255,255,255,0.45)" }}>{label}</div>
      <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>{value}</div>
    </div>
  );
  const checkedAt = check.checkedAt ? new Date(check.checkedAt).toLocaleString() : "";

  const content = (
    <div style={{ width: 320, maxWidth: "80vw" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
        <span style={{ fontWeight: 600, fontSize: 14, color }}>{check.status}</span>
      </div>
      {row("Action by", check.actionBy)}
      {row("Findings", check.description)}
      {row("Compared with", check.comparedWith && check.comparedWith.split("; ").join(", "))}
      {row("Checked", checkedAt)}
      {check.reportName && (
        <Button
          icon={<DownloadOutlined />}
          size="small"
          href={`${apiBase}/checkreport/${check.subjectId}`}
          style={{ marginTop: 4 }}
        >
          Download check report
        </Button>
      )}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="right">
      <button
        type="button"
        aria-label={`Model check: ${check.status}`}
        title={check.status}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 18, height: 18, padding: 0, border: "none", borderRadius: "50%",
          background: "transparent", cursor: "pointer",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color,
          boxShadow: `0 0 0 3px ${color}33` }} />
      </button>
    </Popover>
  );
}

function DisciplineLabel({ label, itemTypes, typeStates, viewerReady, onToggle, check, apiBase }) {
  const total       = itemTypes.length;
  const loaded      = itemTypes.filter(t => typeStates[t.key] === "loaded").length;
  const anyLoading  = itemTypes.some(t => typeStates[t.key] === "loading");
  const checked       = total > 0 && loaded === total;
  const indeterminate = loaded > 0 && loaded < total;

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
      onClick={e => e.stopPropagation()}
    >
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        disabled={anyLoading || !viewerReady}
        onChange={() => onToggle(itemTypes, !checked)}
      />
      <span style={{
        fontFamily: "'DM Mono', monospace", fontSize: 10.5, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.06em",
        color: "rgba(255,255,255,0.85)",
      }}>
        {label}
      </span>
      <CheckBadge check={check} apiBase={apiBase} />
    </span>
  );
}

function BranchLabel({ label, depth = 0, checks }) {
  const tier = BRANCH_TIERS[Math.min(depth, BRANCH_TIERS.length - 1)];
  return (
    <span style={{
      fontFamily: "'DM Mono', monospace",
      textTransform: "uppercase",
      ...tier,
    }}>
      {label}
      <CheckCount checks={checks} />
    </span>
  );
}

function LeafTitle({ t, state }) {
  return (
    <Space size={4}>
      {state === "loading" && <Spin size="small" />}
      <span style={{ fontSize: 12 }}>{t.displayName}</span>
      <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>({t.count})</span>
      {state === "error" && <span style={{ color: "#e5484d" }} title="Load failed">!</span>}
    </Space>
  );
}

// ── Small reusable components ─────────────────────────────────────────────────

function SvgIcon({ d, size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

